// The DOM overlay: top bar, empire dock, selection panel, tooltip, notices.
// Nothing textual or tabular is ever drawn into the canvas — that split is
// load-bearing (ui-map-interface.md §1, §8). Text updates are throttled to
// 4 Hz (§10.3); only the tooltip follows the pointer per frame.

import { hhmm, hhmmss } from '../locale/clock';
import { pressedControl } from './pressroute';
import { oneOf } from '../util/oneof';
import './styles.css';
// The kit of the ☸ OPTIONS menu and the hub cards it carries (R1731). They
// come after the chrome sheet, so a kit rule wins a tie of weight.
import './options/options.css';
import './choice/choice.css';
import './options/cards.css';
import './cards/cardkit.css';
import { inboxPage, type InboxCtx } from './inbox/page';
import { inboxPress } from './inbox/press';
import { openRecordMessage, recordOnOpen, showBattleReports } from './inbox/record';
import { inboxOpened, unfoldSinceLeft } from './inbox/sinceleft';
import { type ChatMsg,
  AdviceKind, Building, ChatChannel, FleetOp, HeatmapKind, JobKind,
  PlotOp, ShipClass, SiegeOp, SiteKind,
} from '../gen/proto/aquaphobia';
import type { CoursePlot, NamedSeamount, NpcAct, Resources, Site } from '../gen/proto/aquaphobia';
import { DEFAULT_CHANNEL, GameState, HEAT_MODES, RES_ORDER, dur, heatMode, heatModeShown } from '../state';
import type { FeatureFlag } from '../gen/featureflags';
import { cellAt, factionColor, factionName } from '../world';
import { cellPos } from './chartpos';
import { keyHtml, rosterHtml, squadView, type SquadLook } from './squad';
import { keyPictures, squadIcon } from './squadmarks';
import {
  CHAT_MAX_LEN, FLEET_NAME_MAX_RUNES, FLEET_NAME_MIN_RUNES, KEY_FLEET_DESIGNER,
  MAINTENANCE_RELOAD_AFTER_SEC,
  MAINTENANCE_WARN_MARKS_MINUTES,
} from '../gen/balance';
import {
  overlay as S, options as OPT, viewpanel as VS, card as CS,
  inbox as IB, chart as CH,
} from '../gen/strings';
import { posLookupOf, type PosLookup } from './quickchat';
// The arm window rides presentation.toml [presentation.chrome]: the
// hub's clan controls wait the same four seconds (R312).
import {
  CHROME_ARM_MS, FLEET_MARKS, KEYS, OPTIONS_MENU, QUEUE_MIN_OPEN_JOBS, QUEUE_OPEN_SHARE, QUEUE_SMALL_SHARE,
  SIEGE_WINDOW, WELCOME_HELD_NOTICES, WELCOME_RELEASE_GAP_MS, type CardKeyId,
} from '../gen/presentation';
// The first frame of a new commander (R2127): what waits while the welcome
// card is open, and the first session of this device (R2057).
import { FirstHold, RANK } from './firsthold';
import { isFirstSession } from './firstsession';
import { claimOf, digitOf, matchKey } from '../keys';
// The shared two-press gate every destructive control goes through (R696).
import { ArmGate } from './arm';
import { arrowGlyph, dockPressHtml, minGlyph } from './arrowpress';
// The notices column and the drain writer live in their own module since
// R806, so the theme preview runs the same code inside its own frame.
import { NoticeColumn, TimerLine, type NoticeOptions, type TimerClaim } from './notices';
// The phase of the moving red hatch across a rewrite (R1749).
import { alignHatches } from './hatch';
// The alarm screen (R1753): the signal rank, the siege row and the list.
import {
  type FullStore, type RunningTimer, type Signal, type SiegeWarning, alarmListHtml, alarmRows, alarmRuns, orderTimers,
  rankSignals, siegeRowHtml, siegeWarnings,
} from './alarms';
// The siege window of WAR, the time cell and the squad card (the game
// critique of 2026-10-14).
import {
  type FleetPresence, type WindowView, nextWindows, readinessOf, slotWindow, squadWindowHtml, targetName,
  windowClaim, windowHeadHtml,
} from './siegewindow';
import { siege as SW } from '../gen/strings';
// The two live regions (R2052): what a screen reader hears of the news.
import { LiveRegions } from './live';
import { readStore, writeStore } from './localstore';
import { coarsePointer } from './pointer';
import { live as SL } from '../gen/strings';
import { NETTEST_HREF } from './reportsheet';
import { AUTO_CHROME_LOOK, wearChromeLook, type ChromeLook } from './chromelook';
import {
  bandLines, boxState, type BoxState, channelTabHtml, chatForm, chatIcon, chatLineHtml, chatLineView, dockDesk,
  lineChannel, phoneChatLine, releasePane, sizeDesk, undockDesk, type ChatDeskKeys,
  type ChatForm, type ChatLineView,
} from './chatdock';
import { GROUP_SLOTS, planAssign, undoSteps } from '../groups';
import {
  type ConsoleState, GAP, installCluster, installLayoutVars, isSheet, landscapeRail, railCtxBeside, relayoutCluster,
  SHEET_MQ, setMirrorDesk, sheetBarH,
} from './layout';
import { installReadoutFit, readoutRoom, type ReadoutFit } from './readoutfit';
import { heatChipHtml, viewButtonHtml, viewButtonLabel, viewPanelHtml, type SeaRow } from './viewpanel';
import { noteRoseHover, noteRosePress, rosePeekHtml } from './rosepress';
import { ScaleBar } from './scalebar';
import {
  installSheetDrag, nearest, type SheetSettle, type SheetStops,
} from './sheetdrag';
import { ListTap } from './touch';
import { installTitleHold, type TitleHoldWiring } from './titlehold';
// Chrome px for a placement (R1556).
import { chromeRect, chromeViewport, toChromePx } from './uiscale';
// The casino's own panel (casino.md).
import { CasinoPanel } from './casino/casino';
import { casinoForm, casinoTabHtml, paneHeadHtml, type CasinoForm } from './casino/pane';
import { openLink } from '../platform/links';
import { casinoHidden } from '../platform/platform';
import { chatFilterOn } from './chatfilter';
import {
  CHART_GROUPS, shownGroups, type GameSettings, flushSettings, loadSettings, onSettingsWrite, saveSettings,
} from './settings';
// The ☸ OPTIONS menu (R1731): its markup, its shared rows, the kit root
// and the hub cards aboard.
import {
  HUB_LINKS, MENU_ACT, menuParts, parentOf, type DevView, type MenuPage, type MenuView,
} from './options/gamemenu';
import { devDeskHead, devDeskShell, devPageHtml, type DevArmOp, type DevPanelView } from './options/devpanel';
import type { OptionsHost } from './options/gamehost';
import { type CommanderMarks, KIT_ROOT, SHELL_DESK, SHELL_PHONE, SHELL_SIDE } from './options/kit';
import { ACT as OPT_ACT, applyPress, isRangeKey, rangeValue, syncRangeMarks } from './options/rows';
import { stepUiTheme } from './options/themepick';
import { armFaces, tabStrip } from './choice/choice';
import { setThemePick, wearTheme } from './themestate';
import { cachedEntitlements } from './lookcache';
import { isPageId } from './options/sections';
// The advisor line above the queue strip (player-reach.md §4, R1305).
import {
  ADVISOR_TITLE, adviceFleet, adviceLine, adviceRowHtml, adviceTarget, type AdviceNames,
} from './advisor';
// The sea trials beside the advisor line, and the welcome card of the first
// Core season (player-reach.md §8, R1572).
import {
  DRILL_URL, TRIALS_TITLE, TRIAL_COUNT, TrialsCard, nextTrial, trialNextLine, trialTarget, trialsCountHtml,
  trialsRowsHtml, type TrialsView,
} from './trials';
import {
  ASHORE, calloutTag, fleetNote, levelNote, researchNote, situationOf, toggleBuilt, toggleSeason, truceLine,
  welcomeHtml, type WelcomeFacts, type WelcomeItem,
} from './welcome';
import { openHowPlays } from './howplays';
import { hubSectionHref } from './hublink';
import { assignWithGesture } from './nav';
// The chart's own legend, one group inside the KEYS panel (R548, card 40).
import {
  badgePicture, factionKey, holdLegend, keyRowsHtml, legendHeld, releaseLegend, setLegendOpen,
} from './chart/faction-key';
import { setLegendWindow } from './chart/stack';
import { cartouche } from './chart/cartouche';
import { legendGroup } from './chart/legend';
import { currentBodyHtml, deepShown, depthBodyHtml, depthHeatBodyHtml } from './chart/deep-legend';
import { depthLook } from '../depthlook';
import { strongestCurrent } from '../deep';
import type { ChartPieceOverlay } from '../fast/chart/furniture';
// The overlay is DOM chrome and imports nothing from `render/`: `holdTotal`
// used to come from the fleet layer, and it took the renderer with it
// (R431).
import {
  cssColor,
  linkClaim, maintenanceClaim, quakeClaim, rampCss, seaIdleClaim, truceClaim,
} from './format';
import { esc } from './esc';
// The card family (R1810): one builder for the peek, the card and the
// page, and the map-object cards that ride on it.
import { signatureRefusal } from './cards/approach';
import { LANE_OFFER_ACT } from './cards/laneoffer';
import { armHtml, cardHtml, type Action, type CardEnv, type CardSize, type CardSpec } from './cards/card';
import { findLane, laneCard, laneGapAt, laneMarkId, type LaneRef } from './cards/lane';
import { DraftForm, libraryShelf, type ShelfCtx } from './overlay/draft';
import { captainFact, fleetCard, pointCard, scanReportCard, siteCard, type CardFleet, type MapCardCtx } from './cards/mapcards';
import { markSvg, shipMarkSvg } from './cards/marks';
import { deployAskState, deployCardSpec, penStep } from './deployask';
import { ContextSlot } from './ctxslot';
import {
  CTX_CLOSED, closedHtml, ctxStep, foldHtml, grabHtml, gridPress, menuYield, raisedSpec, selChipHtml, selPlace,
  type CtxMode,
} from './ctxsheet';
import { SheetDrag } from './ctxdrag';
import {
  plotAdd, plotCaps, plotCardSpec, plotClosedHtml, plotLoop, plotMove, plotPasses, plotRaisedHtml, plotRemove,
  plotNameOf, plotStart, plotUndo, plotWaypoints, type PlotDraft, type PlotLeg,
} from './plotline';
import { standPeek, type Edges, type PeekSide } from './cards/peek';
// The six group slots (R1817).
import {
  barFit, HeadDrag, rowShown, shownSlots, SlotHost, SlotToast, slotViews, type SlotHooks,
} from './groupslots';

import { el, fitStrip, subRow, sitePlace } from './overlay/markup';
import {
  ARMED_ACTS, AUCTION_PAGES, AUDIO_ACTS, INTENT_ACTS,
  CHANNELS, FLEET_OP_ACTS, GAME_TABS, PICK_ACTS,
  PICK_HINTS, armWords,
  SUBS, subsOf, type Tab, tabLabel, tabShort,
} from './overlay/tables';
// The caps of the telephone tab bar and the light count (R1749).
import { capHtml, dockHeadHtml } from './overlay/thumbdock';
// The tab glyphs of the six game menus, their fit and their hold (R2214).
import {
  type GameTab, type TabForm, type TabWords, deskPlan, isGameTab, phonePlan, sheetHeadHtml, tabInnerHtml, wordMeasure,
} from './tabglyphs';
import { installTabHold } from './tabhold';
import { glossaryTerm, termCardHtml } from './glossary';
// The HOW IT WORKS fold of each game menu and the GO TO empty line (R1895).
import {
  goTarget, hasFold, howFold, foldOpen, setFoldOpen,
} from './overlay/howfold';
// The captain row of CAPTAIN (round 091 card 05, R1889).
import type { CapRowCtx } from './overlay/caprow';
// The pages of FLEET · MISSIONS and of CAPTAIN (R1888, R1889).
import { missionsHtml, type MissionsCtx } from './overlay/missions';
import { plotLegs, routesHtml } from './overlay/routes';
import { COMMISSION_ARCHIVE, COMMISSION_INFO, commissionTitle, commissionsHtml } from './overlay/commissions';
import { depotHtml, nextDepotObject } from './overlay/depot';
import { TREASURE_FOUND, treasureHtml } from './overlay/treasure';
// The rename slot: the four name cards, one at a time (R2993).
import { NameDesk, writeKbInset } from './overlay/namedesk';
import { prisonHtml, recruitmentHtml, wardroomHtml } from './overlay/captaintab';
import { pickerSpec } from './overlay/cappicker';
import { draftOf, loadSheetSpec, maxDraft, stepDraft } from './overlay/loadsheet';
import { maxStow, stepStow, stowDraftOf, stowSheetSpec } from './overlay/stowsheet';
import type { Stock } from './rounds';
import { startRowsHtml, startSheetSpec, underwayHtml } from './overlay/expeditions';
import {
  PICK_MODES, type Cover, type PickMode, type UIActions, type UIAudio, type UIFleet,
} from './overlay/types';
// The queue's dependency rules (R387, R1201), pure of the class.
import { queueStripHtml } from './overlay/queue';
// REPORT and BLOCK (deployment.md §7.6, R1323, R1328), and the chat log
// whose lines carry them.
import { ReportDesk } from './overlay/report';
import { type ChatLogCtx, chatFresh, clanChatHtml, worldChatHtml } from './overlay/chatlog';
// The keyboard reference and the media player (round 083 card 16).
import { audioBodyHtml, audioCardHtml, keysPanelHtml } from './overlay/modals';
// The readout line, its explainer card and the core card (R1458, R1749).
import {
  type BarTipParts, barTipHtml, clockTip, coreCardHtml, coreTip, readoutHtml, resName, storeTip,
} from './overlay/readout';
import { revealScroll, sheetRestPx } from './overlay/sheet';
import { cupMarkFor } from './cupmark';
// The build card and the readers it shares with the menus (§8, R1826).
import {
  type InfoKind, type InfoTarget, backBarHtml, compareSpec, infoSpec, stepTarget,
} from './overlay/infocards';
// The read-only menus of the dock, pure of the
// class (§8, §12).
import {
  type CommanderMark, type SovBeam, clanTab, installationsTab,
  productionTab, shipyardTab, sovereigntyTab,
  standingsTab, techTab, torpedoTab,
} from './overlay/menus';
// The rows of ECONOMY and WAR in the grammar of BUILD (round 091 card 09).
import { auctionTab, intelTab, structuresTab as structureList } from './overlay/econwar';
// The season timelapse of WAR · SOVEREIGNTY (card 09 of round 076, R1620)
// and the swap that keeps its transport in the document.
import { ISLAND_MARK, WarTimelapse } from './timelapse/war';
import { swapAroundIsland } from './overlay/island';
import { keepInnerScroll, keepScroll } from './overlay/scrollkeep';
import { MarkupHost } from './markuphost';
import {
  ROW_MOVES, keepFocus, keyboardFocus, menusTakeTab, onTheMap, rowTarget, watchFocusSource,
} from './focuswalk';

// The contract with main.ts lives in `overlay/types.ts`; every importer
// keeps reading it from here.
export { PICK_MODES };
export type { AudioOp, Cover, FleetApproach, GameReport, PickMode, UIActions, UIAudio, UIFleet } from './overlay/types';

/** What a caller may say about one toast. `key` is what a repeat is judged
 * against — the event id, where there is one. The type lives in `ui/notices.ts` (R806). */
export type { NoticeOptions };

const NO_AUDIO: UIAudio = { available: false, playlists: [], themes: [], status: null };

/** A fleet as main.ts answers `contact`: the UI fleet with whose it is. */
type Contact = CardFleet;

/**
 * The schedule the maintenance drain empties over, in wall seconds (§8):
 * from the FIRST mark the world announces to the instant, because that is
 * the schedule a player was given. The line is full when the first mark
 * sounds and empty when the worlds close. Every other claim keeps the
 * total it always had.
 */
const MAINT_DRAIN_SEC = MAINTENANCE_WARN_MARKS_MINUTES[0] * 60;

/** Where the dev panel remembers being open (R94) — same store as the
 * identity token in net/connection.ts, for the same reason: a reload must
 * not undo something the owner set. */
const ADMIN_OPEN_KEY = 'aquaphobia.adminOpen';

/** The `tipOn` of an explainer card that stands under no top-bar control:
 *  a title held on touch (R1555) or a definition (R1557). No `data-tip` of
 *  the bar carries a space, so the key cannot name a control. */
const FREE_TIP = 'free card';

/** The presses of the pinned peek (R1294, R2633, R2649, R2708, R2851). */
const PEEK_ACTS: ReadonlySet<string> = new Set(['peekact', 'peekwork', 'reportopen', 'peekobj', 'peekpage']);

/** Safari's own pinch events, which `touch-action` does not reach (§8). */
const GESTURES = ['gesturestart', 'gesturechange', 'gestureend'] as const;

/** Reads the dev panel's memory. A browser that refuses site data reads
 * null, and the panel then starts closed, so the mount goes on (R161). */
function readAdminOpen(): boolean {
  return readStore(ADMIN_OPEN_KEY) === '1';
}

/** What a caller may hand the overlay beside the state and the actions. */
export interface OverlayOpts {
  /**
   * Test only (R3056): the overlay never fits its six tabs, and each tab
   * keeps the glyph and the full word it was built with. A suite whose
   * cases do not test the fit passes it, because the fit is a quarter of
   * each build in jsdom. No page passes it, and `fast/nopixi.test.ts`
   * fails when a module outside test code names it.
   */
  readonly noTabFit?: boolean;
}

export class Overlay {
  private root = document.createElement('div');
  private topbar = el('div', 'panel readout', 'topbar');
  private dock = el('div', 'panel', 'dock');
  private tabs = el('div', '', 'tabs');
  /**
   * The two caps of the telephone tab bar (R1749, card 01 of round 082):
   * INBOX at the left end and MENU at the right end, in the dark ink of
   * LEAVE TO THE HUB. They stand in the tab bar only while the shell is a
   * sheet; `syncFormFactor` attaches and detaches them, and `renderCaps`
   * writes their count, their dot and their lit state.
   */
  private capInbox = document.createElement('button');
  private capMenu = document.createElement('button');
  /** The markup of the two caps as they were last written. */
  private lastCaps = '';
  /** The key of the last fit of the six tabs (R2214): the form factor,
   *  the rail, the tab labels and the width of the bar. */
  private tabFitKey = '';
  /** The interval of the version stop's countdown, or 0 (R2243). */
  private reloadTimer = 0;
  /** The tab labels the bar was last drawn for (R2215). */
  private tabLabelsDrawn: GameSettings['tabLabels'] | null = null;
  /** False when a test turned the fit of the six tabs off (R3056). */
  private readonly tabFit: boolean;
  /**
   * The dock head of a desktop (R1749, card 01 of round 082): the sign and
   * the name with the home press, the INBOX, OPTIONS and DEV cells, the
   * squad chip, the rank, the standing-orders tag and the new-build line
   * of an admin seat. The stylesheet hides it on a telephone, where the
   * commander row stands in the OPTIONS menu.
   */
  private dockHead = el('div', '', 'dockhead');
  private readonly dockHeadHost = new MarkupHost(this.dockHead);
  private body = el('div', '', 'tabbody');
  /**
   * The peek line (R1749): the advisor line and the sea trials, between
   * the queue strip and the tab bar. On a telephone it is ONE line of 40 px
   * directly over the tab bar, and it stays on the closed sheet: the sea
   * trials while they run, else the advisor line, else nothing. A desktop
   * stands both lines over the queue head.
   */
  private peekEl = el('div', '', 'peekline');
  private readonly peekHost = new MarkupHost(this.peekEl);
  /**
   * The alarm screen (R1753, card 16 of mockup round 082). The siege row
   * stands in the slot of the peek line on a telephone, where the peek
   * line yields to it, and under the dock head on a desktop. The alarm
   * list stands in the dock over the tab bar on a telephone and under the
   * dock head on a desktop. `signalsNow` is the signal rank of this render
   * pass, and the readout line, the dock and the list all read it.
   */
  private siegeEl = el('div', '', 'siegerow');
  private readonly siegeHost = new MarkupHost(this.siegeEl);
  private alarmEl = el('div', '', 'alarmlist');
  private lastAlarmList = '';
  private alarmsOpen = false;
  /** The objects whose siege row a press on GO TO put away. A site leaves
   *  the set when its warning ends, so the next window warns again. */
  private siegeSeen = new Set<number>();
  /** The siege warnings the assertive region said, by object and window
   *  close (R2052). A warning leaves the set when it ends. */
  private siegeSaid = new Set<string>();
  /** The two live regions of the page (R2052). */
  private readonly live = new LiveRegions();
  /** The advice the polite region said last, or '' (R2052). */
  private adviceSaid = '';
  private signalsNow: Signal[] = [];
  private alarmDismiss: ((ev: Event) => void) | null = null;
  private selection = el('div', '', 'selection');
  /**
   * The context slot (§12.0, round 083 cards 10 and 13): the selection
   * card, the commit card and the rename card stand in one place, one at
   * a time. A lane that adds a card hosts its element here.
   */
  readonly slot: ContextSlot;
  /** The state of the telephone context sheet: closed, raised or plot. */
  private ctxMode: CtxMode = CTX_CLOSED;
  /** A menu stood over the sheet at the last render: a menu that opens
   *  beside the rail lowers the card (round 090 card 05 C). */
  private ctxMenuWas = false;
  /** The thing the context sheet names, so a new selection opens closed. */
  private ctxKey = '';
  /**
   * The course while the plot line stands (v2 slot 3, round 088 card 08),
   * or null. It exists only under the flag `routes` (R1795), and no press
   * of the UI opens it until v2 phase 1 adds the PLOT order.
   */
  private plot: PlotDraft | null = null;
  /** The grip of the leg a finger drags, or null. */
  private plotGrip: { id: number; leg: number } | null = null;
  /**
   * DONE of the plot line hands the course here. v2 phase 1 wires it to
   * the order of a course; until then nothing listens, and DONE ends the
   * plot alone.
   */
  onPlotDone: ((fleetId: number, legs: PlotLeg[]) => void) | null = null;
  /**
   * The head of the telephone menu sheet while a selection stands (087
   * card 01): the selection chip under TWO, the fold under THREE at the
   * rest stop. Empty otherwise.
   */
  private selHead = el('div', '', 'selhead');
  private readonly selHeadHost = new MarkupHost(this.selHead);
  /**
   * The six group slots (R1817): the group row of a telephone, in the
   * closed dock over the tab bar, and the group bar of a desktop, under
   * the readout line. Both draw one set of slot views; the stylesheet
   * shows one host per form factor.
   */
  private readonly slotHooks: SlotHooks = {
    tap: (n) => this.tapSlot(n),
    hold: (n) => this.holdSlot(n),
    sign: (n) => this.signSlot(n),
    hover: (n, b) => this.hoverSlot(n, b),
  };
  private groupRow = new SlotHost('grouprow', 'chip', this.slotHooks, () => this.root);
  private groupBar = new SlotHost('groupbar', 'key', this.slotHooks, () => this.root);
  /** The toast of an assign by hold, with UNDO after a replace (R1817). */
  private slotToast = new SlotToast();
  /** The peek host (R1810): the card builder writes the peek into it. */
  private tooltip = el('div', '', 'tooltip');
  /** The dashed leader from a map object to its peek (084 card 02). */
  private peekLead = el('div', '', 'peeklead');
  /** The side the peek opened to last, for the hysteresis of the flip. */
  private peekSide: PeekSide | null = null;
  /**
   * The top bar's explainer card (§12.0): what a commodity is worth an hour
   * and where its store stands, and what the world clock schedules.
   *
   * It is **not** `#tooltip`. That one belongs to the canvas hit test —
   * main.ts hides it on `pointerleave` and rebuilds it on every arrival of
   * data — and it takes no pointer at all, so it can be neither hovered nor
   * tapped away. This card is its own panel, and like the other explainer
   * cards it is `display: none` until it is asked for, so it takes no part
   * in the chrome dock-in (§3.2).
   */
  private barTip = el('div', 'panel', 'bartip');
  /** The sheet's drag grip (§12.0), the top edge of the bottom sheet. */
  private grip = el('div', '', 'dockgrip');
  /**
   * The two drain lines (R430): the schedule lane and the fault lane, both
   * of them children of `#topbar` rather than panels of their own.
   *
   * Each one is a 2 px rule along the foot of the bar with two children —
   * the fill that drains and the tab that names the clock. The children are
   * built once in the constructor: the render path rewrites the tab's markup
   * and the `--frac` property, and never the elements.
   */
  private truceEl = el('div', '', 'truce');
  private truceFill = el('i', 'fill');
  private truceTab = el('span', 'tab');
  private linkEl = el('div', '', 'link');
  private linkFill = el('i', 'fill');
  private linkTab = el('span', 'tab');
  private keysEl = el('div', 'panel', 'keys');
  /**
   * The scrim of the modal (round 083 card 16): deep ink at the scrim
   * ground under KEYS, the media player and the report card, so a modal
   * reads alone over the map. A press on it closes the modal.
   */
  private scrimEl = el('div', '', 'scrim');
  /** The build card of a catalogue item in the context slot of a desktop
   *  (R1826). A telephone draws it as a page of the menu sheet instead. */
  private infoEl = el('div', '', 'infocard');
  /** The desktop DEV panel, in the frame of the OPTIONS popup (R1774). */
  private adminEl = el('div', `${KIT_ROOT} ${SHELL_DESK}`, 'admin');
  /** The ☸ OPTIONS menu: the root of the kit (R1731). `buildMenuShell`
   *  adds the class of its shell. */
  private settingsEl = el('div', KIT_ROOT, 'settings');
  private audioEl = el('div', 'panel', 'audio');
  /** The thin heat ramp at the edge of the clear map on a telephone
   *  (round 086 card 11 b, R1806): the heat legend of the sheet. */
  private heatRamp = el('div', '', 'heatramp');
  private notices = el('div');
  /**
   * The commit confirm card (R788). A commit is an order the fleet sails
   * to obey, so a right-click on a strategic object asks before it spends
   * the fleet. It is an inline card beside the toasts, in the idiom of
   * `coreCard()` — one sentence and two buttons, never a modal.
   */
  private commitCardEl = el('div', '', 'commitcard');
  /** What the confirm card asks about, or null while it is not up. */
  private commitAsk: { fleetId: number; siteId: number } | null = null;
  /** The id of the own item a DEPOT family head showed last, by family:
   *  [deployables, sea mines], 0 for none (R3244). */
  private depotShown = [0, 0];
  /** The selected hull whose card was the deploy ask last, or 0 (R1988). */
  private dockedSel = 0;
  /** The markup of the confirm card as it was last written. */
  private readonly commitHost = new MarkupHost(this.commitCardEl);
  /**
   * The top-bar core card (R1458): a press on the Data Core cell asks
   * before it spends a core, in the commit card's idiom — one question,
   * the trade, and two buttons. It stands under the cell, the place the
   * explainer card takes, because the cell is what the player pressed.
   */
  private coreCardEl = el('div', 'panel', 'corecard');
  /** The top-bar core card is up. */
  private coreAsk = false;
  /** The same change guard as `barTipHost`, for the card above. */
  private readonly coreCardHost = new MarkupHost(this.coreCardEl);
  /**
   * The rename slot (R1250, R2589, R2626, R2649): the rename card of a
   * hull, the name card of a course plot, the naming sheet of a seamount
   * and the cache sheet, one at a time (`overlay/namedesk.ts`). It is
   * built in the constructor body, for the casino panel's reason.
   */
  private readonly names: NameDesk;
  /**
   * REPORT and BLOCK (deployment.md §7.6, R1323, R1328): the report card,
   * the ⋯ and ⚑ marks that open it, and the blocks the chat tabs hide.
   * It is built in the constructor body, for the casino panel's reason.
   */
  private readonly reports: ReportDesk;
  /** What a quick line's position reads, from this seat's own sites and
   *  sieges (R1607). */
  private readonly posLook: PosLookup = posLookupOf(() => this.state);
  /**
   * The season banner's glass (`ui/banner.ts`), empty until an arrival
   * writes a card into it. main.ts hands this to `SeasonBanner`; the
   * overlay itself never writes to it.
   *
   * It lives inside `#ui` rather than beside it, for the stacking: `#ui` is
   * z-index 10, so anything in it is under `#blackout` (40) and under
   * `#landing` (50), which is the correct order — a title card must never
   * stand over the black it is emerging from, and never over the way in.
   * Inside `#ui` it takes z-index 45, over every panel and under `#fatal`
   * (100): a connection that has died says so over the ceremony.
   *
   * It is not one of the ten dockable panels and carries no `--out`: the
   * card does not arrive with the chrome, it arrives *at* the chrome.
   */
  readonly bannerEl = el('div', '', 'banner');
  /**
   * The replay film's glass (`fast/film.ts`, R531), empty until a film
   * opens. `main.ts` hands this to `ReplayFilm`; the overlay never writes
   * to it.
   *
   * It lives inside `#ui` for the banner's own reason, and it takes the
   * rung under the banner: a film covers the map and every panel, and a
   * title card or a dead connection still stands over a film.
   */
  readonly filmEl = el('div', '', 'film');
  /**
   * The chart furniture's DOM host (`fast/chart/`, R548), empty until a
   * piece mounts in it. `main.ts` hands this to `MapScene`; the overlay
   * never writes to it.
   *
   * It lies over the whole map inside `#ui` and takes no pointer, so a card
   * that wants a press sets `pointer-events: auto` on itself. It is the
   * LOWEST rung of the three: a title card, a film and a dead connection
   * all stand over the furniture.
   */
  readonly furnitureEl = el('div', '', 'furniture');
  /** The toasts: the lines of `#notices`, the repeat rule and the two
   *  toast clocks (`ui/notices.ts`, R806). */
  private readonly noticeCol = new NoticeColumn(this.notices);
  /** The two timer lines (R430, R824). Each one owns its own classes, its
   *  tab's markup and its `--frac`; this class states a claim and a share. */
  private readonly truceLine = new TimerLine(this.truceEl, this.truceTab);
  private readonly linkLine = new TimerLine(this.linkEl, this.linkTab);
  /** The chrome half of the look (R814). `setChromeLook` writes it. */
  private chromeLook: ChromeLook = AUTO_CHROME_LOOK;
  private stats = el('div', '', 'stats');
  /** The last lines of the statistics sweep, and the line on the screen
   *  (R1746). */
  private statsLines = '';
  private readonly statsHost = new MarkupHost(this.stats);
  /**
   * The chat (ui-map-interface.md §12.0 "The chat"): the chat band of a
   * telephone over the tab bar (R1799); on a desktop the chat box at the
   * bottom-left of the map under SMALL and LARGE, or the chat pane at the
   * foot of the dock column under DOCKED (R1838). One element takes the
   * three forms: it holds the head, the four channel tabs, the log and
   * the input row with QUICK and SEND.
   */
  private chat = el('div', '', 'chat');
  private chatHead = el('div', '', 'chathead');
  private chatLog = el('div', '', 'chatlog');
  private chatTabs = el('div', '', 'chattabs');
  private chatIn = el('div', '', 'chatin');
  private chatInput = document.createElement('input');
  /** The chat line (R1799, card 16 of round 085): the newest line for
   *  `chat_line_sec`, then the count. It stands in the slot of the peek
   *  line on a telephone and over the queue line on a desktop. */
  private chatLineEl = el('div', '', 'chatline');
  private readonly chatLineHost = new MarkupHost(this.chatLineEl);
  /** The peek of the chat box (R2057): the chat line inside a closed box
   *  of SMALL and LARGE, at the bottom-left where the box stands. */
  private chatPeekEl = el('div', '', 'chatpeek');
  private readonly chatPeekHost = new MarkupHost(this.chatPeekEl);
  private lastChatTabs = '';
  /** The form the chat stood in at the last render, and the size of the
   *  box: a change moves the element and repacks the console. */
  private chatFormNow: string | null = null;
  /** The chat band of a telephone stands (R1799). No setting keeps it: it
   *  opens on a press and closes on ▾ or on a menu tab. */
  private bandOpen = false;
  /** A drag of the grip of the desktop chat pane: where the finger began,
   *  the pane's height then and the room of the column. */
  private paneDrag: { id: number; y0: number; h0: number; column: number } | null = null;
  /** The map buttons (R1747, card 08 of mockup round 082): the VIEW
   *  button, at the right edge of a telephone and on the chart strip of a
   *  desktop (R2904). It is built once; `renderMapButtons` writes the count and
   *  the lit state. */
  private mapBtns = el('div', '', 'mapbtns');
  private viewBtn = document.createElement('button');
  /** The rose press (R1758): a transparent button over the compass rose's
   *  slot. The rose is canvas, so the press is DOM (§1). It centres the map
   *  on the hold, and it stands only while the rose draws. */
  private rosePress = document.createElement('button');
  /** The rose peek (R1802): HOME and its key beside the round unit, while
   *  a hovering pointer is on the rose press. The stylesheet shows it. */
  private rosePeek = el('div', '', 'rosepeek');
  /** The telephone scale bar beside the round unit (R1801). The frame
   *  loop writes it. */
  readonly scaleBar = new ScaleBar();
  /** The VIEW panel: the tiles of what stands over the map (R1747). */
  private viewPanel = el('div', KIT_ROOT, 'viewpanel');
  /** The chart's minimize control. The chart is canvas and cannot carry a
   *  DOM button, so the button is its own element parked over the
   *  top-right corner of the round unit (R1801) — §1's canvas/DOM split,
   *  the picture on the canvas and the control in the overlay. It stands
   *  after the rose press in the markup, so it is over the press. */
  private mapChrome = el('div', '', 'mapchrome');
  /** The HEAT chip on the round unit (R1849, card 17 a of mockup round
   *  086): it says that the disc shows the heat surface. */
  private heatChip = el('div', '', 'heatchip');
  private readonly heatChipHost = new MarkupHost(this.heatChip);
  /** The hint host (R2164): the armed order as a card strip at the context
   *  slot, while no card in the slot names the armed hull. It holds no
   *  control reminder at rest. */
  private hintEl = el('div', '', 'hint');
  private readonly hintHost = new MarkupHost(this.hintEl);
  /** The card in the context slot shows the armed order now (R2164), so
   *  the hint host stands empty. `renderSelection` writes it. */
  private armOnCard = false;
  /** The lift of the hint host over another card in the slot, in px. */
  private hintLift = 0;
  private tab: Tab = 'BUILD';
  /** Remembered sub-menu per tab, so returning to a menu lands where you left. */
  private sub: Partial<Record<Tab, string>> = {};
  /** The sheet's height: resting, or expanded to fill under the top bar.
   *  Only ever true in the sheet form factor (R233). */
  private expanded = false;
  /** The height a drag left the sheet at, in CSS pixels, or null while the
   *  sheet stands at one of its stops (R570). Only ever set in the sheet
   *  form factor under the setting of three heights (R1748), and a press
   *  in the tab bar clears it. */
  private freeH: number | null = null;
  /** The queue strip: one line, or opened into the full queue. A desktop
   *  starts at the stored state of `settings.queueOpen` (R2740); a
   *  telephone starts at its line. */
  private queueOpen = false;
  /** The advisor line: cut to one line, or folded open to the whole
   *  sentence (R1423). */
  private adviceOpen = false;
  /** The sea trials (R1572): the page's half of the card — whether it saw
   *  an open step, and when the last one closed. The rows stand open on a
   *  desktop and folded on a phone, as card 06 draws them, and folded on
   *  both in the first session of the device, where the line opens them
   *  (R2127). */
  private trialsCard = new TrialsCard();
  private trialsOpen = !SHEET_MQ.matches && !isFirstSession();
  /** The first frame of a new commander (R2127): while the welcome card
   *  is open, the toasts, the achievement cards and the season title card
   *  wait, and the page wires the last two to it. */
  readonly firstHold = new FirstHold(WELCOME_RELEASE_GAP_MS, WELCOME_HELD_NOTICES);
  /** The welcome card (R1572). `open` while it stands; `closed` once the
   *  player closed it on this page, so a frame that still states the card
   *  before the server's answer does not raise it again. */
  private welcomeEl = el('div', 'panel', 'welcome');
  private welcomeDim = el('div', '', 'welcomedim');
  private welcomeState: 'none' | 'open' | 'closed' = 'none';
  private welcomePlaced = false;
  /** The foot of the welcome card on a phone, in chrome px, as the last
   *  measure wrote it; -1 before one (R2138). */
  private welcomeB = -1;
  /** TAKE COMMAND opened the world clock's card, and the release of the
   *  first frame waits for it to go (R2138). */
  private releaseAfterClock = false;
  /** True while the open card stands on the screen: it steps behind a
   *  panel the player opened (R1637), and its two tags go with it. */
  private welcomeShown = false;
  /** The telephone sheet the season timelapse raised to its middle stop
   *  (R1620): where it stood before, and the count of sheet changes at the
   *  raise. A later count is a move of the player, and the sheet then
   *  stays where the player put it. */
  private tlLift: { collapsed: boolean; freeH: number | null; at: number } | null = null;
  /** The telephone sheet the play of the season timelapse collapsed
   *  (R1779): where it stood before, and the count of sheet changes at the
   *  collapse, read as `tlLift` reads its own. */
  private tlPlayDrop: { collapsed: boolean; expanded: boolean; freeH: number | null; at: number } | null = null;
  /** How many times `applySheet` ran. */
  private sheetMoves = 0;
  /** The capture log: last 3 collapsed, 20 expanded (plan §4.2, R83). */
  private warLogOpen = false;
  /** FOUND of ECONOMY · TREASURE stands unfolded (R2700). */
  private treasureFoundOpen = false;
  /** The ARCHIVE of FLEET · COMMISSIONS stands unfolded (R2715). */
  private commissionArchiveOpen = false;
  /** The keystone fold of TECH stands open (R2160). Closed by default. */
  private techKeysOpen = false;
  /** The names line of the captain codes stands open (R2176). */
  private capCodes = false;
  /** ECONOMY · AUCTION subpage: browse the book, or add an offer (v12). */
  private auctionPage: typeof AUCTION_PAGES[number] = 'BROWSE';
  /** Wall-clock of the last WarQuery, to debounce flip-driven refreshes. */
  private lastWarQuery = 0;
  /** True once the first flip asked for the WAR data ahead of the tab. */
  private warPrefetched = false;
  /**
   * Supplied by main.ts: every sov beam in view, one entry per working
   * source (R116). The server builds the list and decides who is entitled to
   * each row; this panel only ranks it.
   */
  beams: () => SovBeam[] = () => [];
  private queueStrip = el('div', 'panel', 'queuestrip');
  /** The draft form of DRAFT · INVENTION (R701): the picks not sent yet. */
  private readonly draft = new DraftForm();
  /** The typed systems the last INVENT DESIGN sends (numbers.md §17,
   *  R1922); `fast/actions.ts` puts them on the intent. 0 = untyped. */
  get inventWeapType(): number { return this.draft.weapType; }
  get inventShieldType(): number { return this.draft.shieldType; }
  /** The fleet (or 0 for the home base's Torpedo Battery) waiting for an aim
   * point; null means no firing solution is armed. */
  aiming: number | null = null;
  /** The base round a base shot fires (R86): a catalog TorpedoKind id,
   * picked on the base panel's pills; 0 falls back to the first stocked. */
  baseTorp = 0;
  /** The fleet waiting for an escort target to be picked, if any. */
  escorting = 0;
  /** The fleet waiting for a patrol far-end to be picked, if any. */
  patrolling = 0;
  /** The fleet waiting for an attack-move point to be picked, if any (R79). */
  hunting = 0;
  /** The fleet waiting for a move point to be picked, if any (R469). A move
   * needs a pick of its own, because a right-click on an enemy fleet sends
   * an attack-move: it was impossible to order a hull to keep course. */
  moving = 0;
  /** The fleet waiting for a gather field to be picked, if any (R469). */
  gathering = 0;
  /** The fleet waiting for a cell to SEARCH, if any (v2 §9.6, R2834). */
  searching = 0;
  /** The penned fleet waiting for a GO… target to be picked, if
   * any. The hull's officer and load are the fleet's own server state, so
   * the id is all the pick needs to carry. */
  deploying = 0;
  /** The kind the player laid last with each Sapper (R2635): LAY of the
   *  order row lays it again. It lives for the page, as the server keeps
   *  its own `last_lay` for a LAY that names no kind. */
  private readonly lastLay = new Map<number, number>();
  /** The kind LAY of a Sapper lays now as the overlay keeps it, 0 for none;
   *  the keys `f` and `n` read it (R2710). */
  lastLayOf(fleetId: number): number { return this.lastLay.get(fleetId) ?? 0; }
  /** Sets the kind LAY of a Sapper lays, from a key (R2710): `f` keeps the
   *  kind it laid, `n` the next kind. The card draws the kind at once. */
  setLastLay(fleetId: number, kind: number): void {
    this.lastLay.set(fleetId, kind);
    this.render(true);
  }
  /** The picker that stands open (R1893): the captain picker of a hull
   *  in the pens, the start sheet of a dive, or the LOADOUT sheet of a hull
   *  in the pens (R2031). `sel` is the selected captain, 0 until a row is
   *  pressed; the card then selects its own. `mix` is the draft of the
   *  LOADOUT sheet, which nothing sends until LOAD. */
  private pick: { kind: 'cap'; fleetId: number; sel: number }
    | { kind: 'dive'; depth: number; sel: number }
    | { kind: 'load'; fleetId: number; sel: number; mix: Stock[] }
    | { kind: 'stow'; fleetId: number; sel: number; mix: Stock[] } | null = null;
  /** The picker's element in the context slot of a desktop. */
  private pickEl = el('div', '', 'pickcard');
  private readonly pickHost = new MarkupHost(this.pickEl);
  /** The name of the list a telephone's picker page returns to, and its
   *  scroll offset, as the build card keeps them (087 card 02). */
  private pickFrom = '';
  private pickScroll = -1;
  private lastText = 0;
  private readonly topbarHost = new MarkupHost(this.topbar);
  /** The measured form of the desktop readout line (R1868). */
  private readoutFit: ReadoutFit | null = null;
  private lastBody = '';
  private lastQueue = '';
  private lastSel = '';
  /** The own fleet whose card has its ⋯ open, or 0 (R1809). */
  private moreFor = 0;
  /** The own fleet whose card has its EDIT grid open, or 0 (R2008). ⋯
   *  and EDIT exclude each other: at most one of the two is not 0. */
  private editFor = 0;
  /** The fleet row of FLEET · MISSIONS whose ⋯ is open, or 0 (R1816). */
  private rowMore = 0;
  /** The design of DRAFT · PRINTS or MASTERS whose ⋯ stands open, or 0
   *  (R1888). */
  private libMore = 0;
  /** The captain row of CAPTAIN · WARDROOM whose ⋯ is open, or 0 (R1889). */
  private capMore = 0;
  private lastChat = '';
  private readonly infoHost = new MarkupHost(this.infoEl);
  /** The head and the rows of the DEV panel as they were last written;
   *  '' while the panel is closed and holds no frame. */
  private lastAdmin = { head: '', body: '' };
  /**
   * The parts of the ☸ OPTIONS menu as they were last written (R1731): the
   * shell, then one guard for each part. Each part has an element of its
   * own, so a new summary in the rail never rebuilds a range in the pane
   * under a dragging finger.
   */
  private optsShell = '';
  private optsLast = { head: '', rail: '', body: '', foot: '', session: '', page: '' };
  /** The elements of one shell of the menu, written once per shell. */
  private optsEls: Partial<Record<'head' | 'rail' | 'body' | 'foot' | 'session', HTMLElement>> = {};
  private readonly audioHost = new MarkupHost(this.audioEl);
  private readonly heatRampHost = new MarkupHost(this.heatRamp);
  /** The legend card's two settings at the last draw (R1805). */
  private lastLegend = '';
  /** The VIEW panel's markup at the last draw, '' while it is shut. */
  private lastView = '';
  /** What the console and the VIEW button were last drawn for: the open
   *  units, the rose (R555), the chat size (R1665), the panel and the
   *  count. A change asks the console to pack again. */
  private lastConsole = '';
  /** What the build card is explaining, if it is open (R1826). */
  private infoOn: InfoTarget | null = null;
  /** The items of the list the card was opened from, in the order of
   *  the list: the ‹ › stepper of the page walks them (084 card 13). */
  private infoList: InfoTarget[] = [];
  /** The name of that list for the back chip: BUILD · BASE. */
  private infoFrom = '';
  /** The scroll offset of the list when the page opened, so the back chip
   *  returns to the list at the same row; -1 while no page stands. */
  private infoScroll = -1;
  /** A scroll offset the tab body takes after its next write, or null. */
  private bodyScrollTo: number | null = null;
  /** The backstory of the captain card stands unfolded (MORE). */
  private storyOpen = false;
  /** The TRAIN pick of the captain card stands open (R2087). */
  private trainPick = false;
  /** The hull that COMPARE marked, 0 for none (R1829, 084 card 17). */
  private compareFrom = 0;
  /** The level the picker of a building card shows (R1839); 0 shows the
   *  first level past the next. A new card starts at 0. */
  private levelPick = 0;
  /** The two hulls of the compare card while it stands, or null. */
  private comparePair: [number, number] | null = null;
  /** The row lit for the open card at the last pass, as `kind:id`. */
  private litKey = '';
  /**
   * An opened probe scan report, or null.
   *
   * It is drawn in the **selection** anchor (R203), not in the build card. A
   * report is a look at one place on the map, so it behaves like a
   * selection: it appears bottom-left, and it is gone the moment the player
   * clicks somewhere else. `renderSelection` reads this ahead of
   * `state.selection`, and the two can never both be set.
   */
  private scanReport: ChatMsg | null = null;
  /**
   * The lane card (round 088 card 09, R2658): a shipping lane of the
   * faction that a press on chart card 16 opened. It shares the selection
   * anchor as the scan report does, so every change of the selection drops
   * it. `fleet` is the own fleet SEND sends: the one selected at the press,
   * or the one selected last.
   */
  private laneOpen: { ref: LaneRef; fleet: number } | null = null;
  /** The own fleet at sea the player selected last (R2658): SEND of the
   *  lane card sends it after the press on the lane took the selection. */
  private lastOwnFleet = 0;
  /**
   * Touch chrome: the overlay grows the two controls a finger has no other
   * way to reach — ✕ DESELECT in the selection panel and ✕ CANCEL on the
   * hint line, because esc is a keyboard key and "never mind" is a mouse
   * button. A coarse pointer arms it up front; the first touch anywhere
   * arms it too, since a hybrid laptop reports a fine pointer until someone
   * puts a finger on the screen.
   */
  touchUI = coarsePointer();
  /** The keyboard reference (plan §5.1): '?', the KEYS button, esc closes. */
  keysOpen = false;
  /**
   * What the KEYS panel's chart-symbol group was last drawn for (R548,
   * card 40): the reading and the form factor, as one word.
   *
   * The group is drawn when the panel opens and when either of those two
   * moves, and never on a frame the panel is shut. An empty word means the
   * panel holds no group.
   */
  private keysChartAt = '';
  /** The ☸ OPTIONS menu (R124, R1731): the ☸ OPTIONS button opens it, and
   *  esc, the ✕ and a press on the dimmed map of the desktop popup, and
   *  the ✕ CLOSE cap of the telephone tab bar close it (R1754). A close
   *  takes the cover down at once, before the next render: a cover that
   *  stood until the 4 Hz render took a press meant for the chrome under
   *  it (R2162). */
  get settingsOpen(): boolean { return this.menuOpen; }
  set settingsOpen(open: boolean) {
    this.menuOpen = open;
    if (!open) this.settingsEl.classList.remove('on');
  }
  private menuOpen = false;
  /** The page of the menu: the telephone root, a section, ACCOUNT or the
   *  music player. The desktop draws MAP for the root. A close goes back
   *  to the root, so the next open starts there. */
  optsPage: MenuPage = 'root';
  /** The language pick that waits for RELOAD NOW, or null (R855). */
  optsLang: string | null = null;
  /**
   * The hub cards aboard the menu and the standing orders (R1731), or null
   * on a page with no meta server. main.ts builds it where a session can
   * stand behind the page.
   */
  optionsHost: OptionsHost | null = null;
  /** The heat page of the VIEW panel (round 086 card 10, R1806): the HEAT
   *  row opens it, and so does the HEAT tile while no mode paints; esc, its
   *  ‹ and a pick go back. It stands only while the panel stands. */
  heatOpen = false;
  /** The FACTION KEY section of the telephone VIEW panel is open (R1859).
   *  It folds to its head row by default, and a press on the head opens it
   *  or folds it again. The fold lasts the session, as the folds of the
   *  legend card do (R1805). */
  viewKeyOpen = false;
  /** The DEPTH and CURRENT fold of the telephone VIEW panel is open
   *  (R2616). It folds by default, as the key section does. */
  viewDeepOpen = false;
  /** The VIEW panel (R1747): a press on VIEW opens and closes it; a press on
   *  the map or on any other control, a CHAT tile and esc close it. */
  viewOpen = false;
  /**
   * The two-press gate of every control that destroys something (R696).
   * Two presses rather than a modal: a modal over a live map stops the
   * game to ask a question. The first press arms one key and the control
   * shows its armed face, which names the act (R2069); a second press
   * inside the window fires. A press on any other control disarms, and
   * the timer disarms the control that sits armed under a stray press
   * made minutes later. Every panel is
   * markup-diffed, so the gate's own redraw draws the new label.
   */
  private gate = new ArmGate(CHROME_ARM_MS, () => this.render(true));
  /** `gate.is`, bound once for the builders that draw an armed face. */
  private readonly armed = (key: string): boolean => this.gate.is(key);
  /**
   * The CASINO panel (casino.md). It owns the host inside the constant
   * shell `casinoTab()` returns, and it holds its own markup guard, so a
   * table frame writes its half of the screen and leaves the dock alone.
   *
   * It is built in the constructor body and not here, because a field
   * initialiser runs before the constructor's parameter properties are
   * assigned, and the panel is handed `state` and `actions`.
   */
  private readonly casino: CasinoPanel;
  /**
   * The casino pane of a desktop (R2198, ui-map-interface.md §12.4): the
   * casino wide over the map under the desktop casino setting UNDOCKED. It
   * stands in the root of the chrome, as the chat box does, and holds a
   * head (the name, DOCK and the close) and the host the panel draws its
   * board and its table into. It stands while ECONOMY · CASINO is open.
   */
  private readonly casinoPane = el('section', 'panel', 'casinopane');
  private readonly casinoPaneHead = el('div', 'cp-head');
  private readonly casinoPaneBody = el('div', 'cp-body');
  private readonly casinoPaneHost = el('div', 'casino-host wide');
  /**
   * The season timelapse of WAR · SOVEREIGNTY (card 09 of round 076,
   * R1620): its scrubber, its band over the map, and what the tab draws of
   * it. main.ts hands it the map and reads `showing` every frame.
   */
  readonly warTl: WarTimelapse;
  /** The media player card (ui-map-interface.md §15): 'm' opens it, and
   * 'm', esc or ✕ closes it. */
  audioOpen = false;
  /** The display and audio switches (R124), read by main.ts every frame.
   * Loaded once here; every flip goes back through saveSettings. */
  readonly settings: GameSettings = loadSettings();
  /** Takes off the MIRRORED LAYOUT listener (R2883). */
  private offMirror: () => void = () => {};
  /** What the audio half of the chrome draws, pushed in by main.ts. Until
   * the manifest lands this says "nothing published", which is also the
   * final answer on a host that published nothing. */
  private audio: UIAudio = NO_AUDIO;
  /** The state the link timer renders from. The change guards for the two
   * timer lines live in `TimerLine` now (R824). */
  private linkDown = false;
  /** Whether the link timer claims the lane. A `column` notices style
   * shows the lane while either timer stands, so the lane's own count has
   * to know about both (R824). */
  private linkClaimed = false;
  /** The link claim of the last `setLink`, which the time cell of the
   *  readout line states (R1749). */
  private linkClaimNow: TimerClaim | null = null;
  /**
   * The retry interval the link drain empties over (R430). `setLink` is
   * handed the *end* of the backoff and never its length, so the first
   * frame that names a new `retryAt` is taken as the start of it.
   */
  private linkRetryAt: number | null = null;
  private linkRetryFrom = 0;
  /** Change guard for the tooltip markup (plan §5.3): a data-driven
   * re-render under a stationary cursor only touches the DOM on change. */
  private readonly tipHost = new MarkupHost(this.tooltip);
  /**
   * The label of the pinned peek's one button (R134, R1294): MARK, or
   * REMOVE over an own map marker, or null for no button. `fast/tooltip.ts`
   * writes it when it pins a peek and clears it when the peek goes.
   */
  private tipAction: string | null = null;
  /** A press on the pinned peek's button. `fast/main.ts` fills it in. */
  onTooltipAction: (() => void) | null = null;
  /**
   * The work press of the pinned peek beside MARK (v2 §9, R2629, R2649):
   * SEARCH HERE or SEARCH, on water or on a wreck, or null for none. `fast/tooltip.ts`
   * writes it with the peek's action.
   */
  private tipWork: { label: string; title: string } | null = null;
  /** A press on the work press. `fast/main.ts` fills it in. */
  onTooltipWork: (() => void) | null = null;
  /** Takes every peek away, for a ⚑ press in the peek (R2633).
   *  `fast/main.ts` fills it in. */
  onTooltipDismiss: (() => void) | null = null;
  /** A press on a row of the cell list, with the key of its object, or on
   *  the press back to the list, with '' (R2708, R2851).
   *  `fast/main.ts` fills it in. */
  onTooltipObject: ((key: string) => void) | null = null;
  /** A press on the pager of the cell list, with the page (R2851).
   *  `fast/main.ts` fills it in. */
  onTooltipPage: ((page: number) => void) | null = null;
  /** MARK on a contact card: the marker menu on the contact (R2002).
   *  `fast/main.ts` fills it in; a page with no markers leaves it null. */
  onMarkContact: ((fleetId: number) => void) | null = null;
  /** VIA LANE of the on-ramp offer on an own fleet card (R3075): the lane
   *  course goes out. `fast/main.ts` fills it in. */
  onLaneOffer: ((fleetId: number) => void) | null = null;
  /**
   * Which top-bar control the explainer card is open on: a commodity key,
   * `clock`, or the empty string for closed. A hover writes it on a
   * desktop and a tap writes it on a telephone; the card itself holds no
   * other state.
   */
  private tipOn = '';
  /** The same change guard as `tipHost`, for the card above. */
  private readonly barTipHost = new MarkupHost(this.barTip);
  /** True while the explainer card stands; `showBarTip` keeps it. */
  private barTipShown = false;
  /** Who hears the explainer card open and close (`onBarTip`, R2139). */
  private readonly barTipHeard: ((open: boolean) => void)[] = [];
  /**
   * What a free card shows while `tipOn` is `FREE_TIP`: `title` for a
   * title hold (R1555), or `term:<id>` for a definition (R1557), so a
   * second press on the same term button puts the card away.
   */
  private tipFree = '';
  /** The title hold on touch (R1555), while the overlay is on the page. */
  private titleHold: TitleHoldWiring | null = null;
  /** The dev admin panel (R50), toggled from the top bar or by backtick.
   * Open/closed outlives a reload (R94): the owner's test client refreshes
   * on every build, and the panel should come back where they left it. */
  private adminOpen = readAdminOpen();
  // The two admin toggles, seeded from the hello and flipped locally after
  // (R94). The server ops echo no state, but the hello carries where both
  // stand at join, so a reload no longer guesses: `reveal` is per-connection
  // and comes back off, `autopilot` lives on the commander and survives.
  private revealOn = false;
  private autopilotOn = false;
  // TOGGLE VEIL and TOGGLE CACHES live on the connection like `reveal`,
  // and the hello says where both stand (R2825).
  private adminCachesOn = false;
  private veilOff = false;
  /** The hello these two were last seeded from; a new one re-seeds them. */
  private adminSeeded: unknown = null;

  /**
   * The subscriptions the constructor makes outside its own subtree: the
   * window hotkey handler, the capture-phase touch latch, the state change
   * hook, the form-factor query and the capture-phase dismissal of the
   * explainer card. destroy() needs the exact references to take them off
   * again, so each one is kept here rather than left anonymous.
   */
  private readonly onKey = (e: KeyboardEvent): void => this.shellKey(e);
  /** Stops the watch of the focus source (R2082). */
  private unwatchFocus: () => void = () => {};
  private readonly onCardKey = (e: KeyboardEvent): void => this.cardKey(e);
  private touchLatch: ((ev: PointerEvent) => void) | null = null;
  private offState: () => void = () => {};
  /**
   * The form-factor subscription. JS switches on the same predicate CSS
   * does (`isSheet()`), so the class on `#ui` and the sheet media block can
   * never disagree.
   */
  private onForm: () => void = () => {};
  /** The window resize: the room of the open queue strip may have shrunk. */
  private onRoom = () => this.foldCrampedQueue();
  /**
   * A press anywhere that is not the top bar's explainer card, or the
   * control that opened it, puts the card away. It is on the **document**
   * and not on `#ui`, because a tap on the map has to dismiss it too and
   * the canvas is not a child of this overlay.
   */
  private tipDismiss: ((ev: Event) => void) | null = null;
  /**
   * Safari's pinch. `gesturestart` / `gesturechange` / `gestureend` are
   * proprietary and are not in `lib.dom`, so they are registered by name
   * and typed as the plain `Event` they are handled as. The canvas owns the
   * pinch (§8): a pinch that begins on a panel must not zoom the page.
   */
  private onGesture: (ev: Event) => void = (ev) => ev.preventDefault();
  /** The sheet grip's teardown while one is installed (§12.0). */
  private dragOff: (() => void) | null = null;

  /** Supplied by main.ts: the fleets the client currently knows about. */
  fleets: () => UIFleet[] = () => [];
  /** Supplied by main.ts: any fleet by id, own or not, or null if unknown. */
  contact: (id: number) => Contact | null = () => null;
  /** Supplied by main.ts: where every hull the fleet layer holds stands.
   *  The siege window reads it: the sides near a target and the squad
   *  readiness (`ui/siegewindow.ts`). */
  presence: () => FleetPresence[] = () => [];
  /** Supplied by main.ts: the hull draws its chevrons at full alpha now
   *  (`FleetLayer.chevronsShown`, R2457). */
  chevronsShown: (id: number) => boolean = () => false;
  /** Supplied by main.ts: the design and the act of a monster the monster
   *  layer draws, or null (R2719). The monster card states both. */
  monsterOf: (id: number) => { kind: number; design: number; act: NpcAct } | null = () => null;
  /** Supplied by main.ts: the heading and the telegraph step that the
   *  helm keys of the map order a fleet, or null (R2707). */
  helmOf: (id: number) => { headingDeg: number; step: number; top: number } | null = () => null;
  /** Supplied by main.ts: the on-ramp offer that stands for an own fleet,
   *  or null (R3075). */
  laneOfferOf: (id: number) => { lane: string; saveMapSec: number } | null = () => null;
  /**
   * Supplied by main.ts: RALLY of the window head and the squad card. It
   * places a squad marker on the object's cell (DEFEND when the faction
   * holds the object, ATTACK otherwise) unless an own live marker stands
   * there, and sends the H-hour quick line to SQUAD with the object.
   */
  rally: (siteId: number, cell: number, defend: boolean) => void = () => {};
  /** The wall ms until which RALLY rests after a press. */
  private rallyRestUntil = 0;
  /**
   * Supplied by main.ts: is this account id one of the viewer's squadmates?
   * The squad is the clan members who share the viewer's squad number, and
   * main.ts owns the set because the two map layers mark the same people
   * (R199). The viewer's own id is not a squadmate.
   */
  squadmate: (accountId: number) => boolean = () => false;
  /**
   * Supplied by main.ts: the reader's look and hold symbol (R1571). The
   * squad chip's icon and the marks key draw from it, so the chrome draws
   * the marks the map draws. Null draws the chip with no icon.
   */
  squadLook: (() => SquadLook) | null = null;
  /**
   * Supplied by main.ts: the Chart theme's word over the piece table
   * (themes.md §3.6), or null for AUTO. The chart legend of KEYS lists a
   * row while its piece is on under it (ui-map-interface.md §17.3.40).
   */
  chartPieces: ChartPieceOverlay | null = null;

  /** The ship and structure icon set of the reader's look (R1844, R1872),
   *  or none: the AUTO set. */
  private get sceneLook(): SquadLook['scene'] | undefined { return this.squadLook?.().scene; }
  /**
   * Supplied by main.ts: the World field `factionKey` (R1568) is `key`.
   * The theme decides whether the faction key exists. Under `off` the
   * legend card has no FACTIONS section, and the LEGEND tile of a
   * telephone leaves the VIEW panel while no deep field stands, because
   * it would toggle nothing (R2832).
   */
  factionKeyThemed = true;
  /** The roster card under the squad chip (R1571, round 075 card 16). */
  private squadCardEl = el('div', 'panel', 'squadcard');
  /** The marks key (R1571, round 075 card 17). */
  private squadKeyEl = el('div', 'panel markskey', 'squadkey');
  private squadOpen = false;
  private squadKeyOpen = false;
  /** The same change guard as `barTipHost`, for the two cards above. */
  private readonly squadCardHost = new MarkupHost(this.squadCardEl);
  private readonly squadKeyHost = new MarkupHost(this.squadKeyEl);
  /** When the chip's pulse of a squad change began (performance.now),
   *  or null while no pulse runs (round 075 card 18). */
  private squadFlashAt: number | null = null;
  /**
   * Supplied by main.ts: the chrome is veiled behind the season summary
   * (R1519, `ui/seasonover.ts`). The shell's keys then do nothing, except
   * the backtick that drops the dev console.
   */
  chromeVeiled: () => boolean = () => false;
  /**
   * Supplied by main.ts: OPTIONS · PHOTO enters photo mode (R1623,
   * `ui/photomode.ts`). The row stands only where a page wires it.
   */
  onPhoto: (() => void) | null = null;
  /**
   * Supplied by main.ts: OPTIONS · REPORT opens the REPORT sheet (R1626,
   * `ui/reportsheet.ts`). The row stands only where a page wires it: the
   * sheet needs the meta server.
   */
  onReportProblem: (() => void) | null = null;
  /** Supplied by main.ts: true while the REPORT sheet stands. The
   *  welcome card steps behind it (R1637). */
  reportOpen: () => boolean = () => false;
  /**
   * Supplied by main.ts: the cover state changed (R1755, §10.5). The frame
   * loop pauses the map under the telephone menu and caps it under the
   * full sheet. The overlay states the state and knows no frame loop.
   */
  onCover: ((cover: Cover) => void) | null = null;
  /** Supplied by main.ts: the countdown of the idle sea view (R2865), in
   *  wall seconds, or null while none runs (`ui/seaidle.ts`). */
  seaIdleCountdown: (() => { leftSec: number; totalSec: number } | null) | null = null;
  /** Supplied by main.ts: the VIEW panel opened. The peek stands on the
   *  card rung over the panel, so the panel takes it away, as a press on
   *  the map that puts a peek up takes the panel away (R1747). */
  onViewOpen: (() => void) | null = null;
  /** The cover state as `onCover` last heard it. */
  private cover: Cover = 'none';

  /**
   * The shell itself (`#ui`). The replay film puts its own class here, so
   * the panels travel out on the arrival's distances while a film runs
   * (R531). Nothing else writes to it from outside.
   */
  get shellEl(): HTMLElement { return this.root; }

  /**
   * The explainer card under the top bar (the world clock card and the
   * other bar cards) holds the top slot of the map with the guest warning
   * (R2139, ui-map-interface.md §3.5). `fn` hears each open and each close.
   */
  onBarTip(fn: (open: boolean) => void): void { this.barTipHeard.push(fn); }

  /** Closes the explainer card: the guest warning takes the slot (R2139). */
  closeBarTip(): void { this.setBarTip(''); }

  /** True while a telephone's sheet stands open over the map: SEA TRIALS
   *  opens it, and the season title card of the release then stays down,
   *  because it would draw over the rows (R2138). */
  get sheetStandsOpen(): boolean { return isSheet() && !this.collapsed; }

  /** The dock stands at its bar: the closed sheet of a telephone, or the
   *  desktop column at rest (R2190). */
  private get collapsed(): boolean { return this.dock.classList.contains('collapsed'); }

  constructor(private state: GameState, private actions: UIActions, opts: OverlayOpts = {}) {
    this.tabFit = opts.noTabFit !== true;
    // A session after the first starts with the whole legend minimized (R2057).
    if (!isFirstSession()) this.settings.legend = false;
    // A desktop strip opens where the viewer left it (R2740).
    if (!isSheet()) this.queueOpen = this.settings.queueOpen;
    // A line with a place is the INBOX GO TO (R2370): the map goes to the
    // cell, and a telephone sheet folds so the map shows.
    this.noticeCol.setGo((cell) => {
      this.actions.focusCell(cell);
      this.collapseSheet();
    });
    // The legend card stands minimized while the welcome card is the first
    // frame, and returns last in the release (R2127).
    this.firstHold.watch({
      hold: () => {
        holdLegend();
        factionKey.refresh();
        // A toast that rose before the card waits for its close too
        // (R2138). The live region already said it, so the release puts
        // the line back on the glass alone.
        for (const t of this.noticeCol.liftStanding()) {
          this.firstHold.defer('', RANK.notice, () => this.noticeCol.push(t.text, t.opts));
        }
      },
      release: () => {
        this.firstHold.defer('legend', RANK.fold, () => { releaseLegend(); factionKey.refresh(); });
      },
    });
    this.casino = new CasinoPanel(state, actions, this.gate);
    this.reports = new ReportDesk(actions, () => this.render(true), () => this.state.selfAccountId);
    this.warTl = new WarTimelapse({
      state,
      query: () => actions.timelapseQuery(),
      bandHost: this.root,
      changed: () => this.render(),
      titleHeld: () => this.titleHeld(),
      focus: (on) => this.timelapseFocus(on),
      play: (on) => this.timelapsePlay(on),
    });
    this.root.id = 'ui';
    // The context slot (§12.0): the three cards that explain a thing
    // stand in one place, in the rank of `ui/ctxslot.ts`.
    this.slot = new ContextSlot(this.root);
    this.names = new NameDesk({
      state, actions, slot: this.slot,
      fleets: () => this.fleets(),
      cardEnv: () => this.cardEnv(),
      refuse: (text) => this.notice(text, true),
      cacheNoteMark: (c) => this.reports.cacheNoteMark(c),
    });
    this.slot.host('selection', this.selection);
    this.slot.host('info', this.infoEl);
    this.slot.host('commit', this.commitCardEl);
    this.slot.host('pick', this.pickEl);
    this.slot.host('rename', this.names.el);
    // The column wrote its class at the field's birth, so the id follows it.
    this.notices.id = 'notices';
    // The `ledger` look stamps each line with the world clock the top bar
    // shows (R824). The column holds no game state, so it is handed the
    // reading and never the state.
    this.noticeCol.setClock(() => hhmmss(this.state.worldClockSec()));
    this.commitCardEl.hidden = true;
    this.pickEl.hidden = true;
    this.coreCardEl.hidden = true;
    this.squadCardEl.hidden = true;
    this.squadKeyEl.hidden = true;

    this.chatInput.id = 'chatinput';
    this.chatInput.placeholder = S.chat_placeholder;
    // One cap for one box. The game socket takes 240 characters and the
    // meta server's clan socket takes 500 (R225), so the box holds the
    // smaller of the two and every line fits both rooms. The headroom is
    // deliberate: the player never types a line one room refuses.
    this.chatInput.maxLength = CHAT_MAX_LEN;
    this.chatInput.setAttribute('enterkeyhint', 'send');
    // The head (R1799, R1838): the chat icon and the word, the grip of the
    // desktop pane, the size press and DOCK of the box, UNDOCK of the pane,
    // and the close. DOCK and UNDOCK are square presses with a glyph, and
    // the word is their accessible name (R1866). It names no channel: the open tab right under it does
    // (R1841). The stylesheet shows the presses of the form that stands.
    // The input row: QUICK, the field and SEND. Both are built once; only
    // the size glyph and the close are written later.
    this.chatHead.innerHTML = `${chatIcon('chat')}<span class="cw">${esc(S.chat_head)}</span>`
      + `<span class="cgrip" title="${esc(S.chat_grip_title)}"><i></i></span>`
      + '<button class="csize sqp" data-act="chatsize"></button>'
      + dockPressHtml({ way: 'dock', act: 'chatdock', word: S.chat_dock, title: S.chat_dock_title, cls: 'cdock' })
      + dockPressHtml({
        way: 'undock', act: 'chatundock', word: S.chat_undock, title: S.chat_undock_title, cls: 'cdock cundock',
      })
      + '<button class="cclose sqp" data-act="chatclose"></button>';
    const quick = document.createElement('button');
    quick.className = 'cquick';
    quick.dataset.act = 'chatquick';
    quick.title = S.chat_quick_title;
    quick.setAttribute('aria-label', S.chat_quick_title);
    quick.innerHTML = chatIcon('quick', 17);
    const send = document.createElement('button');
    send.className = 'csend';
    send.dataset.act = 'chatsend';
    send.textContent = S.chat_send;
    this.chatIn.append(quick, this.chatInput, send);
    // The peek of the closed chat box (R2057) stands first; the stylesheet
    // shows it on a closed box alone.
    this.chat.append(this.chatPeekEl, this.chatHead, this.chatTabs, this.chatLog, this.chatIn);
    // The casino pane (R2198): its head is built once, and the panel
    // writes the host inside the body.
    this.casinoPane.setAttribute('aria-label', S.casino_head);
    this.casinoPane.hidden = true;
    this.casinoPaneHead.innerHTML = paneHeadHtml();
    this.casinoPaneBody.append(this.casinoPaneHost);
    this.casinoPane.append(this.casinoPaneHead, this.casinoPaneBody);
    // The chat log is a log, and a quiet one (R2052): a screen reader
    // reads it on a visit, and no line speaks by itself, because the
    // autopilot chat of a busy world would bury the notices.
    this.chatLog.setAttribute('role', 'log');
    this.chatLog.setAttribute('aria-live', 'off');

    // The sheet's grip (§12.0). It is drawn in both form factors' DOM and
    // shown in one: the stylesheet's sheet block is what puts it on the
    // screen, and `syncFormFactor` is what makes it work.
    this.grip.title = S.grip_title;
    this.grip.innerHTML = '<span class="grab"></span>';
    // The stylesheet orders the dock's rows (R1749, R1763): both form
    // factors stand the peek line over the head line of the queue strip,
    // and the strip directly over the tab bar, where the closed telephone
    // sheet keeps its head line. The siege row and the alarm list follow
    // the dock head on a desktop (R1753); the telephone block stands them
    // in the slot of the peek line.
    // The chat and the chat line stand over the queue strip (R1799).
    this.dock.append(this.dockHead, this.grip, this.siegeEl, this.alarmEl, this.selHead, this.body, this.peekEl,
      this.chat, this.chatLineEl, this.queueStrip, this.groupRow.el, this.tabs);
    // The two caps of the telephone tab bar (R1749). Each one is a fixed
    // control with a fixed act; its inside is written by `renderCaps`.
    this.capInbox.id = 'capinbox';
    this.capInbox.className = 'cap';
    this.capInbox.dataset.act = 'meta';
    this.capInbox.dataset.tab = 'INBOX';
    this.capInbox.title = S.cap_inbox_title;
    this.capMenu.id = 'capmenu';
    this.capMenu.className = 'cap';
    this.capMenu.dataset.act = 'opts';
    this.capMenu.title = S.cap_menu_title;
    // The two drains ride inside the bar (R430). `renderTopbar` wipes the
    // bar's innerHTML, so it puts them back on every rebuild; this is where
    // they are built, and the two children of each are built with them.
    this.truceEl.append(this.truceFill, this.truceTab);
    this.linkEl.append(this.linkFill, this.linkTab);
    this.topbar.append(this.truceEl, this.linkEl);
    // The dim of the welcome card goes first, so every panel stands over it
    // and only the map takes the shade (R1572).
    this.root.append(this.welcomeDim, this.stats, this.topbar, this.dock, this.casinoPane, this.selection, this.infoEl, this.groupBar.el, this.slotToast.el,
      this.mapBtns, this.viewPanel, this.rosePress, this.rosePeek, this.mapChrome, this.heatChip,
      this.scaleBar.el, this.peekLead, this.tooltip, this.barTip,
      this.scrimEl, this.keysEl,
      this.adminEl, this.settingsEl, this.audioEl, this.heatRamp, this.notices,
      this.commitCardEl, this.pickEl, this.coreCardEl, this.squadCardEl, this.squadKeyEl,
      this.names.el, this.reports.el, this.hintEl,
      this.furnitureEl, this.bannerEl, this.filmEl, this.welcomeEl);
    document.body.appendChild(this.root);
    // The two bar heights the sheet layout stacks against (§10.3).
    installLayoutVars();
    // The form of the desktop readout line: full, or narrow where its
    // room is too narrow (R1868).
    this.readoutFit = installReadoutFit(this.root, this.topbar, this.dock);
    // The MIRRORED LAYOUT of a desktop (R2883): the setting at boot, and
    // each write after it (the OPTIONS row, the account's copy), so a
    // change lays the console out again with no reload.
    setMirrorDesk(this.settings.mirrorDesk);
    this.offMirror = onSettingsWrite((s) => setMirrorDesk(s.mirrorDesk));
    // The console geometry (R218). One owner, and the overlay is the only
    // party that knows which units are open.
    installCluster(() => this.consoleState());
    // A desktop drags the head of an own fleet's card onto a slot of the
    // group bar (R1817, 085 card 13). The drop is the long press's assign.
    new HeadDrag(this.selection, this.groupBar.el, () => {
      const sel = this.state.selection;
      return sel?.kind === 'fleet' ? this.seaFleet(sel.id)?.name ?? null : null;
    }, (n) => this.holdSlot(n), () => this.root);
    // The keyboard reference is static markup — built on each open, because
    // the heat-map rows of CURRENTS and DEPTH follow the flags (R2870).
    this.keysEl.innerHTML = keysPanelHtml(this.hasFlag);

    // The channel tabs are the tab strip of the choice kit (R2011), built
    // once (R237): `renderChat` lights a tab and writes its count in place.
    // The press is delegated, like every other control in the shell: it
    // acts on pointerdown, and Enter and Space reach the same body.
    this.chatTabs.innerHTML = tabStrip(CHANNELS.map(([label, ch]) => ({ id: String(ch), label })), '', 'channel',
      { label: S.chat_head, size: 'compact' });
    // The chart's minimize control. Static markup: it says the same thing
    // for the whole of a session, and only its position moves.
    this.mapChrome.innerHTML = `<button class="min" data-act="minimap"`
      + ` title="${S.map_min_title}">–</button>`;
    // The VIEW button (R1747). It is built once and keeps its node, so the
    // long press of VIEW (`installViewHold`, R1610) never loses its button
    // under a finger.
    this.viewBtn.type = 'button';
    this.viewBtn.className = 'mb view';
    this.viewBtn.dataset.act = 'view';
    this.viewBtn.title = VS.view_title;
    this.viewBtn.setAttribute('aria-expanded', 'false');
    this.viewBtn.setAttribute('aria-controls', 'viewpanel');
    this.mapBtns.append(this.viewBtn);
    // The rose press (R1758) carries the act of the name in the dock head:
    // it centres the map on the hold. `data-from` tells the dispatcher to
    // answer on the rose. It holds no words of its own on the glass, so the
    // name a screen reader reads is its label.
    this.rosePress.type = 'button';
    this.rosePress.id = 'rosepress';
    this.rosePress.dataset.act = 'home';
    this.rosePress.dataset.from = 'rose';
    this.rosePress.title = VS.rose_title;
    this.rosePress.setAttribute('aria-label', VS.rose_title);
    // The rose is a visible control (R1802): a hover lights its ring and
    // its glyph on the canvas, and the stylesheet shows the peek.
    this.rosePress.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') noteRoseHover(true); });
    this.rosePress.addEventListener('pointerleave', () => noteRoseHover(false));
    this.rosePeek.setAttribute('aria-hidden', 'true');
    this.rosePeek.innerHTML = rosePeekHtml(VS.rose_peek, KEYS.map.home.label);
    this.chatInput.addEventListener('keydown', (e) => {
      e.stopPropagation(); // typing must never reach the map's key handler
      if (e.key !== 'Enter') return;
      this.sendChat();
    });
    this.installChatDock();

    for (const t of GAME_TABS) {
      const b = document.createElement('button');
      b.className = 'gt';
      // The glyph and the word (R2214). `fitTabs` writes the form of each
      // tab at a form-factor flip, a new width of the bar and a new setting
      // of the tab labels. The full word is the name and the title of the
      // tab in every form.
      b.innerHTML = tabInnerHtml(t, tabLabel(t));
      b.title = tabLabel(t);
      b.setAttribute('aria-label', tabLabel(t));
      // A menu switches on the press and not on the release (R237). `click`
      // fires on pointerup, so a menu opened a beat after the finger landed
      // and a press that slid off the button opened nothing at all.
      b.dataset.act = 'tab';
      b.dataset.tab = t;
      this.tabs.appendChild(b);
    }
    // A touch that rests on a tab names it in a bubble and does not press
    // it; a shorter touch presses it at the release (R2214, round 102 card
    // 15). A mouse keeps the press of fixed chrome (R237).
    installTabHold(this.tabs, (b) => { this.actButton(b); this.render(true); });
    // The bar fits its six tabs again on a new width (R2214): a new size
    // of the dock, a turn of the telephone, a new UI scale. The plan reads
    // the width alone, so a new form of a tab never asks again.
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => this.fitTabs()).observe(this.tabs);
    // The face of the chrome may load after the first fit, and a fall-back
    // face measures other widths.
    void document.fonts?.ready.then(() => this.fitTabs(true));
    // The strip acts on the RELEASE and not on the press (R577). It is one
    // of the panels that pan under a finger (`touch-action: pan-y`), and a
    // press that acted at once toggled the strip, or pressed a job's
    // button, on the first pixel of every scroll. A press that lands on no
    // button of the strip toggles the queue, so the head is the whole
    // strip and not one control in it.
    this.releaseHost(this.queueStrip, { onTap: (btn) => {
      if (btn) { this.actButton(btn); return; }
      // The closed telephone sheet holds the queue line alone (R1763), so
      // its press opens the sheet with the whole queue open in it.
      if (isSheet() && this.collapsed) {
        this.queueOpen = true;
        this.openSheet();
        this.applySheet();
        return;
      }
      // A desktop press on the line minimizes the open strip, or opens the
      // minimized one at its stored size (R2740).
      if (!isSheet()) { this.setQueueDesk(!this.queueOpen, this.settings.queueLarge); return; }
      this.queueOpen = !this.queueOpen;
      // A manual open gives the strip its default share of the dock
      // (R1202). A free height under the resting stop goes, so the sheet
      // stands at its resting stop again; a free height above it stays
      // (R1222). `applySheet` renders.
      if (this.queueOpen && this.freeH !== null && this.freeH < this.sheetStops().rest) {
        this.freeH = null;
        this.applySheet();
        return;
      }
      this.render(true);
    } });
    // A new width can cut the advisor line, or give it room, without a
    // new markup, so the peek line checks the fit again on each resize
    // (R1423).
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => this.fitAdvice()).observe(this.peekEl);
    // The shell's own keys (`shellKey`, R539, R543).
    window.addEventListener('keydown', this.onKey);
    // A focus that a pointer press gave lets Tab walk the menus (R2082).
    this.unwatchFocus = watchFocusSource();
    // The keys of the card of a docked fleet (`cardKey`, R1989), in the
    // capture phase, before the map handlers.
    window.addEventListener('keydown', this.onCardKey, true);
    // One delegated handler for every generated button in the dock and the
    // selection panel. It fires on pointerdown, not click: the panels are
    // regenerated four times a second, and a button destroyed between press
    // and release would swallow the click entirely.
    //
    // Which left the dock unusable from a keyboard (R106). The controls are
    // real <button>s, so Tab has always reached them and a native button
    // turns Enter and Space into a `click` — but nothing listened for click,
    // and pointerdown is the one activation a keyboard cannot produce. So
    // keydown routes to the same handler. Listening for `click` instead
    // would have fired twice for every mouse press; a mouse never produces
    // keydown, so this cannot double-act.
    //
    // The chat log is on the list too. A squad-chat line that carries a
    // probe scan can hold two buttons — REPORT and SHOW — and they used to
    // ride a listener of their own. They are ordinary generated buttons, so
    // they go through the one dispatcher like every other control (R203).
    // The console's hosts (R218, R1747) are on the list for the same reason
    // as the rest: the map buttons and the three minimize buttons are
    // ordinary controls, and they go through the one dispatcher. A touch
    // press on VIEW is the exception: `installViewHold` takes it first, to
    // tell a tap from the long press of the quick wheel (R1610).
    //
    // The dock body is NOT on the list (R578). It scrolls under the same
    // finger as the queue strip, so it takes the release rule instead.
    //
    // Nor are the panels that scroll (R1206 gap 1, §16.8): the four cards,
    // the admin panel, the chat log and the build card all pan under the
    // finger (`touch-action: pan-y`), so each one acts on the release. The
    // list below is the fixed chrome, which keeps the press.
    // The explainer card is fixed chrome too: the title of a commodity's
    // card is a term button (R1557). The welcome card is not (R2120): its
    // fold press stands in the body that scrolls, so it takes its own rule
    // below. The peek line is fixed chrome over the tab bar, and the dock
    // head is fixed chrome at the top of the desktop dock (R1749): neither
    // scrolls under a finger, so their controls act on the press.
    for (const host of [this.topbar, this.hintEl, this.peekEl, this.dockHead, this.selHead,
      this.mapBtns, this.mapChrome, this.rosePress, this.chatTabs, this.chatHead, this.chatLineEl,
      this.chatPeekEl, this.tabs, this.casinoPaneHead,
      this.commitCardEl, this.coreCardEl, this.names.el, this.reports.el,
      this.barTip, this.squadCardEl, this.squadKeyEl, this.furnitureEl]) {
      host.addEventListener('pointerdown', (ev) => this.onAction(ev));
      this.keyActivates(host);
    }
    // A press on the scrim closes the modal over it (round 083 card 16).
    // It is a bare element and not a button, so it takes its own press.
    this.scrimEl.addEventListener('pointerdown', (ev) => {
      if (ev.button > 0) return;
      this.closeModals();
      this.render(true);
    });
    for (const host of [this.infoEl, this.pickEl, this.adminEl, this.keysEl, this.settingsEl,
      this.audioEl, this.chatLog]) {
      this.releaseHost(host);
    }
    // The context slot is fixed chrome on a desktop and a scroller in two
    // cases: the scan report, and the context sheet of a telephone, whose
    // raised card scrolls (R1818). The host asks at every press which case
    // stands, so one listener set serves both.
    this.pressOrRelease(this.selection,
      () => isSheet() || this.selection.classList.contains('report'));
    this.installCtxSwipe();
    // The welcome card acts on the press while it fits, and on the release
    // while it scrolls, so a scroll over its fold press opens nothing (R2120).
    this.pressOrRelease(this.welcomeEl, () => this.welcomeScrolls());
    // The VIEW panel is fixed chrome while its tiles fit (R1747). Over the
    // chat band on a short screen it scrolls inside the room it has, and a
    // tile then acts on the release (§16.8).
    this.pressOrRelease(this.viewPanel,
      () => this.viewPanel.scrollHeight > this.viewPanel.clientHeight + 1);
    this.installAlarms();
    // The tab body acts on the RELEASE, like the queue strip (R578). It is
    // the tallest scroller of the chrome (`touch-action: pan-y`), and a
    // press that acted at once bought the building, or sent the fleet, on
    // the first pixel of every scroll through a menu. A press that lands
    // on no button does nothing: the body carries the price fields of the
    // auction, and the recogniser calls no preventDefault, so a field
    // still takes the caret and the keyboard.
    this.releaseHost(this.body);
    // The body of the casino pane scrolls its two columns under the same
    // finger, so its presses act on the release too (R2198).
    this.releaseHost(this.casinoPaneBody);
    // The pinned peek's one button acts on the press (§16.8): the tooltip
    // is fixed chrome and scrolls under no finger (R1294). The press ends
    // here, so the canvas under the tooltip never reads it as a tap.
    // The ⚑ of a seamount name on the ridge peek (R2633) opens the report
    // card on the press too, and the peek goes, so the card stands alone.
    this.tooltip.addEventListener('pointerdown', (ev) => {
      const btn = (ev.target as HTMLElement).closest('button');
      const act = btn?.dataset.act ?? '';
      if (!btn || !PEEK_ACTS.has(act)) return;
      ev.preventDefault();
      ev.stopPropagation();
      switch (act) {
        case 'peekobj': this.onTooltipObject?.(btn.dataset.id ?? ''); return;
        case 'peekpage': if (!btn.disabled) this.onTooltipPage?.(Number(btn.dataset.id ?? 0)); return;
        case 'reportopen': this.onTooltipDismiss?.(); this.actButton(btn); return;
        case 'peekwork': this.onTooltipWork?.(); return;
        default: this.onTooltipAction?.();
      }
    });
    // The ranges (the two volumes and the idle time of the sea view,
    // R2865) are the one control in the overlay a pointerdown cannot read:
    // a drag is a stream of `input` events on a range, and the press that
    // starts it carries no value yet. So the menu gets a second delegated
    // listener of its own.
    this.settingsEl.addEventListener('input', (ev) => {
      const r = ev.target as HTMLInputElement;
      const key = r?.dataset?.key;
      if (!isRangeKey(key)) return;
      // No full re-render: the menu's markup carries no value, so a drag
      // only writes the figures the slider owns. A volume slider counts
      // whole percent, and the bus wants a gain.
      this.setOpt(key, rangeValue(key, r.value), false);
    });
    // The hold'em bet size is the third slider of the chrome, and it is
    // the same case: a `pointerdown` on a range carries no value, and the
    // value arrives as a stream of `input` events. The tab body gets its
    // own delegated listener, and it forwards the one act the casino
    // owns. Every other control of the panel still acts on the RELEASE
    // through `releaseHost` above (casino.md §9, R988).
    // The casino pane holds the same slider (R2198).
    const casinoInput = (ev: Event) => {
      const el = (ev.target as HTMLElement | null)?.closest?.(
        '[data-act="casino"]') as HTMLElement | null;
      if (!el) return;
      // `GameView.act` reads `.value` off the element, so the range goes
      // over as it is. The arm key is built the way every casino control
      // builds it, so a slider can never disarm another control by
      // carrying a key of its own.
      this.casino.act(el as HTMLButtonElement, this.armKey(el as HTMLButtonElement));
    };
    this.body.addEventListener('input', casinoInput);
    this.casinoPaneBody.addEventListener('input', casinoInput);
    // The touch latch: the first finger on the glass arms the touch chrome,
    // whatever the media query said. Capture phase, because the canvas is
    // not a child of this overlay and a touch on the map must arm it too.
    if (!this.touchUI) {
      const latch = (ev: PointerEvent) => {
        if (ev.pointerType !== 'touch') return;
        this.touchUI = true;
        window.removeEventListener('pointerdown', latch, true);
        this.touchLatch = null;
        this.render(true);
      };
      this.touchLatch = latch;
      window.addEventListener('pointerdown', latch, true);
    }

    // --- the top bar's explainer card (§12.0) ---
    //
    // Two ways in, one state. A mouse hovers the cell; a finger taps it and
    // taps again to put it away. The discriminator is `touchUI` — the field
    // the touch latch above already arms on a hybrid laptop — plus the
    // pointer type of the event itself, because a touch fires `pointerover`
    // before the latch has seen its `pointerdown`.
    this.topbar.addEventListener('pointerover', (ev) => {
      if (this.touchUI || ev.pointerType === 'touch') return;
      // `data-tip` and not `data-act`: the Data Core cell explains itself
      // on a hover and asks on a press (R1458). While its card stands, the
      // explainer stays away from under the same cell.
      const btn = (ev.target as HTMLElement).closest('button[data-tip]');
      const tip = (btn as HTMLElement | null)?.dataset.tip ?? '';
      this.setBarTip(tip === 'cores' && this.coreAsk ? '' : tip);
    });
    this.topbar.addEventListener('pointerleave', (ev) => {
      if (this.touchUI || ev.pointerType === 'touch') return;
      // The card stays while the mouse moves into it, because its title is
      // a term button the mouse may press (R1557). The card overlaps the
      // bar's foot padding, so the move crosses no gap.
      if (ev.relatedTarget instanceof Node && this.barTip.contains(ev.relatedTarget)) return;
      this.setBarTip('');
    });
    this.barTip.addEventListener('pointerleave', (ev) => {
      if (this.touchUI || ev.pointerType === 'touch') return;
      if (ev.relatedTarget instanceof Node && this.topbar.contains(ev.relatedTarget)) return;
      this.setBarTip('');
    });
    // A press anywhere else puts the card away. Capture phase on the
    // document: the delegated `pointerdown` that opens the card runs in the
    // bubble phase afterwards, so a press on a trigger closes and reopens
    // nothing — it is filtered here and toggled there.
    this.tipDismiss = (ev: Event) => {
      const t = ev.target as HTMLElement | null;
      // The squad cards (R1571) go away the same way. The chip toggles
      // them, so its press is no press elsewhere either.
      if (!t?.closest?.('#squadcard, #squadkey, button[data-act="squad"]')) this.closeSquad();
      // The VIEW panel stays open until a press on the map, or on any
      // control outside it (R1747). VIEW toggles it, so its press is no
      // press elsewhere.
      if (this.viewOpen && !t?.closest?.('#viewpanel, button[data-act="view"]')) this.closeView();
      // A term button toggles its own card (R1557), so its press is no
      // press elsewhere.
      if (t?.closest?.('#bartip') || t?.closest?.('button[data-act="bartip"]')
        || t?.closest?.('button[data-act="term"]')) return;
      this.setBarTip('');
    };
    document.addEventListener('pointerdown', this.tipDismiss, true);
    // A still finger on an element with a `title` shows the title in the
    // same card (R1555): a finger has no hover. The wiring listens on #ui
    // alone, so the map's own long press never reaches it.
    this.titleHold = installTitleHold(this.root, (text, x, y) => this.showTitleCard(text, x, y));

    // The canvas owns the pinch (§8), and iOS 10 and later ignore
    // `user-scalable=no`, so a pinch that begins on a panel would zoom the
    // whole page. `touch-action` in the stylesheet refuses it for every
    // browser that implements the property; these three refuse Safari's own
    // gesture events, which `touch-action` does not reach.
    for (const name of GESTURES) {
      this.root.addEventListener(name, this.onGesture, { passive: false });
    }

    // A desktop arrives with the column open on the INBOX (the owner's
    // ruling of 2026-10-14, which replaces R2190). A telephone arrives
    // with the sheet at its bar (R1677): `syncFormFactor` collapses it.
    if (!isSheet()) this.tab = 'INBOX';
    // The form factor: the class on #ui, and the sheet's grip.
    this.onForm = () => this.syncFormFactor();
    SHEET_MQ.addEventListener('change', this.onForm);
    window.addEventListener('resize', this.onRoom);
    this.syncFormFactor();

    this.offState = state.on(() => this.render());
  }

  /**
   * The shell's own keys (R539, R543): the bare digits recall a control
   * group, `ctrl-digit` and `shift-digit` assign one, `alt-digit` picks a
   * heat map, and `shift-alt-digit` opens a pill of the open menu. `Tab`
   * walks the six game menus from the map (R2052) and `ctrl-Tab` the
   * pills of the open one.
   * Typing in chat never reaches here (the input stops propagation).
   *
   * The claims live in `data/presentation/keybindings.toml` and the
   * handler names no key of its own (R853): `matchKey` answers with the
   * binding id, and a chord nobody claims — `ctrl-shift-digit`, every
   * meta chord — matches nothing and stays the browser's. A digit claim
   * reads `e.code` and not `e.key`, because `alt-2` is not `2` and
   * `shift-1` is `!` on most layouts, so each digit binding calls
   * `preventDefault()` to stop the character the chord would insert.
   */
  private shellKey(e: KeyboardEvent): void {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const id = matchKey(e, KEYS.map);
    if (id === null) return;
    if (id !== 'admin' && this.chromeVeiled()) return;
    // The casino's table keys (casino.md §9, R988). The group answers
    // ONLY while the CASINO tab stands open AND a table or the slot
    // machine is on the screen: `CasinoPanel.key` takes the press to the
    // screen on the display and answers false where none stands. The
    // seven caps are free on the map surface, so a press the panel did
    // not take falls through with its own default intact — no
    // `preventDefault` and no act.
    if (KEYS.map[id].group === 'casino') {
      const open = this.tab === 'ECONOMY' && this.curSub('ECONOMY') === 'CASINO';
      if (!open || !this.casino.key(id)) return;
      e.preventDefault();
      return;
    }
    switch (id) {
      // The menu keys (R543). A `Tab` in a field kept its meaning two
      // lines up. Tab walks the menus from the map (R2052) and from a
      // control that a pointer press focused (R2082): only while a
      // control of the chrome holds a keyboard focus does the press keep
      // its default, and the browser walks the focus.
      case 'menu_next':
      case 'menu_prev':
        if (!menusTakeTab(e.target)) return;
        e.preventDefault();
        this.stepTab(id === 'menu_next' ? 1 : -1);
        return;
      // Enter on the map is the way in for a player with no pointer
      // (R2052): the focus goes to the tab of the open menu, and Tab
      // then walks its controls. Enter keeps the map rule of R2052 and
      // not the pointer rule of Tab (R2082): Enter on a clicked button
      // presses that button.
      // While the plot line stands, enter is DONE of the line (R3185), and
      // the map handler of `fast/input.ts` takes the press.
      case 'menu_focus':
        if (!onTheMap(e.target) || this.plotting()) return;
        e.preventDefault();
        this.focusMenu();
        return;
      // Esc in the chrome gives the keys back to the map (R2052): the
      // focus leaves the control, so the next Tab walks the menus. The
      // map handler does its own back out on the same press, so the
      // default stays.
      case 'escape':
        if (!onTheMap(e.target) && this.root.contains(e.target as Node)) {
          (e.target as HTMLElement).blur();
        }
        return;
      // The arrow keys, home and end walk the rows of the open menu
      // (R2083), from a keyboard focus in the dock body or on the menu
      // tab alone. On the map and on a pointer focus the press keeps its
      // default, so the body still scrolls under the arrows. Enter and
      // space press the control (R106), and Tab leaves the list.
      case 'row_step':
      case 'row_ends': {
        const t = e.target;
        if (!(t instanceof Element) || (!this.body.contains(t) && !this.tabs.contains(t))) return;
        if (keyboardFocus(t) === false) return;
        const claims: readonly string[] = KEYS.map[id].claims;
        const to = rowTarget(this.body, t, ROW_MOVES[id][claims.indexOf(claimOf(e))]);
        if (!to) return;
        e.preventDefault();
        to.focus();
        return;
      }
      case 'section_next': e.preventDefault(); this.stepSub(1); return;
      case 'section_prev': e.preventDefault(); this.stepSub(-1); return;
      // The two control-group assign chords. Ctrl is the owner's ruling
      // of R539; shift is its second chord (R543), because Chrome and
      // Firefox on Windows and Linux keep `ctrl-1` to `ctrl-8` for their
      // own tabs and the page never sees them. Alt is NOT the equivalent
      // here — `alt-digit` is the heat-map claim below.
      case 'group_assign': {
        const n = digitOf(e);
        if (n >= 1 && n <= GROUP_SLOTS) {
          e.preventDefault();
          this.actions.assignGroup(n);
        }
        return;
      }
      // The open menu's pills, one modifier up since the groups took the
      // bare digits (R539).
      case 'sections': {
        e.preventDefault();
        const n = digitOf(e);
        if (n >= 1) this.openSub(n - 1);
        return;
      }
      // The heat maps (v13): one binding, one claim per mode, in the
      // order `HEAT_MODES` states. The first claim is the first mode
      // (OFF), so the row of number keys reads left to right.
      case 'heat': {
        e.preventDefault();
        const claims: readonly string[] = KEYS.map.heat.claims;
        const mode = HEAT_MODES[claims.indexOf(claimOf(e))];
        // A mode under a flag the world did not latch takes no chord (R2870).
        if (mode && heatModeShown(mode, this.hasFlag)) this.actions.setHeatmap(mode.kind);
        return;
      }
      case 'help':
        this.toggleKeys();
        this.render(true);
        return;
      // The INBOX (R3100): the act of a press of its tab, so a second
      // press folds the sheet.
      case 'inbox':
        e.preventDefault();
        this.openTab('INBOX');
        return;
      // Space centres the map on the hold (R627): the one call the name
      // in the top bar makes. A control that holds the focus keeps the
      // key, because Space activates a focused button (§9, R106), and the
      // default is stopped so the page never scrolls under the map.
      case 'home': {
        if (tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return;
        e.preventDefault();
        this.goHome();
        return;
      }
      // Backtick drops the dev console, Quake-style (R94). Nothing for a
      // seat the server did not make an admin — the panel would not render
      // for it anyway, and a stray press must not look like a broken key.
      case 'admin':
        if (this.state.hello?.admin) this.toggleAdmin();
        return;
      // The media player (plan §5). Nothing happens where the host
      // published no music — the card would be empty and the top-bar
      // button is not drawn either.
      case 'music':
        if (this.hasMusic()) {
          this.toggleAudio();
          this.render(true);
        }
        return;
      // The three display switches of the ☸ OPTIONS card, by key (R456).
      // They go through setOpt, so a key press and the row itself write the
      // same store and both survive a reload. The KEYS panel hides the rows;
      // the card still shows the switches, so nothing here is the only way.
      case 'fluff': this.setOpt('fluff', !this.settings.fluff); return;
      case 'sonar_visuals': this.setOpt('sonar', !this.settings.sonar); return;
      case 'screenshake': this.setOpt('screenshake', !this.settings.screenshake); return;
      // The bare digit recalls a group (R539). The first press selects the
      // fleet and a second press centres the camera on it; an empty slot is
      // silent. The digit of a fleet in the pens asks to deploy it (R1883).
      case 'group_recall': this.recallSlot(digitOf(e)); return;
    }
  }

  /**
   * The keys of the card of a docked fleet (R1989) are read in the
   * capture phase, before the map handlers, so the card key wins over
   * the map claim of the same letter while the card stands with the
   * press on. A press the card does not take goes on to the map with
   * its default intact.
   */
  private cardKey(e: KeyboardEvent): void {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || this.chromeVeiled()) return;
    const id = matchKey(e, KEYS.card);
    if (id === null || !this.pressCardKey(id)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  /**
   * Takes the overlay off the page: its subtree, the two window listeners
   * the constructor wired and the state subscription.
   *
   * The running client never calls this — one overlay lives as long as the
   * document does. A test harness builds and drops one per case, and a
   * listener left on `window` would keep answering for an overlay that is
   * no longer on the page.
   */
  destroy() {
    // The gate holds a timer, and a timer that fires into a dropped
    // overlay redraws a page that is gone.
    this.gate.clear(false);
    this.offMirror();
    this.names.destroy();
    window.removeEventListener('keydown', this.onKey);
    this.unwatchFocus();
    window.removeEventListener('keydown', this.onCardKey, true);
    this.live.destroy();
    this.firstHold.destroy();
    if (this.touchLatch) {
      window.removeEventListener('pointerdown', this.touchLatch, true);
      this.touchLatch = null;
    }
    if (this.tipDismiss) {
      document.removeEventListener('pointerdown', this.tipDismiss, true);
      this.tipDismiss = null;
    }
    if (this.alarmDismiss) {
      document.removeEventListener('pointerdown', this.alarmDismiss, true);
      this.alarmDismiss = null;
    }
    this.titleHold?.off();
    this.titleHold = null;
    for (const name of GESTURES) {
      this.root.removeEventListener(name, this.onGesture);
    }
    SHEET_MQ.removeEventListener('change', this.onForm);
    this.onForm = () => {};
    window.removeEventListener('resize', this.onRoom);
    if (this.dragOff) { this.dragOff(); this.dragOff = null; }
    this.offState();
    this.offState = () => {};
    this.warTl.destroy();
    this.root.remove();
  }

  /** True when the last press on the chrome became a title hold (R1555):
   *  the lift of that press acts on nothing. The season summary card and
   *  the timelapse transport read it. */
  titleHeld(): boolean {
    return this.titleHold?.held() ?? false;
  }

  /**
   * A press on a tab, a cap or a mark that names a tab. While the
   * telephone menu stands over the tab bar, the press closes the menu and
   * opens that tab (R1754): a tab that stands open already stays open,
   * because the player asked for it and not for the map. Otherwise it is
   * `openTab`.
   */
  private pressTab(t: Tab) {
    // A menu tab gives the sheet to that menu, and the chat band goes
    // (R1799): the tab opens its menu and does not close the sheet.
    if (this.bandOpen && isSheet()) {
      this.bandOpen = false;
      this.chatInput.blur();
      this.dock.classList.add('collapsed');
    }
    if (this.settingsOpen && isSheet()) {
      this.settingsOpen = false;
      if (this.tab === t && !this.collapsed) {
        this.render(true);
        return;
      }
    }
    this.openTab(t);
  }

  /** Opens a menu; clicking the active one collapses the sheet to the bar. */
  private openTab(t: Tab) {
    if (this.tab === t && !this.collapsed) {
      // A collapsing sheet is not an expanded one. The two flags used to be
      // independent, and the sheet block states `#dock.collapsed` after
      // `#dock.expanded` at equal specificity, so a sheet that carried both
      // silently lost its expansion: it came back at the resting height
      // when the tab was pressed again.
      this.expanded = false;
      this.dock.classList.add('collapsed');
    } else {
      if (this.tab !== t) this.dropCards();
      this.tab = t;
      this.openSheet();
      // The WAR data is on-demand (plan §4, R83): opening the tab is the
      // event that asks for it — never a timer. STANDINGS asks too, since
      // R95 moved the two capture ledgers there. The inbox follows the
      // same rule: opening the panel is the fetch.
      if (t === 'WAR' || t === 'STANDINGS') this.queryWar();
      // The INBOX opens on UNREAD while anything is unread (inbox.md §6).
      if (t === 'INBOX') {
        recordOnOpen(this.state);
        this.actions.inboxFetch();
        // SINCE YOU LEFT stands folded, and its line asks for the
        // figures it states (inbox.md §7, R1851).
        inboxOpened(this.inboxCtx());
      }
      this.queryProduction();
      this.queryTimelapse();
    }
    // A press in the tab bar states a size of its own, so it gives up the
    // free height a drag asked for (R570).
    this.freeH = null;
    // applySheet, and not render(): it is the one place that keeps the
    // sheet's three states from contradicting each other, and it re-renders
    // itself.
    this.applySheet();
  }

  /**
   * Opens a collapsed sheet at its default stop (R1677): on a telephone a
   * menu opens at the full screen under the top bar, the expanded stop. A
   * sheet that stands open keeps its height. The desktop dock has no
   * expanded state, so the flag stays false there. The caller runs
   * `applySheet`.
   */
  private openSheet() {
    if (!this.collapsed) return;
    this.dock.classList.remove('collapsed');
    this.freeH = null;
    this.expanded = isSheet();
  }

  private queryWar() {
    this.lastWarQuery = performance.now();
    this.actions.warQuery();
  }

  /** WAR · SOVEREIGNTY asks for the season timelapse when it opens
   * (R1620), by the WAR rule. A flip does not ask again: the frames move
   * every six game hours, and the answer is the whole season. */
  private queryTimelapse() {
    if (this.tab !== 'WAR' || this.curSub('WAR') !== 'SOVEREIGNTY') return;
    this.warTl.open();
  }

  /**
   * The scrubber of WAR · SOVEREIGNTY has the focus while a frame stands on
   * the map (R1620, ui-map-interface.md §3.6). On a telephone the sheet
   * then stands at its middle stop, the rest stop, with the series and the
   * transport in view, as card 09 of round 076 draws it: a sheet under the
   * stop rises to it, and the tab body brings the transport over the queue
   * strip, where the advisor line and the sea trials stood over it. When
   * the map is live again, a sheet the timelapse raised goes back, unless
   * the player moved it in between.
   */
  private timelapseFocus(on: boolean) {
    if (!isSheet()) return;
    // A play holds the sheet down: `timelapsePlay` owns it until the play
    // stops (R1779).
    if (on && this.warTl.player.playing) return;
    if (!on) {
      const lift = this.tlLift;
      this.tlLift = null;
      if (lift === null || lift.at !== this.sheetMoves) return;
      this.freeH = lift.freeH;
      this.dock.classList.toggle('collapsed', lift.collapsed);
      this.applySheet();
      return;
    }
    const row = this.warTl.island.querySelector<HTMLElement>('.tlx');
    if (row === null || !this.body.contains(row)) return;
    const collapsed = this.collapsed;
    if (collapsed || (this.freeH !== null && this.freeH < this.sheetStops().rest)) {
      const before = { collapsed, freeH: this.freeH };
      this.freeH = null;
      this.expanded = false;
      this.dock.classList.remove('collapsed');
      this.applySheet();
      this.tlLift = { ...before, at: this.sheetMoves };
    }
    this.body.scrollTop = revealScroll(chromeRect(this.body), chromeRect(row), this.body.scrollTop);
  }

  /**
   * While the season timelapse plays, a telephone sheet stands collapsed,
   * so the map shows whole under the frame band, whose LIVE stops the play
   * (R1779, ui-map-interface.md §3.6). When the play stops, the sheet goes
   * back to the height it stood at, unless the player moved it in between.
   */
  private timelapsePlay(on: boolean) {
    if (!isSheet()) return;
    if (!on) {
      const drop = this.tlPlayDrop;
      this.tlPlayDrop = null;
      if (drop === null || drop.at !== this.sheetMoves) return;
      this.freeH = drop.freeH;
      this.expanded = drop.expanded;
      this.dock.classList.toggle('collapsed', drop.collapsed);
      this.applySheet();
      return;
    }
    if (this.collapsed && this.freeH === null) return;
    const before = {
      collapsed: this.collapsed,
      expanded: this.expanded,
      freeH: this.freeH,
    };
    this.collapseSheet();
    this.tlPlayDrop = { ...before, at: this.sheetMoves };
  }

  /**
   * A return briefing arrived (R1605): the dock opens on the INBOX with
   * SINCE YOU LEFT unfolded on its own range (inbox.md §7, R1851). A
   * desktop dock opens at its own width; a telephone sheet opens at its
   * full height (`expanded`). The open is the fetch of the list, as a
   * press of the INBOX button is, and the ask of the ledger.
   */
  openBriefing() {
    unfoldSinceLeft(this.inboxCtx());
    this.tab = 'INBOX';
    recordOnOpen(this.state);
    this.freeH = null;
    this.expanded = isSheet();
    this.dock.classList.remove('collapsed');
    this.actions.inboxFetch();
    this.applySheet();
  }

  /**
   * WAR · REPORTS is the INBOX filtered to the battle reports of the WAR
   * bucket (inbox.md §6, round 089 card 08): the pill opens the INBOX with
   * that filter, and the WAR menu keeps the pill it stood on. It is a new
   * list, so it drops the cards and the COMPARE mark (R3024).
   */
  private openBattleReports() {
    this.dropCards();
    this.tab = 'INBOX';
    showBattleReports();
    this.openSheet();
    this.freeH = null;
    this.actions.inboxFetch();
    this.applySheet();
  }

  /** The INBOX panel: the page of `ui/inbox/page.ts` (docs/design/inbox.md). */
  private inboxBody(): string {
    return inboxPage(this.inboxCtx());
  }

  /**
   * The INBOX stands open: its tab is the dock's, the sheet is not
   * collapsed and no menu stands over it. The inbox pins of NEEDS YOU draw
   * only then (inbox.md §8, R1846).
   */
  get inboxShown(): boolean {
    return !this.settingsOpen && this.tab === 'INBOX' && !this.collapsed;
  }

  /**
   * A press on an inbox pin (R1846): the INBOX opens if it is shut, and
   * the message opens as its page and is read, the way a press on its
   * NEEDS YOU item opens it (`openRecordMessage`, inbox.md §6).
   */
  openInboxMessage(id: number): void {
    this.ensureTab('INBOX');
    openRecordMessage(id, this.inboxCtx(), this.body);
    this.render(true);
  }

  /**
   * A press on a commission pennant of the map (R2988): FLEET ·
   * COMMISSIONS opens, and the card of the commission opens over its row,
   * the way OPEN of the row opens it. The rows of the list are the steps
   * of the stepper. A commission the list no longer holds opens the list
   * alone.
   */
  openCommissionCard(id: number): void {
    this.showPanel('FLEET', 'COMMISSIONS');
    const btn = this.body.querySelector<HTMLElement>(
      `[data-act="info"][data-kind="${COMMISSION_INFO}"][data-id="${id}"]`);
    if (!btn) return;
    this.openInfo({ kind: COMMISSION_INFO, id }, btn);
    this.render(true);
  }

  /** What the blocks of the INBOX page read and call. */
  private inboxCtx(): InboxCtx {
    return {
      state: this.state,
      actions: this.actions,
      render: (force) => this.render(force),
      openPanel: (tab, sub) => this.openPanel(tab, sub),
      collapse: () => this.collapseSheet(),
    };
  }

  /** Opens one tab of the dock at one of its pills, with the sheet, and
   *  asks for what the pill draws. */
  private openPanel(tab: Tab, sub: string) {
    if (tab === 'WAR' && sub === 'REPORTS') {
      this.openBattleReports();
      return;
    }
    this.tab = tab;
    this.sub[tab] = sub;
    this.openSheet();
    this.queryPill();
    // applySheet, and not render(): the sheet keeps its stop, and the
    // call re-renders.
    this.applySheet();
  }

  /** Asks for what the open pill draws (R83, R1489, R1620): the open is
   *  the event, never a timer. */
  private queryPill() {
    if (this.tab === 'WAR') this.queryWar();
    this.queryProduction();
    this.queryTimelapse();
  }

  /**
   * Opens the menu `t` unless it stands open already, and answers whether
   * it opened it. A press on the open menu would close it (`openTab`), so
   * a way in that names a menu comes here.
   */
  private ensureTab(t: Tab): boolean {
    if (this.tab === t && !this.collapsed) return false;
    this.openTab(t);
    return true;
  }

  /** The build card and the picker belong to the list they were opened
   *  from (R1826, R1893), so a new list puts them away. */
  private dropCards() {
    this.closeInfo(false);
    this.pick = null;
    this.compareFrom = 0;
  }

  /** The pill of `tab` that stands open: the remembered one, or the first
   *  pill where the world does not hold it (its flag is off, R1811). */
  private curSub(tab: Tab): string {
    const subs = this.subs(tab);
    const kept = this.sub[tab];
    return kept !== undefined && subs.includes(kept) ? kept : subs[0];
  }

  /** The name of the open list for the back chip of a page: BUILD · BASE,
   *  or the menu alone where it has no pill (087 card 02). */
  private listName(): string {
    const sub = this.curSub(this.tab);
    return sub ? `${tabLabel(this.tab)} · ${tabLabel(sub)}` : tabLabel(this.tab);
  }

  /** ECONOMY · PRODUCTION asks for the income ring when it opens (R1489),
   * by the WAR rule: the open is the event, never a timer. */
  private queryProduction() {
    if (this.tab !== 'ECONOMY' || this.curSub('ECONOMY') !== 'PRODUCTION') return;
    this.actions.productionQuery();
  }

  /** Opens the active menu's i-th pill (shift-1…n, plan §5.1). Generic over
   * SUBS, so a new pill row is reachable without touching this. Meta tabs
   * have no pills and a collapsed sheet re-opens on its current menu. */
  private openSub(i: number) {
    const subs = this.subs(this.tab);
    if (i < subs.length) this.openPanel(this.tab, subs[i]);
  }

  /**
   * Walks the six game menus in the tab strip's own order (`Tab` forward,
   * `shift-Tab` back, R543). With no menu open — a collapsed sheet, or a
   * meta screen in it — the walk opens the first menu forward and the last
   * one back, so one press always lands somewhere.
   */
  private stepTab(step: number) {
    const n = GAME_TABS.length;
    const i = (GAME_TABS as readonly string[]).indexOf(this.tab);
    const open = i >= 0 && !this.collapsed;
    this.openTab(open ? GAME_TABS[(i + step + n) % n] : GAME_TABS[step > 0 ? 0 : n - 1]);
  }

  /**
   * Puts the keyboard focus into the open menu (`enter` on the map,
   * R2052): on the tab of the open menu, or on the first control of its
   * body where the tab bar takes no focus. With no menu open, the first
   * menu opens, as `tab` would open it.
   */
  private focusMenu() {
    if (!isGameTab(this.tab) || this.collapsed) this.openTab(GAME_TABS[0]);
    const tab = this.tabButtons().find((b) => b.dataset.tab === this.tab);
    tab?.focus();
    if (document.activeElement !== tab) this.body.querySelector<HTMLElement>('button')?.focus();
  }

  /**
   * Walks the pills of the open menu (`ctrl-Tab` forward, `ctrl-shift-Tab`
   * back, R543). It does nothing at all while no menu is open or the open
   * one carries no pills: a walk must never be what opens the sheet.
   */
  private stepSub(step: number) {
    if (this.collapsed) return;
    const subs = this.subs(this.tab);
    if (subs.length === 0) return;
    const i = subs.indexOf(this.curSub(this.tab));
    this.openSub((i + step + subs.length) % subs.length);
  }

  /** A flip landed (main.ts): refresh the WAR data while it is on screen —
   * event-driven per §10, and at most once every 5 s. */
  warEvent() {
    // The first flip of a session asks once for the WAR data and the
    // season frames, so the first open of WAR draws at once from the cache
    // and the open then refreshes it (the data is public and small).
    if (!this.warPrefetched && this.state.war === null) {
      this.warPrefetched = true;
      this.queryWar();
      this.warTl.open();
      return;
    }
    if (this.tab !== 'WAR' && this.tab !== 'STANDINGS') return;
    if (this.collapsed) return;
    if (performance.now() - this.lastWarQuery < 5000) return;
    this.queryWar();
  }

  /**
   * Writes the sheet's state onto the dock.
   *
   * `expanded`, `collapsed` and `free` are three answers to one question —
   * how tall is the sheet — and the sheet block states them at equal
   * specificity, so a dock that carries two takes whichever rule stands
   * later in the file. They can therefore never be set together, and this
   * is the one place that says so.
   *
   * A free height (R570) is the answer a drag that ended away from every
   * stop gives. It travels as `--sheet-h`, the same custom property the
   * live drag uses, because `#dock.free` and `#dock.dragging` are the same
   * `max-height` rule at two moments.
   */
  private applySheet() {
    // Every change of the sheet passes here, so the count tells the
    // timelapse whether the player moved the sheet after it raised it
    // (R1620).
    this.sheetMoves++;
    if (this.freeH !== null) {
      this.expanded = false;
      this.dock.classList.remove('collapsed', 'expanded');
      this.dock.classList.add('free');
      this.dock.style.setProperty('--sheet-h', `${this.freeH}px`);
      this.render(true);
      return;
    }
    this.dock.classList.remove('free');
    this.dock.style.removeProperty('--sheet-h');
    if (this.expanded) this.dock.classList.remove('collapsed');
    this.dock.classList.toggle('expanded', this.expanded);
    this.render(true);
  }

  /**
   * Follows the form factor: the `sheet` class on `#ui`, the words of the
   * six tabs and the two caps of the telephone tab bar (R1749).
   *
   * The class exists so that behaviour switches on exactly the predicate
   * CSS switches on. Nothing else in the overlay may ask a media query of
   * its own.
   */
  private syncFormFactor() {
    const sheet = isSheet();
    const wasSheet = this.root.classList.contains('sheet');
    this.root.classList.toggle('sheet', sheet);
    if (sheet) {
      // The caps stand at the two ends of the tab bar, and nowhere on a
      // desktop: there the dock head holds INBOX and OPTIONS. They are
      // attached and detached, never built and hidden.
      if (this.capInbox.parentNode !== this.tabs) {
        this.tabs.prepend(this.capInbox);
        this.tabs.append(this.capMenu);
        this.lastCaps = '';
      }
      // A telephone sheet starts at its bar (R1677). Its two stops of
      // choice are the full screen and the bar, and a full screen on
      // arrival would hide the whole map. No render here: this runs in
      // the constructor too, and the next render draws the stop.
      if (!wasSheet) {
        this.expanded = false;
        this.freeH = null;
        this.dock.classList.remove('expanded', 'free');
        this.dock.classList.add('collapsed');
      }
    } else {
      this.capInbox.remove();
      this.capMenu.remove();
      // A desktop dock cannot expand at all, so the flag comes off: a
      // window that was widened out of the sheet form factor must not keep
      // an expansion the desktop stylesheet no longer states. A free height
      // belongs to the sheet as much as the expansion does (R570): the
      // desktop column has one size, and the drag that asked for another is
      // not installed there.
      if (this.expanded || this.freeH !== null) {
        this.expanded = false;
        this.freeH = null;
        this.applySheet();
      }
      // A window widened out of the sheet takes the stored state of the
      // desktop queue strip again (R2740).
      if (wasSheet) this.queueOpen = this.settings.queueOpen;
    }
    this.syncSheetHeights();
    // The six tabs take the forms of the new form factor (R2214), after
    // the caps stand, so the fit measures them.
    this.fitTabs(true);
  }

  /**
   * Writes the form of each of the six tabs (R2214, R2215): the glyph
   * and the full word, the glyph and the short word, or the glyph alone.
   *
   * - A desktop dock shows the glyph and the full word, and a tab falls
   *   back to its short word, then to its glyph, while the six do not fit
   *   the bar (`deskPlan`, round 102 card 14). TAB LABELS does not reach
   *   it.
   * - A telephone under GLYPH shows the glyph alone, on the bar and on the
   *   rail, and the caps drop their word (`tg-glyph`).
   * - A telephone under GLYPH + WORD shows the glyph over the short word;
   *   on the bar the tab with the longest word falls back to its glyph
   *   while the bar does not fit (`phonePlan`), and on the rail the
   *   stylesheet shows the word in a cell of `rail_word_px` and up.
   *
   * The measure runs at a form-factor flip, a new width of the bar and a
   * new setting, never per frame: `force` is false from the observer of
   * the bar, which runs again only when the key moved.
   */
  private fitTabs(force = false) {
    if (!this.tabFit) return;
    const sheet = isSheet();
    const rail = sheet && landscapeRail();
    const labels = this.settings.tabLabels;
    const key = `${sheet}|${rail}|${labels}|${this.tabs.clientWidth}`;
    if (!force && key === this.tabFitKey) return;
    this.tabFitKey = key;
    this.tabLabelsDrawn = labels;
    this.tabs.classList.toggle('tg-glyph', sheet && labels === 'glyph');
    this.tabs.classList.toggle('tg-word', sheet && labels === 'word');
    const buttons = this.tabButtons();
    const tabs = buttons.map((b) => b.dataset.tab ?? '').filter(isGameTab);
    if (tabs.length !== buttons.length) return;
    const words: TabWords[] = tabs.map((t) => ({ full: tabLabel(t), short: tabShort(t) }));
    // The widest forms first, so the bar the measure reads holds every
    // word it may need; a bar with no layout (a test, a hidden page) keeps
    // them.
    const wide: TabForm = sheet ? 'S' : 'F';
    let forms: TabForm[] = tabs.map(() => (sheet && labels === 'glyph' ? 'G' : wide));
    this.writeTabForms(tabs, words, forms);
    if (!(sheet && (rail || labels === 'glyph'))) forms = this.measureTabs(sheet, words) ?? forms;
    this.writeTabForms(tabs, words, forms);
  }

  /** Writes one form on each tab: its word and the class `g` of a glyph
   *  alone. A tab whose word and form stand already is not touched. */
  private writeTabForms(tabs: readonly GameTab[], words: readonly TabWords[], forms: readonly TabForm[]) {
    this.tabButtons().forEach((b, i) => {
      const form = forms[i], word = form === 'F' ? words[i].full : words[i].short;
      if (b.dataset.form !== form || b.dataset.word !== word) {
        b.innerHTML = tabInnerHtml(tabs[i], word);
        b.dataset.form = form;
        b.dataset.word = word;
      }
      b.classList.toggle('g', form === 'G');
    });
  }

  /**
   * The plan of the bar from its real measure (R2214): the width of the
   * bar, its padding and its gaps, the two caps on a telephone, the rim
   * and the padding of a tab, and each word in the face and the tracking
   * of the tab, read with canvas `measureText`. The bar of a telephone is
   * 2 px narrower than the chrome (the side rims of the sheet), and the
   * plan reads the bar, so it holds there. Null when the bar has no layout
   * or the page can measure no word.
   */
  private measureTabs(sheet: boolean, words: readonly TabWords[]): TabForm[] | null {
    const width = this.tabs.clientWidth;
    const first = this.tabButtons()[0];
    if (!(width > 0) || !first) return null;
    const px = (v: string): number => parseFloat(v) || 0;
    const bar = getComputedStyle(this.tabs);
    const tab = getComputedStyle(first);
    const measure = wordMeasure(tab);
    if (measure === null) return null;
    const frame = px(tab.paddingLeft) + px(tab.paddingRight) + px(tab.borderLeftWidth) + px(tab.borderRightWidth);
    let room = width - px(bar.paddingLeft) - px(bar.paddingRight) - px(bar.columnGap) * (words.length - 1);
    if (!sheet) return deskPlan(words, { room, frame }, measure);
    for (const cap of [this.capInbox, this.capMenu]) {
      if (cap.parentNode !== this.tabs) continue;
      const cs = getComputedStyle(cap);
      room -= cap.offsetWidth + px(cs.marginLeft) + px(cs.marginRight) + px(bar.columnGap);
    }
    return phonePlan(words.map((w) => w.short), { room, frame }, measure);
  }

  /** The six tab buttons of the tab bar, without the two caps. */
  private tabButtons(): HTMLButtonElement[] {
    return Array.from(this.tabs.querySelectorAll<HTMLButtonElement>('button[data-act="tab"]'));
  }

  /**
   * Follows the setting of the sheet heights (R1748). Under `two`, the
   * default, the telephone sheet has two heights, closed and open, and no
   * grip: a press on a tab opens it and a press on the active tab closes
   * it. Under `three` the grip comes back with the rest stop and the free
   * height of a drag (R570). The class `sheet-three` on `#ui` shows the
   * grip, and the drag is installed only while it stands.
   */
  private syncSheetHeights() {
    const sheet = isSheet();
    const three = sheet && this.settings.sheetHeights === 'three';
    this.root.classList.toggle('sheet-three', three);
    // Two heights hold no free height: a sheet a drag left at one opens
    // to the full height, the open stop.
    if (sheet && !three && this.freeH !== null) {
      this.freeH = null;
      this.expanded = true;
      this.applySheet();
    }
    if (three && !this.dragOff) {
      this.dragOff = installSheetDrag({
        grip: this.grip,
        stops: () => this.sheetStops(),
        // The height a drag begins from is the height the sheet really
        // stands at, and its origin is the stop nearest that height — the
        // sheet may stand at no stop at all (R570).
        at: () => {
          const height = this.dock.offsetHeight;
          return { snap: nearest(height, this.sheetStops()), height };
        },
        live: (px) => this.liveSheet(px),
        settle: (at) => this.setSheet(at),
      });
    } else if (!three && this.dragOff) {
      this.dragOff();
      this.dragOff = null;
    }
  }

  /**
   * How tall the sheet stands at each of its three stops.
   *
   * Measured, never assumed: the collapsed height is the sheet's bar — the
   * grip under three heights, the peek line while it stands (R1555, R1749)
   * and the tab bar — which changes with the pointer and with the safe
   * area, and the expanded height is whatever the readout line leaves.
   */
  private sheetStops(): SheetStops {
    // Chrome px, the space the sheet's heights are measured in (R1556).
    const vh = chromeViewport().h || 0;
    return {
      collapsed: sheetBarH(),
      // The same height the sheet block states for the resting sheet (R1371).
      rest: sheetRestPx(vh),
      expanded: Math.max(0, vh - this.topbar.offsetHeight),
    };
  }

  /**
   * The height a drag is holding the sheet at, or null to give it back to
   * the stylesheet.
   *
   * It travels as `--sheet-h`, which `#dock.dragging` reads as its
   * `max-height`. It must not be a `transform`: the chrome dock-in owns
   * that property for every panel that travels (§3.2). The two settled
   * classes come off for the length of the drag, because each of them
   * states a `max-height` of its own.
   */
  private liveSheet(px: number | null) {
    if (px === null) {
      this.dock.classList.remove('dragging');
      this.dock.style.removeProperty('--sheet-h');
      return;
    }
    this.dock.classList.remove('collapsed', 'expanded', 'free');
    this.dock.classList.add('dragging');
    this.dock.style.setProperty('--sheet-h', `${Math.round(px)}px`);
    this.foldCrampedQueue(px);
  }

  /**
   * The height the open queue strip may take, in CSS pixels (R1202): the
   * share of the height the dock stands at. The sheet's height comes from
   * its stop or its drag, never from its content, because the content is
   * what the strip changes. `dragPx` is the height a drag holds right now.
   */
  private queueRoom(dragPx?: number): number {
    if (!isSheet()) return this.dock.clientHeight * (this.settings.queueLarge ? QUEUE_OPEN_SHARE : QUEUE_SMALL_SHARE);
    const stops = this.sheetStops();
    const h = dragPx ?? this.freeH ?? (this.expanded ? stops.expanded : stops.rest);
    return h * QUEUE_OPEN_SHARE;
  }

  /**
   * The height the open strip needs to show `QUEUE_MIN_OPEN_JOBS` jobs
   * under its line, in CSS pixels (R1221). It measures the rendered rows,
   * so the floor follows the pointer and the font. A strip with no job
   * row has nothing to show and gives 0, so it never folds.
   */
  private queueFloor(): number {
    const line = this.queueStrip.querySelector<HTMLElement>('.qline');
    const job = this.queueStrip.querySelector<HTMLElement>('.job');
    if (!line || !job) return 0;
    // Sibling margins collapse, so each job adds its height and one margin.
    const gap = parseFloat(getComputedStyle(job).marginTop) || 0;
    return line.offsetHeight + QUEUE_MIN_OPEN_JOBS * (job.offsetHeight + gap) + gap;
  }

  /**
   * Folds the open queue strip to its one line when its room cannot show
   * `QUEUE_MIN_OPEN_JOBS` jobs (R1202, R1221). It runs when the room
   * shrinks — a drag, a settle, a window resize — and never on a render, so
   * a strip the player opens by hand in a small room stays open. A strip it
   * folded does not open again when the room grows: only the player opens it.
   */
  private foldCrampedQueue(dragPx?: number) {
    if (!this.queueOpen) return;
    if (this.queueRoom(dragPx) >= this.queueFloor()) return;
    this.queueOpen = false;
    this.render(true);
  }

  /**
   * Settles the sheet where the drag left it: one of the three stops, or
   * the free height the finger let go at (R570).
   */
  private setSheet(at: SheetSettle) {
    if (at.kind === 'free') {
      this.freeH = Math.round(at.height);
    } else {
      this.freeH = null;
      this.expanded = at.snap === 'expanded';
      this.dock.classList.toggle('collapsed', at.snap === 'collapsed');
    }
    this.foldCrampedQueue();
    this.applySheet();
  }

  /**
   * Puts the sheet down on the map (R840).
   *
   * Two orders in the pens end at sea and not in a menu: DEPLOY puts a hull
   * on the map, and GO… arms a pick the player must make on the
   * map. On a phone the sheet covered the water both orders name, so the
   * player had to put it down by hand before they could see anything. The
   * desktop dock is a column beside the map and covers nothing, so this
   * does nothing there.
   *
   * A collapse names a stop, so the free height of the last drag goes with
   * it (R570): the three answers to the one question stay exclusive.
   */
  private collapseSheet() {
    if (!isSheet()) return;
    this.expanded = false;
    this.freeH = null;
    this.dock.classList.add('collapsed');
    this.applySheet();
  }

  /**
   * The link banner (R108): what a player sees when the world stops moving.
   *
   * Until now a dropped socket said nothing a player could act on — the game
   * simply froze, and the only clue was a line in the dev stats corner
   * telling them to run `go run ./cmd/aquaphobiad`, which is an instruction
   * for the person who wrote the server, not the person playing it.
   *
   * `retryAt` is a `performance.now()` stamp or null. Null while the link is
   * down means the client has stopped trying (a protocol refusal), and the
   * banner says so instead of counting down to nothing.
   *
   * Since R430 the banner is the fault-tone drain along the foot of the top
   * bar: the tab carries the same sentence it always did, and the line under
   * it empties over the backoff interval.
   */
  setLink(connected: boolean, retryAt: number | null) {
    this.linkDown = !connected;
    const now = performance.now();
    // The maintenance window outranks the link (§8). A socket that drops
    // inside a window IS the window, and "CONNECTION LOST" would name a
    // fault where there is none. The claim itself is drawn by
    // `renderTruce`, which owns the other element of the same lane, so
    // this line only gives the claim up.
    const claim = this.maintClaim() !== null
      ? null : linkClaim(connected, retryAt, now);
    this.linkClaimed = claim !== null;
    // The readout line states the same claim under its clock (R1749).
    this.linkClaimNow = claim;
    // The drain's total is the backoff interval, and only its end is stated
    // — so the first frame that names a new `retryAt` starts it.
    if (retryAt !== this.linkRetryAt) {
      this.linkRetryAt = retryAt;
      this.linkRetryFrom = now;
    }
    // A null `retryAt` while the link is down means nothing is coming, so
    // there is no interval to drain and the line stands full.
    const span = retryAt === null ? 0 : retryAt - this.linkRetryFrom;
    this.linkLine.set(claim, span > 0 ? ((retryAt as number) - now) / span : 1);
  }

  /**
   * The audio feed (plan §5), pushed by main.ts once the manifest lands and
   * again on its 1 Hz sweep. This is a store and nothing more: the sweep
   * renders straight after, and the media player's clock only has to move
   * once a second.
   */
  setAudio(a: UIAudio) {
    this.audio = a;
  }

  /** True where there is music to play. The card, the 'm' key and the
   * player row of AUDIO all hang off this: a host may publish effects and no music. */
  private hasMusic(): boolean {
    return this.audio.available && this.audio.playlists.length > 0;
  }

  /** Open or close the media player. It is a centred card, so it opens
   * through `openModal` and shuts the other centred cards (R2254). */
  private toggleAudio() {
    if (this.audioOpen) this.audioOpen = false;
    else this.openModal('audio');
  }

  /** What the SEA VIEW row of the VIEW panel says (R2493): no row while
   * the page opens no sea view, FOLLOW while a fleet is selected. */
  private seaRow(): SeaRow {
    if (!this.actions.seaView) return 'off';
    return this.state.selection?.kind === 'fleet' ? 'follow' : 'here';
  }

  /** The DEPTH and the CURRENT sections of the telephone VIEW panel
   * (R2616): a section stands while the hello carries its field and the
   * reader shows its group of chart pieces. The body is written only while
   * the fold is open. */
  private viewDeep(): { title: string; body: string } | null {
    const s = this.state;
    // The CURRENTS and DEPTH heat maps pick the sections (R2870).
    const shown = deepShown(s.heatmap, s.depthBands !== null, s.currents !== null, this.settings.chartHide);
    const depth = shown.depth !== null, current = shown.current;
    if (!depth && !current) return null;
    const hist = this.settings.chart === 'historical';
    const title = [depth ? CH.legend_depth : '', current ? CH.legend_current : ''].filter((t) => t !== '').join(' · ');
    if (!this.viewDeepOpen) return { title, body: '' };
    // The look the game map draws (R2726), or the depth heat look.
    let body = shown.depth === 'heat' ? depthHeatBodyHtml(hist)
      : depth ? depthBodyHtml(hist, depthLook()) : '';
    if (current) {
      body += currentBodyHtml(hist, {
        strongestPxH: strongestCurrent(s.currents, s.rules.currentUnitPxH),
        tide: s.tideNow(), steps: s.rules.tideSteps, clockSec: s.worldClockSec(), tacticalScale: s.tacticalScale,
      });
    }
    return { title, body };
  }

  /** Centre the map on the home cell: the name in the dock head, the rose
   * press, the GO TO THE HOLD row of the VIEW panel and the `home` key. */
  private goHome() {
    const cell = this.state.hello?.homeCell;
    if (cell !== undefined) this.actions.focusCell(cell);
  }

  /**
   * Writes one setting, saves it and asks main.ts to apply it at once.
   *
   * `redraw` is false for a slider: the card's markup carries no volume, so
   * a drag has nothing to rebuild and rebuilding it would take the range
   * out from under the finger holding it.
   */
  private setOpt<K extends keyof GameSettings>(key: K, value: GameSettings[K], redraw = true) {
    this.settings[key] = value;
    saveSettings(this.settings);
    this.actions.audioApply();
    if (redraw) this.render(true);
    else this.syncVolumes();
  }

  /**
   * Puts the stored volumes on the two ranges, their figures and the level
   * bars of the QUICK row.
   *
   * The values are deliberately absent from the markup string: a value in
   * it would change the guard of the pane on every tick of a drag, rebuild
   * the pane under the pointer and end the drag. So they are written here,
   * once after each rebuild and once per slider event.
   */
  private syncVolumes() {
    syncRangeMarks(this.settingsEl, this.settings);
  }

  /**
   * The statistics line (`#stats`). It stands only while the dev stats
   * switch of OPTIONS · HELP is on (R1746), so the sweep writes the line
   * then alone; `renderStats` writes the last lines when the switch turns
   * on.
   */
  setStats(lines: string) {
    this.statsLines = lines;
    if (this.settings.devStats) this.writeStats();
    // An admin reads the line in the DEV row of the telephone menu and in
    // the foot of the desktop DEV panel too, whatever the switch says
    // (R1754, R1774).
    this.syncDevStats();
  }

  /** Writes the statistics line when its lines changed. */
  private writeStats() {
    this.statsHost.write(`<span class="brand">AQUAPHOBIA</span>${this.statsLines}`);
  }

  /** Shows the statistics line while the dev stats switch is on (R1746). */
  private renderStats() {
    const on = this.settings.devStats;
    this.stats.classList.toggle('on', on);
    if (on) this.writeStats();
  }

  /** Tells the toast how close together two identical messages have to be
   *  for the second to restart the first. The event table carries the
   *  number and main.ts passes it on at the hello, which keeps the overlay
   *  ignorant of the event bus the same way it is ignorant of the audio
   *  director. */
  setNoticeDedup(ms: number) {
    this.noticeCol.setDedup(ms);
  }

  /**
   * The chrome half of the look (themes.md §3.4, §12, R814).
   *
   * `#ui` wears one class per field, so a look that is only CSS needs no
   * code here, and the notices column takes its own id, because a look can
   * also change the markup the column writes. The page calls this ONE time,
   * after the overlay is built: the look is resolved once a session, and a
   * theme cannot change during a match (§2). The dock-in and the banner are
   * not the overlay's own modules, so `fast/main.ts` hands each of those
   * its id when it builds it.
   */
  setChromeLook(look: ChromeLook) {
    this.chromeLook = look;
    wearChromeLook(this.root, look);
    this.noticeCol.setStyle(look.notices);
    this.truceLine.setStyle(look.notices);
    this.linkLine.setStyle(look.notices);
    this.placeTimers();
  }

  /**
   * Stands the two timer elements where the look wants them (R824).
   *
   * Under `drain`, `ledger`, `under-clock` and `bare-type` they are
   * children of `#topbar`, where R430 put them: they travel home with the
   * bar and displace no panel. Under `split-lane` and `ribbon` they stand
   * in the lane's own cell, because the value draws the timer and the
   * latest line in one strip. `append` MOVES the same nodes, so no state
   * on them is lost.
   */
  private placeTimers() {
    const host = this.noticeCol.timerCell ?? this.topbar;
    host.append(this.truceEl, this.linkEl);
  }

  /** The chrome look in force. The panels read it as they learn to. */
  get chrome(): ChromeLook { return this.chromeLook; }

  /** Tells the toast how long a line with no duration of its own
   *  stands. The event table carries the number and main.ts passes it
   *  on at the hello, the same way `setNoticeDedup` is fed: the server
   *  owns both clocks and the overlay only holds a fallback. */
  setNoticeMs(ms: number) {
    this.noticeCol.setMs(ms);
  }

  /**
   * Puts one line on screen above the map.
   *
   * The duration is single-sourced here: the stylesheet names the
   * animation and this sets its length, so the fade and the removal cannot
   * drift apart. `key` is what a repeat is judged against.
   *
   * A repeat — the same key and the same words, while the last one is
   * still on screen and still fresh — restarts that toast instead of
   * stacking a second copy under it. Somebody holding a refused button
   * down then sees one line pulsing, not five identical ones.
   *
   * The old two-argument form (`notice(text, true)`) still works; it is
   * a plain toast with the error styling.
   */
  notice(text: string, opts: NoticeOptions | boolean = false) {
    // While the welcome card is open, a toast waits for its close (R2127).
    // A refusal answers a press the player made, so it stands at once.
    const error = typeof opts === 'boolean' ? opts : opts.error === true;
    if (!error && this.firstHold.defer('', RANK.notice, () => this.notice(text, opts))) return;
    this.noticeCol.push(text, opts);
    // A screen reader hears each line once (R2052): a refusal and an
    // urgent event at once, every other line in the polite region.
    this.live.notice(text, typeof opts === 'boolean' ? { error: opts } : opts);
  }

  /** The full-screen stop (R69): the connection is permanently down and
   * the whole game is covered over rather than toasted at. Idempotent.
   *
   * The panel says which stop it is (R158). It used to title every stop
   * "CLIENT / SERVER VERSION MISMATCH", including a refused join ticket,
   * which sent the reader looking for a version problem that was not
   * there — and a reload, the only remedy it offered, could not fix it.
   */
  fatal(text: string, kind: 'protocol' | 'ticket' = 'protocol') {
    // A second stop redraws the panel: the countdown of the first one ends
    // here, so one timer at most runs (R2243).
    window.clearInterval(this.reloadTimer);
    this.reloadTimer = 0;
    let f = document.getElementById('fatal');
    if (!f) {
      f = el('div');
      f.id = 'fatal';
      this.root.appendChild(f);
    }
    const title: string = kind === 'ticket' ? S.fatal_ticket_title : S.fatal_protocol_title;
    const hint: string = kind === 'ticket' ? S.fatal_ticket_hint : S.fatal_protocol_hint;
    f.innerHTML = `<div class="panel">`
      + `<div class="title">${title}</div>`
      + `<div class="line">${esc(text)}</div>`
      + `<div class="line">${hint}</div>`
      + (kind === 'protocol' ? `<div class="line" data-in="reload"></div>` : '')
      + (kind === 'ticket' ? `<button data-act="restart">${S.fatal_start_over}</button>` : '')
      + `<button>${S.fatal_reload}</button></div>`;
    // A version mismatch reloads by ITSELF (the owner's ruling of
    // 2026-09-12). A release that moves the protocol leaves every open page
    // on the old build, and a person who is not looking at the screen must
    // not have to press anything to come back. The RELOAD button stays and
    // acts at once; the line under the hint says how long the page waits.
    //
    // The refused TICKET is deliberately not reloaded: a reload presents
    // the very seat the world refused, so that panel keeps its two presses
    // and the player chooses (R158).
    if (kind === 'protocol') this.reloadCountdown(f);
    // START OVER drops the session and reloads: the landing screen then
    // signs a fresh player in, which takes a free seat instead of the one
    // the registry cannot find.
    //
    // It goes through the same `logout` action the OPTIONS card uses. This
    // used to post the logout by hand and keep the resume token, so the
    // reload presented the very seat the world had just refused and the
    // button did not start anything over.
    f.querySelector('[data-act="restart"]')?.addEventListener('click', () => {
      this.actions.logout();
    });
    // `click`, not pointerdown: this screen is drawn once and never
    // regenerated, so the reason the dock avoids click does not apply — and
    // click is what a keyboard produces (R106). Nobody should be unable to
    // reload because they have no mouse.
    //
    // RELOAD is the one button without an act. A bare `button` selector
    // answered START OVER on a ticket stop, which stands first, so that
    // press reloaded under the logout it had just started (R1325).
    f.querySelector('button:not([data-act])')?.addEventListener('click', () => location.reload());
  }

  /**
   * Counts the version stop down and reloads the page at zero.
   *
   * The figure is `ops.maintenance.reload_after_sec`, on the client lane.
   * The overlay keeps the timer id in `reloadTimer`: a second stop draws
   * the panel again, and `fatal` clears the old timer first, so the page
   * reloads when the line it shows reads zero (R2243).
   */
  private reloadCountdown(f: HTMLElement) {
    const line = f.querySelector<HTMLElement>('[data-in="reload"]');
    if (!line) return;
    let left = MAINTENANCE_RELOAD_AFTER_SEC;
    const draw = () => { line.textContent = S.fatal_protocol_reload_in({ sec: left }); };
    draw();
    const timer = window.setInterval(() => {
      left -= 1;
      if (left <= 0) {
        window.clearInterval(timer);
        if (this.reloadTimer === timer) this.reloadTimer = 0;
        location.reload();
        return;
      }
      draw();
    }, 1000);
    this.reloadTimer = timer;
  }

  /**
   * The peek (R1810; round 084 cards 01 and 02): the head, the vitals and
   * at most two facts of a card, written by the card builder, beside the
   * thing it describes.
   *
   * `pointer` is the pointer in viewport px. `at` is the object in
   * viewport px, or null for a thing with no mark (open water, a ridge, a
   * firing solution), which takes the pointer as its anchor. `finger` is
   * a peek a long press pinned: it stands clear of the finger. `cell` is
   * the centre of the hovered cell in viewport px, or null: a peek with no
   * mark ties its leader to it (R1875). A peek with a mark keeps the one
   * leader to the mark. The pinned
   * peek's one action (MARK, or REMOVE over an own marker, R1294) replaces
   * every action of the spec. `place` is the cell the peek names, or -1:
   * the peek states its chart position on the line under the kind line, in
   * the figures the graticule labels (R2742).
   *
   * Data-driven refreshes re-run this under a still pointer (plan §5.3),
   * so the DOM is written only when the markup changed, and the place only
   * when the anchor or the size moved (§10.3).
   */
  showPeek(pointer: { x: number; y: number }, spec: CardSpec, at: { x: number; y: number } | null,
    finger = false, cell: { x: number; y: number } | null = null, place = -1) {
    this.tooltip.classList.add('on');
    // The work press stands first, in the dark face, because it is the
    // order of the selected fleet; MARK keeps the quiet face (R2649).
    const actions: Action[] = [];
    if (this.tipWork) actions.push({ act: 'peekwork', label: this.tipWork.label, title: this.tipWork.title, face: 'dark' });
    if (this.tipAction !== null) actions.push({ act: 'peekact', label: this.tipAction, face: 'quiet' });
    const pos = place >= 0 ? cellPos(place) : spec.pos;
    const peek: CardSpec = { ...spec, size: 'peek', actions, peekPair: actions.length > 1, pos };
    this.tipHost.write(cardHtml(peek, this.cardEnv()));
    this.peekSide = standPeek(this.tooltip, this.peekLead, {
      pointer, at, cell, finger, clear: this.clearMap(), prev: this.peekSide,
    });
  }

  /**
   * The peek of a map object, from the same spec as its card. `sonar` is
   * the listening reach the hover found for another hold, 0 when no honest
   * range is known (R112).
   */
  showObjectPeek(pointer: { x: number; y: number }, obj: { fleet: number } | { site: Site },
    at: { x: number; y: number } | null, finger = false, sonar = 0) {
    const f = 'fleet' in obj ? this.contact(obj.fleet) : null;
    const spec = 'fleet' in obj
      ? fleetCard(this.cardCtx, f, 'peek')
      : siteCard(this.cardCtx, obj.site, 'peek', sonar);
    // The place of a hull is the cell it stands in now (R2742).
    const place = 'fleet' in obj ? (f ? cellAt(f.x, f.y) : -1) : obj.site.cell;
    this.showPeek(pointer, spec, at, finger, null, place);
  }

  /**
   * The clear map in chrome px: the screen less the readout line, the
   * dock (the column of a desktop, the sheet of a telephone) and the
   * console of a desktop. The peek opens toward its centre (084 card 02).
   */
  private clearMap(): Edges {
    const v = chromeViewport();
    const top = this.topbar.isConnected ? chromeRect(this.topbar).bottom : 0;
    const dock = this.dock.isConnected ? chromeRect(this.dock) : null;
    if (isSheet()) {
      return { l: 0, t: top, r: v.w, b: dock && dock.height > 0 ? Math.min(v.h, dock.top) : v.h };
    }
    const cluster = parseFloat(this.root.style.getPropertyValue('--cluster-h')) || 0;
    return { l: 0, t: top, r: dock && dock.width > 0 ? Math.min(v.w, dock.left) : v.w, b: v.h - cluster };
  }

  hideTooltip() {
    this.tooltip.classList.remove('on');
    this.peekLead.classList.remove('on');
    this.peekSide = null;
  }

  /**
   * Sets the pinned peek's one button, or takes it away with null
   * (R1294). The next `showPeek` draws it.
   */
  setTooltipAction(label: string | null) { this.tipAction = label; }

  /** Sets the pinned peek's work press, or takes it away with null
   *  (R2649). The next `showPeek` draws it. */
  setTooltipWork(w: { label: string; title: string } | null) { this.tipWork = w; }

  /**
   * The cover state (R1755, §10.5). Only a telephone has one: the opaque
   * OPTIONS menu hides the map whole, and the sheet at its open height
   * covers it at `full_sheet_alpha` (R1748). The desktop popup dims the
   * map and the map shows through, so a desktop is `none`.
   */
  coverState(): Cover {
    if (!isSheet()) return 'none';
    if (this.settingsOpen) return 'menu';
    return this.dock.classList.contains('expanded') ? 'sheet' : 'none';
  }

  /** Tells the page the cover state when it changed. Every change of the
   *  menu and of the sheet asks for a render, so a render is the one place
   *  that looks. It runs before the 4 Hz gate: a cap must lift at once. */
  private syncCover() {
    const cover = this.coverState();
    if (cover === this.cover) return;
    this.cover = cover;
    this.onCover?.(cover);
  }

  /** Redraws the throttled text panels. force skips the 4 Hz gate. */
  render(force = false) {
    this.syncCover();
    const now = performance.now();
    if (!force && now - this.lastText < 250) return;
    this.lastText = now;
    if (!this.state.hello) return;
    // A seamount or a cache the commander's hull found asks for a name or
    // shows its note, and an open picker follows its fleets (R2626, R2649,
    // R2722).
    this.names.sync();

    // The base round pick falls back to the first stocked round here,
    // before the panels, so no markup builder writes a field (R1349).
    const stocked = this.state.stockedHoldRounds();
    if (!stocked.some((t) => t.id === this.baseTorp)) {
      this.baseTorp = stocked[0]?.id ?? 0;
    }
    // The welcome card first: while it stands, the bar and the queue strip
    // carry its two numbered tags (R1572).
    this.renderWelcome();
    this.renderStats();
    // The signal rank of this pass (R1753): the time cell, the siege row
    // and the alarm list read the one list.
    this.signalsNow = this.collectSignals();
    this.renderTopbar();
    // The dock head before the squad cards: the roster card stands under
    // the chip the head draws (R1749).
    this.renderDockHead();
    // After the bar: the card reads the controls the bar has just drawn,
    // and it is a no-op while nothing is open.
    this.renderBarTip();
    // The top-bar core card (R1458), for the same reason; it drops itself
    // when the last core is gone.
    this.renderCoreCard();
    // The squad chip's roster card and the marks key (R1571), after the
    // bar for the same reason.
    this.renderSquadCards();
    // The commit confirm card (R788). It is a no-op while nothing is
    // asked, and it drops an ask whose fleet or object has gone.
    this.renderCommitCard();
    this.renderTruce();
    this.keysEl.classList.toggle('on', this.keysOpen);
    // The chart's own symbols ride at the foot of the KEYS panel (R548,
    // card 40). They are drawn by the same glyph leaves the map pieces draw
    // with, on one small canvas a row, when the panel opens and when the
    // reader changes reading — never while the panel is shut.
    const wantLegend = this.keysOpen ? `${this.settings.chart}:${isSheet()}` : '';
    if (wantLegend !== this.keysChartAt) {
      this.keysChartAt = wantLegend;
      this.keysEl.querySelector('.chart-legend')?.remove();
      const group = this.keysOpen ? legendGroup(this.settings.chart, isSheet(), this.chartPieces,
        (f) => this.state.hasFeatureFlag(f)) : null;
      if (group) this.keysEl.insertBefore(group, this.keysEl.querySelector('.foot'));
    }
    // While the telephone menu stands over the tab bar, no tab is lit: the
    // lit MENU cap says which panel stands (R1754).
    const menuOver = this.settingsOpen && isSheet();
    // The sheet draws no box under the menu (R1769).
    this.dock.classList.toggle('menu-over', menuOver);
    // A desktop lights the tab of the menu that stands open, and none while
    // the column rests at its bar (R2190); the telephone tab bar keeps its
    // lit tab at the bar.
    const restDesk = !isSheet() && this.collapsed;
    for (const b of this.tabButtons()) {
      b.classList.toggle('active', !menuOver && !restDesk && b.dataset.tab === this.tab);
    }
    this.renderCaps();
    // A new value of TAB LABELS, from this menu, the hub or the account,
    // draws the bar again (R2215).
    if (this.tabLabelsDrawn !== this.settings.tabLabels) this.fitTabs(true);
    // Only touch the DOM when the markup actually changed — a static panel
    // must not be rebuilt under the user's pointer.
    // The chat panel repaints on its own terms. It used to live inside the
    // dock's "markup changed" guard below, which meant a channel switch did
    // nothing at all until something unrelated in the empire panel happened
    // to change — chat looked slow, or dead. renderChat has its own
    // change guard (lastChat), so this stays as cheap as §10 requires.
    this.renderChat();
    this.renderPeek();
    this.renderAlarms();
    this.renderQueueStrip();
    // The phone places the welcome card over the advisor line once the
    // peek line that holds the line is drawn (R1572).
    if (this.welcomeShown && !this.welcomePlaced) this.placeWelcome();
    const html = this.tabBody();
    if (html !== this.lastBody) {
      this.lastBody = html;
      // The transport of the season timelapse stays in the document while
      // the markup around it changes, so a drag of its scrubber survives
      // the 4 Hz regenerate (R1620). The body keeps its scroll offset, and
      // writes it back only when the swap moved it (R1761).
      // The focus of a keyboard player survives the rewrite (R2052).
      keepScroll(this.body, () => keepFocus(this.body,
        () => swapAroundIsland(this.body, html, this.warTl.island, ISLAND_MARK)));
    }
    // The page of the build card opens at its top, and the back chip
    // returns to the list at the row it left (087 card 02).
    if (this.bodyScrollTo !== null) {
      this.body.scrollTop = this.bodyScrollTo;
      this.bodyScrollTo = null;
    }
    // The CASINO panel draws into the host the constant shell above left
    // in the body (casino.md). It runs only while that tab is the open
    // one: any other tab holds no host, and the panel keeps its own
    // markup guard, so a table frame writes its half and nothing else.
    // `reset` forgets the guard when the tab closes, because the next
    // body swap throws the host away.
    //
    // Under the desktop casino setting UNDOCKED the host is the one of the
    // casino pane over the map, and the tab body holds the away line
    // (R2198). Closing the casino closes the pane.
    const casinoOpen = this.tab === 'ECONOMY' && this.curSub('ECONOMY') === 'CASINO';
    const pane = casinoOpen && this.casinoFormOf() === 'pane';
    this.casinoPane.hidden = !pane;
    const casinoHost = pane ? this.casinoPaneHost
      : casinoOpen ? this.body.querySelector('.casino-host') as HTMLElement | null : null;
    if (casinoHost) this.casino.sync(casinoHost, pane); else this.casino.reset();
    this.renderSelection();
    this.renderGroups();
    this.renderInfo();
    this.renderPick();
    this.renderAdmin();
    this.renderSettings();
    this.renderAudio();
    // The scrim stands while a modal does (round 083 card 16). OPTIONS draws
    // its own scrim in the kit, and the DEV panel is a tuning panel the
    // admin keeps open beside the map, so neither takes this one.
    this.scrimEl.classList.toggle('on', this.keysOpen || this.audioEl.classList.contains('on')
      || this.reports.shown);
    this.renderMapButtons();
    this.renderHint();
  }

  /**
   * The armed order (R2164): the hull it is armed for (0 for the hold's
   * battery), the order word of the chip, and the line that says what the
   * next press on the map does, in touch words for a finger. Null while
   * nothing is armed. The aim reads first, then the first armed pick mode.
   */
  private armedNow(): { id: number; order: string; line: string; aim: boolean } | null {
    const t = this.touchUI;
    if (this.aiming !== null) return { id: this.aiming, ...armWords(this.aiming === 0 ? 'aimbase' : 'aim', t) };
    const pick = PICK_HINTS.find(([mode]) => this[mode] !== 0);
    if (!pick) return null;
    return { id: this[pick[0]], ...armWords(pick[0], t, this.state.survey.on) };
  }

  /**
   * The hint host (R2164): while an order is armed and no card in the
   * context slot names the armed hull (a deploy from the pens, a pick armed
   * from the fleet list), it stands at the context slot as a card strip of
   * its own: the line in the text ink and CANCEL, which esc does too. On a
   * telephone the slot is the place of the closed context sheet, over the
   * sheet's bar. At rest it is empty, and the stylesheet hides it.
   */
  private renderHint() {
    const a = this.armedNow();
    const html = a && !this.armOnCard
      ? `<div class="ck ck-sz-card">${armHtml({
        line: a.line,
        cancel: { act: 'cancelmodes', face: 'quiet', label: CS.arm_cancel, key: KEYS.map.escape.label.toUpperCase() },
      }, this.cardEnv())}</div>`
      : '';
    this.hintEl.classList.toggle('armed', html !== '');
    this.hintHost.write(html);
    // Another card in the slot (a contact while a pick of the fleet list
    // waits): the host stands one gap over it. The read runs only while
    // the host stands beside a card, which is rare.
    const lift = html !== '' && this.selection.classList.contains('on')
      ? Math.round(this.selection.offsetHeight + GAP) : 0;
    if (lift !== this.hintLift) {
      this.hintLift = lift;
      if (lift > 0) this.hintEl.style.setProperty('--hint-lift', `${lift}px`);
      else this.hintEl.style.removeProperty('--hint-lift');
    }
  }

  /** Show or hide the dev panel, remembering which across reloads (R94).
   * Every way in — the top bar's ⚙, the panel's ✕ and the backtick — lands
   * here, so the memory can't be set by one and missed by another. */
  private toggleAdmin() {
    // A telephone holds no card: the DEV page of the OPTIONS menu stands in
    // its place, and it opens and closes like every other page (R1766).
    if (isSheet()) {
      const here = this.settingsOpen && this.optsPage === 'dev';
      if (here) this.settingsOpen = false;
      else { this.openModal('options'); this.openOptsPage('dev'); }
      this.render(true);
      return;
    }
    this.adminOpen = !this.adminOpen;
    // Private mode, a full store: the panel just forgets.
    writeStore(ADMIN_OPEN_KEY, this.adminOpen ? '1' : '0');
    this.render(true);
  }

  /**
   * The dev tuning panel (R50): the knobs a playtest needs, talking to
   * the server's admin ops. Only rendered when the hello says this seat
   * may use them.
   */
  private renderAdmin() {
    // Server truth, not a guess (R94): every hello says where the two
    // toggles stand for this seat, so a reload shows the autopilot still
    // flying and the fog back on rather than the state of the last click.
    if (this.state.hello !== this.adminSeeded) {
      this.adminSeeded = this.state.hello;
      this.revealOn = !!this.state.hello?.adminReveal;
      this.autopilotOn = !!this.state.hello?.adminAutopilot;
      this.adminCachesOn = !!this.state.hello?.adminCaches;
      this.veilOff = !!this.state.hello?.adminVeil;
    }
    // The card is the desktop's: a telephone draws the same rows as the
    // DEV page of the OPTIONS menu (R1766).
    const show = this.adminOpen && !!this.state.hello?.admin && !isSheet();
    this.adminEl.classList.toggle('on', show);
    if (!show) {
      // The rows are written only after the head, so an empty head is an
      // empty panel: the 4 Hz pass of a closed panel writes nothing.
      if (this.lastAdmin.head !== '') {
        this.adminEl.replaceChildren();
        this.lastAdmin.head = '';
        this.lastAdmin.body = '';
      }
      return;
    }
    // The frame of the OPTIONS popup (R1774): the head and the rows each
    // stand in a slot of their own, so a press keeps the scroll of the rows.
    if (this.lastAdmin.head === '') this.adminEl.innerHTML = devDeskShell();
    const head = devDeskHead(this.state.hello?.worldName ?? '');
    if (head !== this.lastAdmin.head) {
      this.lastAdmin.head = head;
      const el = this.adminEl.querySelector('[data-slot="head"]');
      if (el) el.innerHTML = head;
    }
    const body = devPageHtml(this.devPanelView(), false);
    if (body !== this.lastAdmin.body) {
      this.lastAdmin.body = body;
      const el = this.adminEl.querySelector('[data-slot="body"]');
      if (el) el.innerHTML = body;
    }
    this.syncDevStats();
  }

  /**
   * What the rows of the admin panel show (`ui/options/devpanel.ts`), on the
   * desktop card and on the telephone page (R1766). Two clocks, two rows
   * (R91): each clock is its own multiplier against wall time. RESEED,
   * RESTART and ROLL each throw the world away, so each one asks for a
   * second press (R696); the time controls and the seaquake stages are
   * reversible and keep their one press.
   */
  private devPanelView(): DevPanelView {
    // The label holds the rest face and the armed face that names the act
    // in one cell (R2069).
    const arm = (op: DevArmOp, label: string, armLabel: string) => {
      const armed = this.gate.is(`adm:${op}`);
      return { label: armFaces(label, armLabel, armed), armed };
    };
    return {
      timescale: this.state.timescale,
      tactical: this.state.tacticalScale,
      reveal: this.revealOn,
      veilOff: this.veilOff,
      caches: this.adminCachesOn,
      autopilot: this.autopilotOn,
      arm: {
        roll: arm('roll', S.admin_roll, S.admin_roll_armed),
        reseed: arm('reseed', S.admin_reseed, S.admin_reseed_armed),
        restart: arm('restart', S.admin_restart, S.admin_restart_armed),
      },
    };
  }

  /**
   * The ☸ OPTIONS menu (R124, R256, R1731; ui-map-interface.md §13). One
   * structure in two shells: on a desktop a centred popup of one fixed
   * size, with a rail of the seven sections and the pane of one; on a
   * telephone the whole screen, one page at a time. `ui/options/gamemenu.ts`
   * turns the state into markup. This method keeps each part in an element
   * of its own and writes a part only when its markup changed.
   *
   * The settings blob is kept against the ACCOUNT since R256 and cached in
   * this browser, so a choice made here reaches every device the player
   * signs in on. The two volumes are the one value the markup does not
   * carry; see `syncVolumes` for why.
   *
   * LOG OUT stands on the ACCOUNT page, one level under the commander row
   * of the session block (R1731), and it takes two presses: the first
   * arms it and swaps the label for what the second one does.
   */
  private renderSettings() {
    this.settingsEl.classList.toggle('on', this.settingsOpen);
    if (!this.settingsOpen) {
      // A closed menu forgets its page and its pending pick, so the next
      // open starts at the root. An armed LOG OUT must not wait for the
      // next open either. Only the LOG OUT key: this runs on every render,
      // and a bare clear would disarm a control of another panel at 4 Hz.
      if (this.optsShell !== '') {
        this.settingsEl.replaceChildren();
        this.optsShell = '';
        // A roster card opened from the session block goes with the menu
        // (R1757); a press that opened the menu closed any other one. A
        // telephone closes the menu to show the card, so it stays (R2448).
        if (!isSheet()) this.closeSquad();
      }
      this.optsPage = 'root';
      this.optsLang = null;
      if (this.gate.is('logout')) this.gate.clear(false);
      return;
    }
    const parts = menuParts(this.menuView());
    if (parts.shell !== this.optsShell) this.buildMenuShell(parts.shell);
    let wrote = false;
    for (const name of ['head', 'rail', 'foot', 'session'] as const) {
      if (this.optsLast[name] === parts[name]) continue;
      this.optsLast[name] = parts[name];
      const el = this.optsEls[name];
      if (el) el.innerHTML = parts[name];
      wrote = true;
    }
    const body = this.optsEls.body;
    if (body && this.optsLast.body !== parts.body) {
      // A new page starts at its top; the same page keeps its scroll.
      const top = this.optsLast.page === this.optsPage ? body.scrollTop : 0;
      this.optsLast.body = parts.body;
      body.innerHTML = parts.body;
      body.scrollTop = top;
      this.optionsHost?.wire(body);
      wrote = true;
    }
    this.optsLast.page = this.optsPage;
    // The ranges and the figures exist again: give them their values back.
    if (wrote) this.syncVolumes();
    this.syncDevStats();
  }

  /**
   * Builds the frame of one shell: the dimmed map and the popup with its
   * rail and pane on a desktop; on a telephone the screen from the top
   * edge down to the tab bar, which stays in view and in reach under it
   * (R1754). Each part stands in an element `renderSettings` writes on its
   * own. The telephone shell is not a modal dialog: the tab bar under it
   * holds the controls that close the menu and go back.
   */
  private buildMenuShell(shell: 'desk' | 'phone' | 'side') {
    const label = `aria-label="${esc(OPT.title)}"`;
    // Beside the landscape rail the menu is a rail and a pane right of the
    // rail, which stays in view and closes it (round 090 card 11 B).
    const head = '<div class="o-slot" data-slot="head"></div>';
    // The rail and the pane of the two wide shells.
    const split = '<div class="o-split"><nav class="o-rail" data-slot="rail"></nav>'
      + '<div class="o-pane"><div class="o-scroll" data-slot="body"></div>'
      + '<div class="o-pfoot" data-slot="foot"></div></div></div>';
    if (shell === 'side') {
      this.settingsEl.innerHTML = `<div class="o-side" role="region" ${label}>${head}${split}</div>`;
    } else this.settingsEl.innerHTML = shell === 'desk'
      // The dimmed map is a button: a press on it closes the menu.
      ? `<button type="button" class="o-scrim" data-act="optsclose" tabindex="-1"`
        + ` aria-label="${esc(OPT.close_title)}"></button>`
        + `<div class="o-pop" role="dialog" aria-modal="true" ${label}>${head}${split}`
        // The session foot spans the popup under the rail and the pane (R1757).
        + '<div class="o-slot" data-slot="session"></div></div>'
      : `<div class="o-full" role="region" ${label}>${head}`
        + '<div class="o-body" data-slot="body"></div></div>';
    this.settingsEl.classList.toggle(SHELL_DESK, shell !== 'phone');
    this.settingsEl.classList.toggle(SHELL_PHONE, shell === 'phone');
    this.settingsEl.classList.toggle(SHELL_SIDE, shell === 'side');
    this.optsEls = {};
    for (const name of ['head', 'rail', 'body', 'foot', 'session'] as const) {
      const el = this.settingsEl.querySelector<HTMLElement>(`[data-slot="${name}"]`);
      if (el) this.optsEls[name] = el;
    }
    this.optsLast = { head: '', rail: '', body: '', foot: '', session: '', page: '' };
    this.optsShell = shell;
  }

  /** What the menu shows at this render (`ui/options/gamemenu.ts`). */
  private menuView(): MenuView {
    const s = this.state;
    const host = this.optionsHost;
    const me = host?.me() ?? null;
    const world = s.hello?.worldName ?? '';
    const page = this.optsPage;
    const music = this.hasMusic();
    const phone = isSheet();
    return {
      phone,
      side: phone && landscapeRail(),
      page,
      // The telephone menu stands over the tab bar (R1754), so its root
      // packs to the screen less the bar.
      height: chromeViewport().h - (phone ? this.tabs.offsetHeight : 0),
      s: this.settings,
      // The head names the world and the day; the commander row names
      // neither (R1731).
      world: world ? OPT.head_line({ world, day: Math.floor(s.seasonElapsedSec() / 86400) + 1 }) : '',
      who: {
        name: me?.account.name ?? s.hello?.playerName ?? '',
        guest: me ? me.account.kind === 'guest' : undefined,
        line: '',
      },
      meta: host !== null,
      password: me?.identities?.includes('email') ?? false,
      sound: this.audio.available,
      advisor: this.advisorOn(),
      standing: host ? host.standing() : null,
      blocked: host?.blockedCount(),
      pendingLang: this.optsLang,
      logout: { armed: this.gate.is('logout'), label: OPT.logout },
      photo: this.onPhoto !== null,
      report: this.onReportProblem !== null,
      // A card is drawn only on its own page: its markup is not cheap.
      alerts: page === 'alerts' ? host?.alertsHtml() ?? null : null,
      chat: page === 'social' ? host?.chatHtml() ?? null : null,
      player: music
        ? { title: this.audio.status?.playlistTitle ?? S.audio_title, body: page === 'player' ? audioBodyHtml(this.audio.status) : '' }
        : null,
      marks: this.commanderMarks(),
      accountBadge: page === 'account' && s.faction > 0
        ? badgePicture(s.faction, this.squadLook?.().era ?? 'm', OPTIONS_MENU.accountBadgePx) : '',
      dev: phone ? this.devView() : null,
    };
  }

  /**
   * What the commander row of the session block shows beside the name
   * (R1757, card 01 of round 082), on both shells: the badge of the
   * reader's faction as the faction key draws it, in the reading the holds
   * wear (R1568); the squad with its mark and its cup mark; the rank; and
   * the standing orders. The squad cell draws no pulse of a squad change:
   * the pulse would rewrite the root at 4 Hz under a finger, and the dock
   * head of a desktop draws it.
   */
  private commanderMarks(): CommanderMarks {
    const s = this.state;
    const clan = s.clan;
    const sq = squadView(clan);
    const era = this.squadLook?.().era ?? 'm';
    return {
      badge: s.faction > 0 ? badgePicture(s.faction, era, OPTIONS_MENU.commanderBadgePx) : '',
      orders: !!s.empire?.standingOrders,
      squad: sq ? {
        icon: this.squadIconHtml(), tag: sq.pickup ? '' : clan?.tag ?? '', n: sq.squad,
        count: sq.members.length, pickup: sq.pickup, cup: sq.pickup ? '' : cupMarkFor(clan?.tag, 13),
      } : null,
      title: s.hello?.title ?? '',
      rank: s.board?.you?.rank ?? 0,
    };
  }

  /** The DEV row of the telephone root, on an admin seat alone (R1754). */
  private devView(): DevView | null {
    if (!this.state.hello?.admin) return null;
    return { ...this.devLine(), panel: this.optsPage === 'dev' ? this.devPanelView() : undefined };
  }

  /** The build of the dev lane and how far it is behind its branch (R89):
   *  the DEV row of the telephone root and the DEV cell of the dock head. */
  private devLine(): { behind: number; build: string; title: string } {
    const d = this.state.dev;
    return {
      behind: d?.behind ?? 0,
      build: d?.build ?? '',
      title: d ? S.dev_title({
        build: d.build, branch: d.branch ? S.dev_branch({ branch: d.branch }) : '', head: d.head,
      }) : '',
    };
  }

  /** Writes the statistics line into the DEV row of the telephone root
   *  (R1754) and into the foot of the desktop DEV panel (R1774). The line
   *  is no part of the markup, so the sweep of every second never rebuilds
   *  the root or the rows. */
  private syncDevStats() {
    for (const host of [this.settingsOpen ? this.settingsEl : null, this.lastAdmin.head ? this.adminEl : null]) {
      const el = host?.querySelector<HTMLElement>('[data-devstats]');
      if (el && el.textContent !== this.statsLines) el.textContent = this.statsLines;
    }
  }

  /** Opens or closes KEYBOARD. It opens through `openModal`, so OPTIONS
   *  (R2184) and the media player (R2254) close. */
  private toggleKeys() {
    if (this.keysOpen) this.keysOpen = false;
    else this.openModal('keys');
  }

  /** `GameState.hasFeatureFlag`, as one closure for the VIEW heat page,
   *  the KEYS panel and the chord (R2870). */
  private readonly hasFlag = (f: FeatureFlag): boolean => this.state.hasFeatureFlag(f);

  /**
   * Opens one centred card and closes the other two: ☸ OPTIONS, KEYBOARD
   * and the media player (plan §5). Two centred cards never stack, on
   * every way in (R2184, R2219, R2254).
   */
  private openModal(which: 'options' | 'keys' | 'audio') {
    if (which === 'keys') this.keysEl.innerHTML = keysPanelHtml(this.hasFlag);
    this.settingsOpen = which === 'options';
    this.keysOpen = which === 'keys';
    this.audioOpen = which === 'audio';
  }

  /** One press on a page of the menu: the root, a section, ACCOUNT or the
   *  music player. A page the menu does not hold is no press. */
  private openOptsPage(id: string) {
    const page: MenuPage | null = id === 'root' || id === 'player' || id === 'dev' ? id : isPageId(id) ? id : null;
    if (page === null || (page === 'player' && !this.hasMusic())) return;
    if (page === 'dev' && !this.state.hello?.admin) return;
    this.optsPage = page;
    this.optsLang = null;
    if (page !== 'root' && page !== 'player' && page !== 'dev') this.optionsHost?.open(page);
  }

  /** Writes the settings a shared row of the menu answered (R1731): the
   *  store and the account, then the sound at once. */
  private writeSettings(next: GameSettings) {
    Object.assign(this.settings, next);
    saveSettings(this.settings);
    this.actions.audioApply();
  }

  /**
   * The gate key of one button: the `data-act` and the id the row carries.
   * The admin card names its operation in `data-op`, so the key holds that
   * instead, because one act stands for every button of the card.
   */
  private armKey(btn: HTMLButtonElement): string {
    const act = btn.dataset.act ?? '';
    if (act === 'adm') return `adm:${btn.dataset.op ?? ''}`;
    const id = btn.dataset.id;
    return id === undefined ? act : `${act}:${id}`;
  }

  /** The media player (plan §5), written only when its markup changed. */
  private renderAudio() {
    const show = this.audioOpen && this.hasMusic();
    this.audioEl.classList.toggle('on', show);
    if (!show) { this.audioHost.forget(); return; }
    this.audioHost.write(audioCardHtml(this.audio.status));
  }

  /**
   * The thin heat ramp of a telephone (round 086 card 11 b, R1806): 10 px
   * along the right edge of the clear map, with the mode word over it and
   * the two end words beside its ends. It stands while a mode paints, on
   * the sheet form factor alone; a desktop reads the HEAT section of the
   * legend card. It takes no press.
   */
  private renderHeatRamp() {
    const show = this.heatLive() && isSheet();
    this.heatRamp.classList.toggle('on', show);
    if (!show) { this.heatRampHost.forget(); return; }
    const m = heatMode(this.state.heatmap);
    this.heatRampHost.write(`<span class="hr-mode">${esc(m.label)}</span>`
      + `<span class="hr-bar" style="background:${rampCss(m).replace('90deg', '0deg')}"></span>`
      + `<span class="hr-hi">${esc(m.hi)}</span><span class="hr-lo">${esc(m.lo)}</span>`);
  }

  // --- the build card (R1826) ---

  /**
   * The build card of one catalogue item (084 card 13, 087 card 02). A
   * desktop stands it in the context slot, where it outranks the card of
   * the selection; a telephone draws it as a page of the menu sheet
   * (`tabBody`), so the slot holds no claim there. The row of the open
   * card stays lit while the card stands.
   */
  private renderInfo() {
    const phone = isSheet();
    const spec = this.infoOn ? this.infoSpecNow() : null;
    // The item left the catalog or the roster: the card goes with it.
    if (this.infoOn && !spec) this.infoOn = null;
    const desk = spec !== null && !phone;
    this.slot.claim('info', desk);
    this.markRows(desk);
    if (!desk) {
      if (this.infoEl.classList.contains('on')) {
        this.infoEl.classList.remove('on');
        this.infoHost.clear();
      }
      return;
    }
    const html = cardHtml({ ...spec, size: 'card', close: true, closeAct: 'infoclose' }, this.cardEnv());
    this.infoEl.classList.add('on');
    this.infoHost.write(html);
  }

  /** The spec of the open card, or null. The captain card asks for the
   *  owner's backstory and redraws when it lands (R1246). */
  private infoSpecNow(): CardSpec | null {
    const t = this.infoOn;
    const pair = this.comparePair;
    if (t?.kind === 'ship' && pair && pair[1] === t.id) return compareSpec(this.state, pair[0], pair[1]);
    // The level picker (R1839): a building card asks once for the levels
    // past the next, and again when the Empire frame changes the row.
    if (t?.kind === 'building') {
      const bl = this.state.empire?.buildings.find((x) => x.building === t.id);
      if (this.state.levelPreviews.shouldAsk(bl)) this.actions.previewQuery(t.id);
    }
    return t ? infoSpec(this.state, t, {
      onBackstory: () => this.render(true), fleets: this.fleets(), sea: this.fleets(), storyOpen: this.storyOpen,
      trainPick: this.trainPick,
      compareFrom: this.compareFrom,
      levelPreview: (bl) => this.state.levelPreviews.get(bl), levelPick: this.levelPick,
      look: this.sceneLook, armed: this.armed,
    }) : null;
  }

  /** The page of a telephone (087 card 02): the back bar with the back
   *  chip and the stepper, then the card at the full width of the sheet. */
  private infoPageHtml(): string {
    const t = this.infoOn;
    const spec = t ? this.infoSpecNow() : null;
    if (!t || !spec) return '';
    const pos = this.infoList.findIndex((x) => x.kind === t.kind && x.id === t.id);
    return backBarHtml(this.infoFrom, Math.max(0, pos), pos < 0 ? 1 : this.infoList.length)
      + cardHtml({ ...spec, size: 'page', close: false }, this.cardEnv());
  }

  /**
   * Opens the card of `t` from a press on the name of a row. The items of
   * the list the row stands in become the steps of the stepper, and the
   * list keeps its scroll offset for the back chip.
   */
  private openInfo(t: InfoTarget, btn: HTMLElement) {
    if (!this.infoOn && this.body.contains(btn)) this.infoScroll = this.body.scrollTop;
    if (this.body.contains(btn)) {
      const seen = new Set<string>();
      this.infoList = [];
      for (const b of Array.from(this.body.querySelectorAll<HTMLElement>('button[data-act="info"]'))) {
        const k = b.dataset.kind as InfoKind;
        const key = `${k}:${b.dataset.id}`;
        if (k !== t.kind || seen.has(key)) continue;
        seen.add(key);
        this.infoList.push({ kind: k, id: Number(b.dataset.id ?? 0) });
      }
      this.infoFrom = this.listName();
    } else {
      this.infoList = [t];
      this.infoFrom = tabLabel(this.tab);
    }
    // A second hull while COMPARE waits opens the two in one card (R1829).
    this.comparePair = t.kind === 'ship' && this.compareFrom !== 0 && this.compareFrom !== t.id
      ? [this.compareFrom, t.id] : null;
    if (this.comparePair) this.compareFrom = 0;
    this.infoOn = t;
    this.foldCard();
    if (isSheet()) this.bodyScrollTo = 0;
  }

  /** A card that opens or closes stands folded: the backstory, the TRAIN
   *  pick and the level picker (R1246, R2087, R1839). */
  private foldCard() {
    this.storyOpen = false;
    this.trainPick = false;
    this.levelPick = 0;
  }

  /** Closes the card. `back` returns a telephone to the list at the row
   *  it left (the back chip, the ✕, Escape). */
  private closeInfo(back = true) {
    if (this.infoOn && back && isSheet() && this.infoScroll >= 0) this.bodyScrollTo = this.infoScroll;
    this.infoOn = null;
    this.infoScroll = -1;
    this.foldCard();
    this.comparePair = null;
  }

  /** Escape closes the build card first (review §2.7): `fast/input.ts`
   *  reads and writes this as one of its panels. */
  get infoOpen(): boolean { return this.infoOn !== null; }

  set infoOpen(on: boolean) { if (!on) this.closeInfo(); }

  // --- the pickers (R1893) ---

  /**
   * Opens a picker: the captain picker of a hull in the pens or the start
   * sheet of a dive. A desktop stands it in the context slot, where it
   * outranks the build card; a telephone draws it as a page of the menu
   * sheet (`tabBody`). The list keeps its scroll offset for the back chip.
   */
  private openPick(p: NonNullable<Overlay['pick']>, btn: HTMLElement) {
    if (!this.pick && this.body.contains(btn)) this.pickScroll = this.body.scrollTop;
    this.pickFrom = this.listName();
    this.closeInfo(false);
    this.pick = p;
    if (isSheet()) this.bodyScrollTo = 0;
  }

  /** A second press on the press that opened the picker puts it away. */
  private togglePick(p: NonNullable<Overlay['pick']>, btn: HTMLElement) {
    const cur = this.pick;
    const same = cur !== null && cur.kind === p.kind
      && (cur.kind === 'dive' ? cur.depth === (p as typeof cur).depth : cur.fleetId === (p as typeof cur).fleetId);
    if (same) this.closePick();
    else this.openPick(p, btn);
  }

  /** Closes the picker; a telephone returns to the list at the row it left. */
  private closePick() {
    if (this.pick && isSheet() && this.pickScroll >= 0) this.bodyScrollTo = this.pickScroll;
    this.pick = null;
    this.pickScroll = -1;
  }

  /** The spec of the open picker, or null when its hull or depth is gone. */
  private pickSpecNow(): CardSpec | null {
    const p = this.pick;
    if (!p) return null;
    if (p.kind === 'load') return loadSheetSpec(this.state, p.fleetId, p.mix, this.sceneLook);
    if (p.kind === 'stow') return stowSheetSpec(this.state, p.fleetId, p.mix, this.sceneLook);
    const ctx = this.capRowCtx();
    return p.kind === 'cap'
      ? pickerSpec(ctx, p.fleetId, p.sel, this.sceneLook)
      : startSheetSpec(ctx, p.depth, p.sel);
  }

  /** Draws the picker in the context slot of a desktop, or hides it. */
  private renderPick() {
    const spec = this.pick ? this.pickSpecNow() : null;
    // The hull left the pens or the depth closed: the card goes with it.
    if (this.pick && !spec) this.pick = null;
    const desk = spec !== null && !isSheet();
    this.slot.claim('pick', desk);
    if (!desk) {
      if (!this.pickEl.hidden) {
        this.pickEl.hidden = true;
        this.pickHost.clear();
      }
      return;
    }
    const html = cardHtml(spec, this.cardEnv());
    this.pickEl.hidden = false;
    this.pickHost.write(html);
  }

  /** The page of a telephone: the back chip, then the picker at the full
   *  width of the sheet. */
  private pickPageHtml(): string {
    const spec = this.pickSpecNow();
    if (!spec) return '';
    return backBarHtml(this.pickFrom, 0, 1, 'pickback')
      + cardHtml({ ...spec, size: 'page', close: false }, this.cardEnv());
  }

  /** Escape closes the picker before the build card: `fast/input.ts`
   *  reads and writes this as one of its panels. */
  get pickOpen(): boolean { return this.pick !== null; }

  set pickOpen(on: boolean) { if (!on) this.closePick(); }

  /**
   * The ENTER key of an open picker: it presses the dark button of the
   * card, the confirm of the current selection (ASSIGN, START, SAVE). It
   * returns true when it pressed one. A disabled button, such as ABOARD for
   * the captain already aboard, takes no press.
   */
  confirmPick(): boolean {
    if (!this.pick) return false;
    const host: HTMLElement = !isSheet() ? this.pickEl : this.body;
    const btn = host.querySelector<HTMLButtonElement>('button.ck-b.dk:not([disabled])');
    if (!btn?.dataset.act) return false;
    this.actButton(btn);
    return true;
  }

  /** Lights the row of the open card on a desktop (084 card 13), and
   *  marks the name of the hull that COMPARE holds (084 card 17). */
  private markRows(desk: boolean) {
    const t = this.infoOn;
    const lit = desk && t ? `${t.kind}:${t.id}` : '';
    const key = `${lit}|${this.compareFrom}`;
    const litEl = this.body.querySelector('.lit');
    const cmpEl = this.body.querySelector('button.info.cmp');
    if (key === this.litKey && (lit === '') === (litEl === null)
      && (this.compareFrom === 0) === (cmpEl === null)) return;
    this.litKey = key;
    litEl?.classList.remove('lit');
    cmpEl?.classList.remove('cmp');
    if (t && lit) {
      this.body.querySelector(`button[data-act="info"][data-kind="${t.kind}"][data-id="${t.id}"]`)
        ?.closest('.row, .cr-row')?.classList.add('lit');
    }
    if (this.compareFrom !== 0) {
      const name = this.body.querySelector<HTMLElement>(
        `button[data-act="info"][data-kind="ship"][data-id="${this.compareFrom}"]`);
      name?.classList.add('cmp');
      if (name) name.dataset.tag = CS.compare_tag;
    }
  }

  // --- chat (R1799: a state of the dock) ---

  /** The chat element: the QUICK press of its input opens the quick wheel
   *  (R1799), so `fast/main.ts` hangs that gesture on it. */
  get chatElement(): HTMLElement { return this.chat; }

  /** The form the chat takes now: the band of a telephone, or the box or
   *  the pane of the desktop chat setting (R1838). */
  private chatFormOf(): ChatForm {
    return chatForm(isSheet(), this.settings.chatDesk);
  }

  /** Is the chat open: the chat band of a telephone, the open chat box of
   *  SMALL and LARGE, or the DOCKED pane over its line stop (R1799,
   *  R1838)? */
  private chatOpen(): boolean {
    switch (this.chatFormOf()) {
      case 'band': return this.bandOpen;
      case 'box': return this.settings.chat;
      case 'pane': return this.settings.chatStop !== 'line';
    }
  }

  /** The settings key that keeps whether the chat of this form factor is
   *  on (R2369): `chatOnPhone` for the band, `chatOn` for the box and the
   *  pane of a desktop. */
  private chatOnKey(): 'chatOn' | 'chatOnPhone' {
    return this.chatFormOf() === 'band' ? 'chatOnPhone' : 'chatOn';
  }

  /** Is the chat of this form factor on? The CHAT tile of VIEW is lit
   *  while it is, so the open chat and its chat line both light the tile,
   *  and a press turns both off (R2369). */
  private chatTileOn(): boolean {
    return this.settings[this.chatOnKey()];
  }

  /** The state of the chat box of SMALL and LARGE (R2057), or null in the
   *  band and the pane: open, its peek line, or off (R2369). */
  private boxStateNow(): BoxState | null {
    return this.chatFormOf() === 'box' ? boxState(this.settings.chat, this.settings.chatOn) : null;
  }

  /**
   * Opens the chat (R1799, R1838): the band on a telephone, which puts the
   * sheet down to its bar; the chat box of SMALL and LARGE; the DOCKED
   * pane at its height when it stood at its line, else as it stands.
   */
  private openChat() {
    // Every way into the chat turns it on again (R2369).
    const key = this.chatOnKey();
    if (!this.settings[key]) this.writeSettings({ ...this.settings, [key]: true });
    switch (this.chatFormOf()) {
      case 'band':
        this.bandOpen = true;
        this.collapseSheet();
        this.render(true);
        return;
      case 'box':
        if (!this.settings.chat) this.setOpt('chat', true);
        else this.render(true);
        return;
      case 'pane':
        if (this.settings.chatStop === 'line') this.setOpt('chatStop', 'pane');
        else this.render(true);
    }
  }

  /** Closes the chat: the band goes, the chat box rests as its peek line
   *  (R2057), and the DOCKED pane goes back to its line. The input loses
   *  the focus, so the keyboard goes with it. */
  private closeChat() {
    this.chatInput.blur();
    switch (this.chatFormOf()) {
      case 'band':
        this.bandOpen = false;
        this.render(true);
        return;
      case 'box':
        if (this.settings.chat) this.setOpt('chat', false);
        return;
      case 'pane':
        if (this.settings.chatStop !== 'line') this.setOpt('chatStop', 'line');
    }
  }

  /** One press of the chat head that changes the desktop chat setting
   *  (R1838): the size press, DOCK or UNDOCK. */
  private writeChatDesk(next: ChatDeskKeys) {
    if (next.chatDesk === 'docked' && next.chatStop === 'line') this.chatInput.blur();
    this.writeSettings({ ...this.settings, ...next });
    this.render(true);
  }

  /** SEND, or Enter in the field: one line on the open channel. The field
   *  keeps the focus, so a player types the next line at once. */
  private sendChat() {
    const text = this.chatInput.value.trim();
    this.chatInput.value = '';
    if (text) this.actions.say(this.state.channel, text);
  }

  /**
   * The presses of the chat that the dispatcher does not take (R1799):
   * SEND acts on the press and keeps the focus in the field, so the
   * keyboard of a telephone stays up; the grip of the desktop pane sets
   * its height (R1870); the field watches the keyboard.
   */
  private installChatDock() {
    this.chatIn.addEventListener('pointerdown', (ev) => {
      if ((ev.button ?? 0) > 0) return;
      if (!(ev.target as HTMLElement).closest('button[data-act="chatsend"]')) return;
      ev.preventDefault();
      this.sendChat();
    });
    this.chatIn.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      if (!(ev.target as HTMLElement).closest('button[data-act="chatsend"]')) return;
      ev.preventDefault();
      this.sendChat();
    });
    const grip = this.chatHead.querySelector<HTMLElement>('.cgrip');
    grip?.addEventListener('pointerdown', (ev) => {
      if ((ev.button ?? 0) > 0 || this.chatFormOf() !== 'pane') return;
      ev.preventDefault();
      ev.stopPropagation();
      const h0 = this.chat.offsetHeight;
      this.paneDrag = { id: ev.pointerId, y0: ev.clientY, h0, column: h0 + this.body.offsetHeight };
      this.dock.classList.add('chatdrag');
      window.addEventListener('pointermove', this.onPaneMove, true);
      window.addEventListener('pointerup', this.onPaneEnd, true);
      window.addEventListener('pointercancel', this.onPaneEnd, true);
    });
    // The keyboard of a telephone (card 05 b of round 087): while the field
    // holds the focus, the band sits on the keyboard with the lines of
    // `chat_band_keyboard_lines`. The visual viewport says how far the
    // keyboard reaches; a resize of it is the one event, never a timer.
    const vv = window.visualViewport ?? null;
    const fit = () => this.fitKeyboard();
    this.chatInput.addEventListener('focus', () => {
      vv?.addEventListener('resize', fit);
      vv?.addEventListener('scroll', fit);
      fit();
    });
    this.chatInput.addEventListener('blur', () => {
      vv?.removeEventListener('resize', fit);
      vv?.removeEventListener('scroll', fit);
      this.fitKeyboard();
    });
  }

  /** Puts the band on the keyboard, or back on the foot of the screen. */
  private fitKeyboard() {
    const typing = isSheet() && this.bandOpen && document.activeElement === this.chatInput;
    this.dock.classList.toggle('kb', typing);
    writeKbInset(this.dock, typing);
    this.chat.style.setProperty('--chat-lines', String(bandLines(typing)));
  }

  /** The height the finger asks of the desktop pane. */
  private paneDragH(ev: PointerEvent): number {
    const d = this.paneDrag!;
    return Math.max(0, Math.min(d.column, d.h0 + toChromePx(d.y0 - ev.clientY)));
  }

  /** The finger on the grip of the desktop pane: the pane follows it. */
  private readonly onPaneMove = (ev: PointerEvent) => {
    if (!this.paneDrag || ev.pointerId !== this.paneDrag.id) return;
    this.chat.style.height = `${Math.round(this.paneDragH(ev))}px`;
  };

  /** The release: the pane keeps the height the finger left, or takes
   *  the line or the whole column from a catch zone (R1870). */
  private readonly onPaneEnd = (ev: PointerEvent) => {
    const d = this.paneDrag;
    if (!d || ev.pointerId !== d.id) return;
    const h = this.paneDragH(ev);
    this.paneDrag = null;
    window.removeEventListener('pointermove', this.onPaneMove, true);
    window.removeEventListener('pointerup', this.onPaneEnd, true);
    window.removeEventListener('pointercancel', this.onPaneEnd, true);
    this.chat.style.removeProperty('height');
    this.dock.classList.remove('chatdrag');
    const { stop, px } = releasePane(h, d.column);
    if (stop === 'line') this.chatInput.blur();
    const chatPanePx = stop === 'pane' ? px : this.settings.chatPanePx;
    if (stop !== this.settings.chatStop || chatPanePx !== this.settings.chatPanePx) {
      this.writeSettings({ ...this.settings, chatStop: stop, chatPanePx });
    }
    this.render(true);
  };

  /**
   * Stands the chat in the place of its form (R1838): the chat box in the
   * root of the chrome, where the console places it, and the band and the
   * pane in the dock over the chat line. The head writes the size glyph
   * and the close of the form.
   */
  private placeChat(form: ChatForm) {
    const box = form === 'box';
    const big = box && this.settings.chatLarge;
    const key = `${form}|${big}`;
    if (key === this.chatFormNow) return;
    const was = this.chatFormNow;
    this.chatFormNow = key;
    if (box && this.chat.parentNode !== this.root) this.root.insertBefore(this.chat, this.mapBtns);
    if (!box && this.chat.parentNode !== this.dock) this.dock.insertBefore(this.chat, this.chatLineEl);
    this.chat.classList.toggle('box', box);
    this.chat.classList.toggle('big', big);
    const size = this.chatHead.querySelector<HTMLElement>('.csize');
    if (size) {
      size.innerHTML = arrowGlyph(big ? 'down' : 'up');
      size.title = big ? S.chat_small_title : S.chat_big_title;
      size.setAttribute('aria-label', size.title);
      size.setAttribute('aria-pressed', String(big));
    }
    const close = this.chatHead.querySelector<HTMLElement>('.cclose');
    if (close && (was === null || was.startsWith('box') !== box)) {
      const title = box ? S.chat_min_title : S.chat_close_title;
      close.title = title;
      close.setAttribute('aria-label', title);
      close.innerHTML = box ? minGlyph() : chatIcon('down', 16);
    }
  }

  /**
   * The chat (R1799, R1838). The classes on the dock say which state of
   * the dock stands: `chatband` (the band of a telephone), `chatpane` and
   * `chatcol` (the two open stops of the DOCKED pane). The chat box of
   * SMALL and LARGE wears `box`, `big` and `off` itself. The channel tabs
   * carry one light count each (R1800).
   */
  private renderChat() {
    const s = this.state;
    const sheet = isSheet();
    // A menu that raised the sheet took it from the band.
    if (sheet && this.bandOpen && !this.collapsed) this.bandOpen = false;
    const form = this.chatFormOf();
    this.placeChat(form);
    const open = this.chatOpen();
    // A shut chat leaves the lines of the open channel unread, and the
    // count stands on VIEW, on the CHAT tile and on the chat line (R1747,
    // R1799); an open chat reads them.
    s.setChatBoxOpen(open);
    const stop = this.settings.chatStop;
    this.dock.classList.toggle('chatband', form === 'band' && open);
    this.dock.classList.toggle('chatpane', form === 'pane' && stop === 'pane');
    this.dock.classList.toggle('chatcol', form === 'pane' && stop === 'column');
    // The height of the pane (R1870); the stylesheet holds it inside the
    // column, so a shorter window shows it lower and keeps the setting.
    const paneH = `${this.settings.chatPanePx}px`;
    if (this.dock.style.getPropertyValue('--chat-pane-h') !== paneH) this.dock.style.setProperty('--chat-pane-h', paneH);
    // A closed box takes the shape of its peek line (R2057), and a chat
    // the CHAT tile turned off fades that shape out (R2369).
    const box = this.boxStateNow();
    this.chat.classList.toggle('peek', box === 'peek' || box === 'off');
    this.chat.classList.toggle('off', box === 'off');
    if (!(sheet && open) && this.dock.classList.contains('kb')) this.fitKeyboard();
    if (!this.chat.style.getPropertyValue('--chat-lines')) {
      this.chat.style.setProperty('--chat-lines', String(bandLines(false)));
    }
    // The CLAN tab belongs to a clan. A commander in none has no room to
    // read, so the tab is hidden — and a hidden tab must hold nothing: no
    // unread count, and not the selection. The strip itself is built once
    // in the constructor and is never rebuilt here (R237), so the tab keeps
    // its node and comes back the moment the clan does.
    const inClan = s.clan !== null;
    if (!inClan) {
      s.unread.delete(ChatChannel.CHAT_CHANNEL_CLAN);
      if (s.channel === ChatChannel.CHAT_CHANNEL_CLAN) s.readChannel(DEFAULT_CHANNEL);
    }
    // One light count per tab (R1800, card 19 of round 085): the open tab
    // shows its lines and no count. The tabs are written only when one of
    // them changed (§10.3).
    const tabs = CHANNELS.map(([label, ch]) => {
      const hide = ch === ChatChannel.CHAT_CHANNEL_CLAN && !inClan;
      const unread = hide ? 0 : s.unread.get(ch) ?? 0;
      return { label, ch, hide, unread, on: !hide && ch === s.channel };
    });
    const tabKey = tabs.map((t) => `${t.hide ? 'h' : ''}${t.on ? 'o' : ''}${t.unread}`).join('|');
    if (tabKey !== this.lastChatTabs) {
      this.lastChatTabs = tabKey;
      for (const b of Array.from(this.chatTabs.querySelectorAll<HTMLButtonElement>('button[data-act="channel"]'))) {
        const t = tabs.find((x) => x.ch === Number(b.dataset.id));
        if (!t) continue;
        b.classList.toggle('hidden', t.hide);
        b.classList.toggle('on', t.on);
        b.setAttribute('aria-selected', String(t.on));
        b.classList.toggle('unread', t.unread > 0 && !t.on);
        b.innerHTML = channelTabHtml(t.label, t.unread, t.on);
      }
    }
    const log: ChatLogCtx = {
      desk: this.reports, selfId: this.state.selfAccountId, stamps: this.settings.chatTimeOn, posLook: this.posLook,
      filter: chatFilterOn(this.settings),
    };
    const html = s.channel === ChatChannel.CHAT_CHANNEL_CLAN ? clanChatHtml(s, log) : worldChatHtml(s, log);
    if (html === this.lastChat) return;
    this.lastChat = html;
    const atBottom = this.chatLog.scrollTop + this.chatLog.clientHeight >= this.chatLog.scrollHeight - 24;
    this.chatLog.innerHTML = html || `<div class="line muted">${S.chat_empty}</div>`;
    if (atBottom) this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }

  /** Does a siege warning stand now (R1753)? A new chat line then only adds
   *  to the count (R1799). */
  private siegeStands(): boolean {
    return this.signalsNow.some((x) => x.kind === 'siege');
  }

  /** What the chat line shows now (R1799). */
  private chatLineNow(): ChatLineView {
    const s = this.state;
    return chatLineView({
      fresh: chatFresh(s, this.reports, this.state.selfAccountId, chatFilterOn(this.settings)), nowMs: performance.now(), siege: this.siegeStands(),
      unread: s.unread, inClan: s.clan !== null, channel: s.channel, countOn: this.settings.chatCountOn,
    });
  }

  /** The markup of the chat line (R1799), or '' for null. The press opens
   *  the chat on the channel the line names. */
  private chatLineButton(v: ChatLineView | null): string {
    return v === null ? ''
      : `<button class="cl${v.kind === 'new' ? '' : ' quiet'}" data-act="chatline"`
        + ` data-ch="${lineChannel(v, this.state.channel)}" title="${esc(S.chatline_title)}">`
        + `${chatLineHtml(v, cssColor)}</button>`;
  }

  /** Writes the chat line of the dock (R1799), or empties it for null. */
  private writeChatLine(v: ChatLineView | null) {
    this.chatLineHost.write(this.chatLineButton(v));
  }

  /** Writes the peek of the closed chat box (R2057), or empties it. */
  private writeChatPeek(v: ChatLineView | null) {
    this.chatPeekHost.write(this.chatLineButton(v));
  }

  // --- report and block (deployment.md §7.6, R1323) ---

  /** `commanderMark` of the report desk, bound once, for the menus of
   *  `overlay/menus.ts`. */
  private readonly cmdrMark: CommanderMark = (accountId, name) => this.reports.commanderMark(accountId, name);

  // --- the console and the map buttons (R218, R1747) ---

  /** Is a heatmap mode actually painting the map? */
  private heatLive(): boolean {
    return this.state.heatmap !== HeatmapKind.HEATMAP_KIND_NONE;
  }

  /**
   * Restores the legend card and opens its HEAT section, because the player
   * has just picked a mode (R218, R1806).
   *
   * Minimizing the legend and turning a mode off stay two different
   * questions — but asking for a *mode* is asking to read it, and the
   * legend is the only thing that says what the tint means. So a pick puts
   * the legend back, and the player can minimize it again against the new
   * mode if the key is not what they wanted.
   *
   * `fast/actions.ts` calls this from `setHeatmap`, which is the one door
   * every mode change goes through: the heat page, the Alt+digit chords
   * and the HEAT tile all arrive there.
   */
  showHeatLegend() {
    if (!this.settings.legend) this.setOpt('legend', true);
    factionKey.refresh();
  }

  /** The column of the map buttons (R1747). The long press of its VIEW
   *  button opens the quick wheel (R1610), so `fast/main.ts` hangs that
   *  gesture on it. */
  get mapButtonsElement(): HTMLElement { return this.mapBtns; }

  /** VIEW's own act: open or close the VIEW panel (R1747). A touch press
   *  reaches it at its release, after the long press could not (R1610). */
  toggleView(): void {
    this.viewOpen = !this.viewOpen;
    this.renderMapButtons();
  }

  /** Puts the VIEW panel away. */
  private closeView(): void {
    if (!this.viewOpen) return;
    this.viewOpen = false;
    this.renderMapButtons();
  }

  /**
   * The unread chat lines, by the rule the channel tabs count with
   * (`renderChat`): the CLAN channel counts only for a commander in a clan.
   */
  private chatUnread(): number {
    const inClan = this.state.clan !== null;
    let lines = 0;
    for (const [ch, n] of this.state.unread) {
      if (n <= 0 || (ch === ChatChannel.CHAT_CHANNEL_CLAN && !inClan)) continue;
      lines += n;
    }
    return lines;
  }

  /**
   * The unread chat lines a light count shows: on VIEW, on the CHAT tile
   * and in the alarm feed. The chat count switch of SOCIAL takes the figure
   * away and keeps the lines unread (R1759); the channel tabs read their
   * own counts.
   */
  private chatCount(): number {
    return this.settings.chatCountOn ? this.chatUnread() : 0;
  }

  /**
   * The unread INBOX items a light count shows: on the INBOX cap, on the
   * INBOX cell of the dock head and in the alarm list. The INBOX count
   * switch of SOCIAL takes the figure away and keeps the items unread
   * (R1759). The dot of a hidden tab reads the same figure, so the switch
   * takes it away too (inbox.md §6, audit U1).
   */
  inboxCount(): number {
    return this.settings.inboxCountOn ? this.state.inboxUnread : 0;
  }

  /**
   * Which console units are open. The console contract asks this and holds
   * no state of its own; the tiles of the VIEW panel are the toggles.
   */
  private consoleState(): ConsoleState {
    const box = this.boxStateNow();
    return {
      // The chat box of SMALL and LARGE is a unit of the console (R1838);
      // the band and the DOCKED pane stand in the dock.
      // The closed box at its chat line is a unit too (R2057); a
      // minimized box is not.
      chat: box === 'open' || box === 'peek',
      // The minimap folds while the plot line stands (088 open point "The
      // minimap in plot mode"), so a tap on the map always adds a leg.
      map: this.settings.minimap && !this.plotting(),
      // The compass rose holds a slot whenever the furniture draws (R555),
      // and the round minimap stands in it (R1801), so the slot also
      // stands for an open chart under the chart setting `off`.
      //
      // The exception of R845 (an open chat box took the rose's slot on a
      // telephone) went with the chat box (R1799): the chat band raises the
      // floor of the console, and the round unit stands over the band.
      rose: this.settings.chart !== 'off' || this.settings.minimap,
    };
  }

  /**
   * The console units, the map buttons and the VIEW panel (R1747, card 08
   * of mockup round 082). The VIEW button carries the light count of the
   * unread chat lines while the chat is shut (R1799), and it is lit while its
   * panel stands. The panel holds one tile per unit in a fixed order —
   * CHAT, MINIMAP, HEAT, then FACTION KEY while the theme draws the key —
   * and a tile is lit while its unit is open.
   *
   * It keeps a change guard of its own rather than riding the empire dock's,
   * for the reason `renderChat` does: a tile press has to appear at once,
   * and the dock's markup may not have changed at all.
   */
  private renderMapButtons() {
    const open = this.consoleState();
    // Animation A is a CSS transition on the unit itself, so the minimized
    // state is a class on the unit (styles.css, "the console").
    const chatOpen = this.chatOpen();
    this.mapChrome.classList.toggle('min', !(open.map && open.rose));
    // The rose press stands while the rose draws (R1758): the console holds
    // the rose's slot, and the chart setting is not `off` (R1801).
    const press = open.rose && this.settings.chart !== 'off';
    this.rosePress.classList.toggle('on', press);
    if (!press) noteRoseHover(false);
    // The LEGEND tile of a desktop stands the legend window, the legend
    // card and the cartouche as one unit (R2832); the stack places the
    // column again on a change. The minimize control of the legend card
    // (R1805) writes the card at once, without waiting for a rebuild
    // reason of the chart.
    setLegendWindow(this.settings.factionKey);
    setLegendOpen(this.settings.legend);
    const legend = String(this.settings.legend);
    if (legend !== this.lastLegend) {
      this.lastLegend = legend;
      factionKey.refresh();
    }
    this.renderHeatRamp();
    // The count stands on VIEW while the chat is shut: an open chat shows
    // its lines, and its tabs carry their own counts. The chat count
    // switch of SOCIAL takes it away (R1759).
    const shown = this.chatCount();
    const count = chatOpen ? 0 : shown;
    // The HEAT tile and the heat pip name the mode that paints. No gold
    // (round 080): the name is the signal, and a minimized legend leaves
    // the mode on.
    const heat = this.state.heatmap;
    // The HEAT chip stands on the round unit while a mode paints the disc
    // (R1849): the chart is open and its unit stands.
    const chip = open.map && open.rose ? heatChipHtml(heat) : '';
    if (this.heatChipHost.write(chip)) this.heatChip.classList.toggle('on', chip !== '');
    // The heat page stands only inside the panel: a shut panel opens on
    // its main page again.
    if (!this.viewOpen) this.heatOpen = false;
    // The LEGEND tile of this form factor (R1805, R2832): the legend
    // window on a desktop, the key and the deep sections of this panel on
    // a telephone. Its tile stands after HEAT; a telephone drops it while
    // it would turn nothing on, with no key in the World theme and no
    // deep field.
    const sheet = isSheet();
    const keyOn = sheet ? this.settings.factionKeyPhone : this.settings.factionKey;
    const deep = this.viewOpen && sheet ? this.viewDeep() : null;
    const legendTile = !sheet || this.factionKeyThemed || deep !== null;
    const panel = this.viewOpen
      ? viewPanelHtml({
        chat: this.chatTileOn(), unread: shown,
        map: open.map, heat, has: this.hasFlag,
        key: legendTile ? keyOn : null, factions: this.factionKeyThemed,
        // A folded section paints no rows (R1859).
        keyRows: sheet && this.factionKeyThemed && keyOn && this.viewKeyOpen
          ? keyRowsHtml(this.state.faction, this.settings.chart === 'historical' ? 'h' : 'm',
            this.state.hasFeatureFlag('pirates'), (f) => this.state.factionOn(f))
          : '',
        keyOpen: this.viewKeyOpen,
        deep, deepOpen: this.viewDeepOpen,
        heatPage: this.heatOpen, sheet,
        chart: this.settings.chart, hide: this.settings.chartHide,
        groups: shownGroups((f) => this.state.hasFeatureFlag(f)),
        sea: this.seaRow(),
      }, this.touchUI)
      : '';
    if (panel !== this.lastView) {
      if (this.lastView === '' && panel !== '') this.onViewOpen?.();
      this.lastView = panel;
      this.viewPanel.innerHTML = panel;
      this.viewPanel.classList.toggle('on', panel !== '');
    }
    // The units, the rose (it has no tile, so a chart pick that turned the
    // furniture on or off changes the console without changing a tile),
    // the chat, the panel and the count: a change of any one redraws the
    // VIEW button and packs the corner again.
    const key = `${chatOpen}|${open.chat}|${this.chatFormNow}|${open.map}|${heat}|${open.rose}|${this.viewOpen}|${count}`;
    if (key === this.lastConsole) return;
    this.lastConsole = key;
    this.viewBtn.innerHTML = viewButtonHtml(count, heat);
    this.viewBtn.classList.toggle('on', this.viewOpen);
    this.viewBtn.setAttribute('aria-expanded', String(this.viewOpen));
    this.viewBtn.setAttribute('aria-label', viewButtonLabel(count, heat));
    relayoutCluster();
  }

  // --- the readout line (R1749) ---

  /**
   * The readout line (§12.0, R1749, card 01 of mockup round 082): the top
   * of the screen only reads. It holds the five commodities with their
   * store gauges (the rates on a desktop alone), the Data Core, and the
   * world clock with the most urgent timer under it. Every other control
   * stands in the dock. A press on a figure opens its explainer card, and
   * the Data Core opens the core card, as before.
   *
   * The signals follow round 080 group C, and none of them is gold: a full
   * store lifts a lid over its figure and fills its gauge thick and white;
   * a schedule drains a light line along the lower edge; a fault draws a
   * moving red hatch there, and a hatched mark before its timer.
   */
  private renderTopbar() {
    const html = readoutHtml(this.state, {
      signals: this.signalsNow, drain: this.chromeLook.notices === 'drain',
      alarmsOpen: this.alarmsOpen, coreAsked: this.coreAsk,
    });
    // The same rule the panels keep (§10.3): only touch the DOM when the
    // markup changed. The line reads at 4 Hz but says something new about
    // once a second, so three rebuilds in four were an innerHTML parse and
    // a layout for the same string.
    if (!this.topbarHost.write(html)) return;
    this.readoutFit?.content(html);
    // The two drain lines are children of the line (R430) and innerHTML
    // has just taken them out of it. `append` moves the same nodes back,
    // so nothing is rebuilt and no state on them is lost; the guard above
    // keeps this to about once a second. A look that stands them in the
    // notices lane instead (R824) keeps them there: the line did not hold
    // them, so the wipe did not take them.
    if (this.noticeCol.timerCell === null) this.topbar.append(this.truceEl, this.linkEl);
    // The rewrite and the append start each hatch again; give it back the
    // phase of the wall clock (R1749).
    alignHatches();
  }

  /**
   * Every running timer in the signal rank (R1753, `ui/alarms.ts`): the
   * maintenance window or the link, then the faults of the seaquake, then
   * the schedules, which is the order of the drain (R430, §8):
   * **maintenance > link > seaquake > founding truce**. The maintenance
   * window takes the link's place, because a socket that drops inside a
   * window IS the window; while the link is down the seaquake and the truce
   * extrapolate a stale anchor, so they stand aside.
   *
   * Each timer carries the share of its schedule still to run (R430): the
   * seaquake stages 1 and 2 run out the season, stage 3 the settlement,
   * the truce itself, and a maintenance window the last
   * `MAINT_DRAIN_SEC`. The totals ride the hello's rules, and a total of 0
   * (every frame before the hello) reads as a full line.
   */
  private runningTimers(): RunningTimer[] {
    const s = this.state;
    const r = s.rules;
    const share = (left: number, total: number) => (total > 0 ? left / total : 1);
    const out: RunningTimer[] = [];
    const maint = this.maintClaim();
    if (maint) {
      const left = Math.max(0, s.maintenanceAt - Date.now() / 1000);
      out.push({ id: 'maint', claim: maint, stage: 0, frac: Math.min(1, left / MAINT_DRAIN_SEC) });
    } else if (this.linkClaimNow) {
      out.push({ id: 'link', claim: this.linkClaimNow, stage: 0, frac: 1 });
    }
    if (!this.linkDown) {
      const stage = s.board?.quakeStage ?? 0;
      // Stage 3 counts the drain, then the roll in the card phase (R2092);
      // both are wall clocks with a wall total (R2091).
      const card = s.settlementCard();
      const quake = quakeClaim(stage, s.seasonLeftSec(), s.settlementLeftSec(), card, s.settlementHeld);
      if (quake) {
        const settleTotal = card ? r.settlementCardSec : r.settlementDrainSec;
        out.push({
          id: 'quake', claim: quake, stage,
          frac: stage === 3 ? share(s.settlementLeftSec(), settleTotal) : share(s.seasonLeftSec(), r.seasonSec),
        });
      }
      // The siege window that concerns the reader takes the cell from
      // `slot_sec` world seconds before it opens; the truce counts on in
      // `+N` and in the clock's card.
      const win = s.settlementEndsMs === 0 ? slotWindow(this.windows().mine, s.truceLeftSec()) : null;
      if (win) {
        const what = esc(SW[win.open ? 'alarm_what_open' : 'alarm_what_shut']({
          target: targetName(win.g), at: hhmm(win.open ? win.closeSec : win.openSec),
        }));
        out.push({
          id: 'window', claim: windowClaim(win), stage: 0, cell: win.g.cell, what,
          frac: win.open ? share(win.leftSec, r.objWindowSec) : Math.min(1, win.leftSec / SIEGE_WINDOW.slotSec),
        });
      }
      const truce = truceClaim(s.truceLeftSec());
      if (truce) out.push({ id: 'truce', claim: truce, stage: 0, frac: share(s.truceLeftSec(), r.truceSec) });
    }
    // The countdown of the idle sea view (R2865): a schedule on the wall
    // clock, last of its rank. It holds at 0s while the sea view loads.
    const sea = this.seaIdleCountdown?.() ?? null;
    if (sea) {
      out.push({ id: 'seaview', claim: seaIdleClaim(sea.leftSec), stage: 0, frac: share(sea.leftSec, sea.totalSec) });
    }
    return orderTimers(out);
  }

  // --- the top bar's explainer card (§12.0) ---

  /** The parts of the explainer card of one top-bar control. */
  private barTipParts(key: string): BarTipParts {
    const s = this.state;
    if (key === 'clock') return clockTip(s, this.runningTimers());
    if (key === 'cores') return coreTip(s);
    return storeTip(s, key as keyof Resources);
  }

  /** Shows or hides the explainer card, and tells `onBarTip` of a change. */
  private showBarTip(open: boolean) {
    this.barTip.classList.toggle('on', open);
    if (open === this.barTipShown) return;
    this.barTipShown = open;
    for (const fn of this.barTipHeard) fn(open);
  }

  /** Opens the card on one control, or closes it when `key` is empty. */
  private setBarTip(key: string) {
    if (key === this.tipOn) return;
    this.tipOn = key;
    this.renderBarTip();
    if (this.tipOn !== 'clock') this.releaseHeld();
  }

  /**
   * Draws and places the card.
   *
   * It follows the tooltip's two rules (§10.3): the markup is written only
   * when it changed, so a card that stands open while the empire frame
   * arrives four times a second costs one string compare; and the place is
   * taken from the control's own box, flipped back inside the viewport at
   * either edge.
   */
  private renderBarTip() {
    const key = this.tipOn;
    // Closed, and closed last time: nothing to write. This runs on every
    // 4 Hz pass, and a card nobody asked for must cost one compare.
    if (key === '' && this.barTipHost.html === '') return;
    // A free card (R1555) was written and placed once, when it opened.
    if (key === FREE_TIP) return;
    const btn = key === ''
      ? null
      : this.topbar.querySelector<HTMLElement>(`button[data-tip="${key}"]`);
    if (!btn) {
      // The control went away — a bar that lost its dev lane, or a card
      // that was open when the empire frame stopped arriving.
      this.tipOn = '';
      this.tipFree = '';
      this.barTipHost.forget();
      this.showBarTip(false);
      this.releaseHeld();
      return;
    }
    this.barTipHost.write(barTipHtml(key, this.barTipParts(key)));
    this.showBarTip(true);
    this.placeUnder(this.barTip, btn);
  }

  /**
   * Opens the explainer card free of the top bar: on a title held on touch
   * (R1555), or on a definition (R1557). The card stands under no top-bar
   * control, so its markup is written once. `id` names what it shows.
   * `place` stands the card once its markup is in, because the card's size
   * decides the place.
   */
  private showFreeTip(id: string, html: string, place: () => void) {
    this.tipOn = FREE_TIP;
    this.tipFree = id;
    this.barTipHost.write(html, true);
    this.showBarTip(true);
    place();
  }

  /** The card of a title held on touch (R1555): the title's lines, the
   *  first in the body ink, over the press point. */
  private showTitleCard(text: string, x: number, y: number) {
    const lines = text.split('\n').map((l) => l.trim()).filter((l) => l !== '');
    const html = lines.map((l, i) => `<div class="line${i === 0 ? ' lead' : ''}">${esc(l)}</div>`).join('');
    this.showFreeTip('title', html, () => this.placeOver(this.barTip, x, y));
  }

  /**
   * A press on a term button (R1557): the definition in the explainer
   * card, under the button, or the card away when it already shows that
   * term. A term button inside the card is the title of a top-bar card,
   * and the press re-writes the card where it stands, under its cell.
   */
  private toggleTerm(btn: HTMLElement) {
    const t = glossaryTerm(btn.dataset.term ?? '');
    if (!t) return;
    const id = `term:${t.id}`;
    if (this.tipOn === FREE_TIP && this.tipFree === id) {
      this.setBarTip('');
      return;
    }
    const cell = this.tipOn === FREE_TIP ? null
      : this.topbar.querySelector<HTMLElement>(`button[data-tip="${this.tipOn}"]`);
    const anchor = this.barTip.contains(btn) ? cell : btn;
    this.showFreeTip(id, termCardHtml(t), () => {
      if (anchor) this.placeUnder(this.barTip, anchor);
    });
  }

  /**
   * Places a shown card over a press point (R1555), centred on it and
   * clear of the finger, or under it when the top of the window has no
   * room. It stays inside the viewport at either edge.
   */
  private placeOver(card: HTMLElement, x: number, y: number): void {
    // The press point is in viewport px, and the card stands in chrome px
    // (R1556).
    const cx = toChromePx(x), cy = toChromePx(y);
    const w = card.offsetWidth, h = card.offsetHeight;
    // A fingertip covers about 40 px round the point (the coarse floor).
    const reach = 28;
    const top = cy - reach - h >= 4 ? cy - reach - h : cy + reach;
    const left = Math.min(chromeViewport().w - w - 4, cx - w / 2);
    card.style.left = `${Math.max(4, left)}px`;
    card.style.top = `${Math.max(4, top)}px`;
  }

  /** Places a shown card under one top-bar control, flipped back inside
   *  the viewport at either edge. The explainer card and the core card
   *  (R1458) both stand this way. */
  private placeUnder(card: HTMLElement, btn: HTMLElement): void {
    const r = chromeRect(btn);
    const v = chromeViewport();
    // Measure from the origin with the width capped to the screen: the
    // card shrinks to fit the room right of its own `left`, so a measure
    // taken at the last pass's place feeds that place back into the width.
    card.style.left = '0px';
    card.style.maxWidth = `${Math.min(260, v.w - 8)}px`;
    const w = card.offsetWidth, h = card.offsetHeight;
    const left = r.left + w > v.w ? v.w - w - 4 : r.left;
    const top = r.bottom + 6 + h > v.h ? r.top - 6 - h : r.bottom + 6;
    card.style.left = `${Math.max(4, left)}px`;
    card.style.top = `${Math.max(4, top)}px`;
  }

  /**
   * Draws the top-bar core card (R1458), or hides it.
   *
   * It is the one place a core is spent (R1893, which took USE CORE off
   * FLEET · EXPEDITIONS): the question, the trade `coreTradeHtml` states,
   * the line that says how a core is won, and SPEND THE CORE beside
   * CANCEL. With no active research it states why and offers CANCEL alone.
   * It goes away when the last core is gone, because the cell it stands
   * under goes with it. The markup is written only when it changed (§10.3).
   */
  private renderCoreCard(): void {
    const n = this.state.empire?.dataCores ?? 0;
    const btn = this.coreAsk && n > 0
      ? this.topbar.querySelector<HTMLElement>('button[data-tip="cores"]')
      : null;
    if (!btn) {
      if (!this.coreAsk && this.coreCardHost.html === '') return;
      this.coreAsk = false;
      this.coreCardEl.hidden = true;
      this.coreCardHost.clear();
      return;
    }
    this.coreCardHost.write(coreCardHtml(this.state));
    this.coreCardEl.hidden = false;
    this.placeUnder(this.coreCardEl, btn);
  }

  /**
   * A squad change (R1571, round 075 card 18): the chip pulses one time.
   * The feed calls it with the notice; reduced motion keeps the chip still
   * through the stylesheet.
   */
  flashSquad(): void {
    this.squadFlashAt = performance.now();
    this.render(true);
  }

  /** How long ago the chip's pulse began, or null once it has run. */
  private squadFlashMs(): number | null {
    if (this.squadFlashAt === null) return null;
    const ms = performance.now() - this.squadFlashAt;
    if (ms < FLEET_MARKS.squadChange.pulseSec * 1000) return ms;
    this.squadFlashAt = null;
    return null;
  }

  /** The chip's icon: the reader's squad mark, or nothing. */
  private squadIconHtml(): string {
    const look = this.squadLook?.();
    return look ? squadIcon(look.scene, factionColor(this.state.faction)) : '';
  }

  /** The marks key, or '' with no squad or no look (R1571, card 17). */
  private squadKeyHtml(): string {
    const v = squadView(this.state.clan);
    const look = this.squadLook?.();
    if (!v || !look) return '';
    const ink = factionColor(this.state.faction);
    return keyHtml(v, look.scene, factionName(this.state.faction), keyPictures(look, ink));
  }

  /** Puts the roster card and the key away. */
  private closeSquad(): void {
    if (!this.squadOpen && !this.squadKeyOpen) return;
    this.squadOpen = false;
    this.squadKeyOpen = false;
    this.renderSquadCards();
  }

  /**
   * Draws the roster card under the chip and the marks key, or hides
   * them (R1571, round 075 cards 16 and 17). Both go when the chip goes.
   * The markup is written only when it changed (§10.3), and the roster
   * card stands under the chip the way the core card stands under its
   * cell; the stylesheet places the key.
   */
  private renderSquadCards(): void {
    const v = squadView(this.state.clan);
    const chip = v ? this.squadChip() : null;
    // A telephone has no chip: the stylesheet stands the card over the
    // tab bar (R2448).
    const sheet = isSheet();
    if (!v || (!chip && !sheet)) {
      this.squadOpen = false;
      this.squadKeyOpen = false;
    }
    let card = '';
    if (this.squadOpen && v) {
      // The readiness of each member for the reader's window (§8).
      const mine = this.state.settlementEndsMs === 0 ? this.windows().mine : null;
      const fleets = this.presence();
      const self = this.state.selfAccountId;
      card = rosterHtml(v, self, {
        ready: (m) => readinessOf(m, self, fleets, mine ? mine.g.cell : null),
        window: squadWindowHtml(mine, this.rallyResting()),
      });
    }
    this.squadCardHost.write(card);
    this.squadCardEl.hidden = card === '';
    if (card !== '' && chip) this.placeUnder(this.squadCardEl, chip);
    else if (sheet && this.squadCardEl.hasAttribute('style')) this.squadCardEl.removeAttribute('style');
    const key = this.squadKeyOpen ? this.squadKeyHtml() : '';
    this.squadKeyHost.write(key);
    this.squadKeyEl.hidden = key === '';
  }

  /**
   * The maintenance claim, or null (§8, deployment.md §6.1). It is read by
   * both halves of the lane — `setLink` gives the line up to it and
   * `renderTruce` draws it — so the one test lives here.
   *
   * The clock is the wall clock, because an operator names a wall instant.
   */
  private maintClaim(): TimerClaim | null {
    return maintenanceClaim(this.state.maintenanceAt, Date.now() / 1000);
  }

  /**
   * The schedule drain along the foot of the top bar (R430, §8). One lane,
   * three claims on it, and they never share it — the lane states the most
   * urgent fact and nothing else. The order is **link down > seaquake >
   * founding truce**, exactly as it was while the lane was a banner.
   *
   * The link banner is its own element (`#link`, `setLink`) in the same
   * lane, and it already suppressed the truce: while the socket is down
   * every countdown here extrapolates a stale wire anchor, so it is a guess
   * that keeps ticking. The seaquake joined the lane in R218, off the top
   * bar, and goes through that same `linkDown` test rather than adding a
   * second mechanism of its own.
   *
   * Founding truce (plan §5.2): counting down on the world's own clock, it
   * drops the moment weapons go live.
   *
   * The tab is the short form — the name of the clock and the figure. The
   * whole sentence is one press away in the clock's explainer card.
   */
  private renderTruce() {
    // The quake arc counts the live figure: the board itself only arrives
    // every five game-minutes, so reading it raw left the label sitting
    // still between broadcasts (R93). Stage 3 counts the settlement's own
    // extrapolated clock (R126) the same way.
    //
    // One claim, built as parts (R824): the first timer of the signal rank
    // that is not the link, which has its own element (R1753). The AUTO
    // look writes the composed sentence each claim also carries, so nothing
    // it says has changed. The drain is the share of that timer's schedule
    // still to run (`runningTimers`).
    const t = this.runningTimers().find((x) => x.id !== 'link') ?? null;
    this.truceLine.set(t?.claim ?? null, t ? t.frac : 1);
    // The lane of a `column` style holds the timer and the latest line
    // together, so it has to be told whether a timer stands in it.
    this.noticeCol.sync(t !== null || this.linkClaimed);
  }

  // --- tabs ---

  private tabBody(): string {
    // A telephone draws an open picker as a page of the menu sheet, as it
    // draws the build card (R1893): the back chip, then the card.
    if (this.pick && isSheet()) {
      const page = this.pickPageHtml();
      if (page) return page;
    }
    // A telephone draws the open build card as a page of the menu sheet
    // (087 card 02): a card cannot stand beside a menu that fills the
    // screen, so it takes the place of the list.
    if (this.infoOn && isSheet()) {
      const page = this.infoPageHtml();
      if (page) return page;
    }
    switch (this.tab) {
      case 'CLAN': return clanTab(this.state, this.cmdrMark, this.squadKeyHtml());
      case 'STANDINGS': return standingsTab(this.state, this.cmdrMark);
      case 'INBOX': return this.inboxBody();
      // A game menu: its pill row, then the open section.
      default: {
        const subs = this.subs(this.tab);
        const cur = this.curSub(this.tab);
        // One HOW IT WORKS fold at the foot, shared by the pills (R1895).
        // The CASINO pill draws none: each table screen opens the help of
        // its own game at its head, and the ECONOMY facts explain the
        // income and the auction, not a table (R2143).
        const fold = cur === 'CASINO' ? '' : howFold(this.state, this.tab);
        // The sheet head names the open menu with its glyph (R2214, round
        // 102 card 16), then WAR's window head (§12.1), then the pills.
        const menu = isGameTab(this.tab) ? sheetHeadHtml(this.tab) : '';
        const head = this.tab === 'WAR' ? this.windowHead() : '';
        return menu + head + subRow('sub', subs, cur) + this.subBody(cur) + fold;
      }
    }
  }

  /** The next window that concerns the reader and the next window of the
   *  sea, read off the Sieges frame, the world clock and the fleet layer. */
  private windows(): { mine: WindowView | null; next: WindowView | null } {
    const s = this.state;
    return nextWindows(s.sieges, {
      faction: s.faction, span: s.rules.objWindowSec, windowIn: (h) => s.windowInSec(h), fleets: this.presence(),
    });
  }

  /** RALLY rests for `rally_rest_sec` after a press. */
  private rallyResting(): boolean {
    return Date.now() < this.rallyRestUntil;
  }

  /** The window head of WAR (§12.1). No window stands in the settlement:
   *  the season is over (R2094). */
  private windowHead(): string {
    const s = this.state;
    if (s.settlementEndsMs !== 0) return '';
    return windowHeadHtml(this.windows(), {
      faction: s.faction, fleets: this.presence(), factionName,
      squad: squadView(s.clan) !== null, rallyResting: this.rallyResting(),
    });
  }

  /** The pills of one menu in this world: the pills of today and the
   *  pills whose v2 flag the world latched (R1811). */
  private subs(tab: Tab): string[] {
    return subsOf(tab, (f) => this.state.hasFeatureFlag(f), casinoHidden());
  }

  private subBody(cur: string): string {
    switch (cur) {
      case 'BASE': return installationsTab(this.state);
      case 'TECH': return techTab(this.state, this.gate, this.techKeysOpen);
      case 'SHIPYARD': return shipyardTab(this.state, this.sceneLook, this.fleets());
      case 'TORPEDO': return torpedoTab(this.state);
      case 'PRINTS': return libraryShelf(this.state, false, this.shelfCtx());
      case 'MASTERS': return libraryShelf(this.state, true, this.shelfCtx());
      case 'INVENTION': return this.draft.html(this.state);
      case 'MISSIONS': return missionsHtml(this.missionsCtx());
      case 'EXPEDITIONS': return this.expeditionsTab();
      case 'WARDROOM': return wardroomHtml(this.capRowCtx(), this.capMore, this.armed);
      case 'RECRUITMENT': return recruitmentHtml(this.capRowCtx());
      case 'PRISON': return prisonHtml(this.state, this.armed);
      case 'PRODUCTION': return productionTab(this.state);
      case 'AUCTION': return auctionTab(this.state, this.auctionPage);
      case 'CASINO': return this.casinoTab();
      // ECONOMY · TREASURE (R2700): the caches, the fragments, the finds.
      case 'TREASURE': return treasureHtml(this.state, this.treasureFoundOpen);
      case 'SOVEREIGNTY': return sovereigntyTab(this.state, this.beams, this.warLogOpen, this.contact,
        this.warTl.has ? this.warTl.view() : null);
      // WAR · STRUCTURES (R83, R95, R1892): one spec row per strategic
      // object, the contested objects first.
      case 'STRUCTURES': return structureList(this.state, this.sceneLook);
      case 'INTEL': return intelTab(this.state);
      // WAR · REPORTS opens the INBOX (openBattleReports) and is never a
      // pill of its own body.
      // BUILD · DEPOT (R2653): the rack, the live counts and ORDER.
      case 'DEPOT': return depotHtml(this.state);
      // FLEET · COMMISSIONS (R2645, R2715): PENDING, the taken offers, the
      // board, then the folded ARCHIVE.
      case 'COMMISSIONS': return commissionsHtml(this.state, this.fleets(), this.commissionArchiveOpen);
      // FLEET · ROUTES (R2589): the course plots of the empire.
      case 'ROUTES': return routesHtml({
        plots: this.state.plots, fleet: this.namedOwnFleet(), env: this.cardEnv(), armed: this.armed,
        // The shipping lanes of the faction, under `routes` (R2646, R2876).
        lanes: this.state.hasFeatureFlag('routes')
          ? { mates: this.state.factionLanes, max: this.state.rules.routeLanesMax, minLegs: this.state.rules.routeLaneLegsMin, blocked: this.state.hello?.blocked }
          : undefined,
      });
    }
    return '';
  }

  /** What the two shelves of DRAFT read beside the state (R1888). */
  private shelfCtx(): ShelfCtx {
    return { env: this.cardEnv(), look: this.sceneLook, moreOpen: this.libMore, armed: this.armed };
  }

  /**
   * Is the advisor on for this player (R1305)? The empire holds the switch
   * and the Empire frame states it (R1566), so every device of the player
   * reads one state. The join ticket set it from the account.
   */
  advisorOn(): boolean {
    return this.state.empire?.advisorOn ?? false;
  }

  /** The catalog name of the row an advice names: the hello catalog for a
   *  building, a node, a hull and a round, the empire's library for a
   *  master. The advisor names nothing itself. */
  private adviceNames: AdviceNames = (kind, id) => {
    const s = this.state;
    switch (kind) {
      case AdviceKind.ADVICE_KIND_BUILDING: return s.building(id)?.name;
      case AdviceKind.ADVICE_KIND_RESEARCH: return s.tech(id)?.name;
      case AdviceKind.ADVICE_KIND_SHIP:
      case AdviceKind.ADVICE_KIND_DESIGN: return s.ship(id)?.name;
      case AdviceKind.ADVICE_KIND_TORPEDO: return s.torpedo(id)?.name;
      case AdviceKind.ADVICE_KIND_PRINT: return s.empire?.blueprints.find((b) => b.id === id)?.name;
      // The fleet line names an own fleet (R1567).
      case AdviceKind.ADVICE_KIND_FLEET: return this.seaFleet(id)?.name;
      // The commission line names the kind of an open offer (R2645).
      case AdviceKind.ADVICE_KIND_COMMISSION: {
        const c = s.empire?.commissions.find((x) => x.id === id);
        return c ? commissionTitle(c.kind) : undefined;
      }
    }
    return undefined;
  };

  /** The sentence of the advice of this frame, or '' while the advisor is
   *  off or has no advice (R1305). */
  private adviceNow(): string {
    const a = this.state.empire?.advice;
    return a && this.advisorOn() ? adviceLine(a, this.adviceNames) : '';
  }

  /**
   * The advisor line (player-reach.md §4.1, R1305): one button above the
   * queue head. ONE line speaks to a new commander (R2131): it carries the
   * count of the sea trials as a quiet press beside its sentence, and while
   * the advisor has no advice, or stands off, its sentence is the next act
   * of the sea trials. A press on the sentence opens the row of the advice
   * or the panel of the act and gives no order (R1305, R1307); a press on
   * the count opens the rows. The hover names the whole sentence, and the
   * caret folds a sentence longer than two rows open (R1423).
   */
  private adviceHtml(trials: TrialsView, advice: string): string {
    const on = this.advisorOn();
    const next = trials.stage === 'live' ? trialNextLine(trials.mask) : '';
    const line = advice || next;
    return adviceRowHtml({
      line,
      act: advice ? 'advice' : 'trialnext',
      title: `${line}\n${advice ? ADVISOR_TITLE : TRIALS_TITLE}`,
      tag: on,
      // While the welcome card stands, the line wears its tag 1 (R1572).
      callout: this.welcomeShown ? calloutTag(1) : '',
      count: trialsCountHtml(trials),
      open: this.adviceOpen,
    });
  }

  /**
   * Says a new advice once in the polite region (R2052). The advice is
   * new when its kind, its row, its figure or its reason changes; the
   * share of a saving moves on every frame and says nothing.
   */
  private sayAdvice(line: string) {
    const a = this.state.empire?.advice;
    if (!a || !line) {
      this.adviceSaid = '';
      return;
    }
    const key = `${a.kind}:${a.id}:${a.amount}:${a.reason}`;
    if (key === this.adviceSaid) return;
    this.adviceSaid = key;
    this.live.say(SL.advisor({ line }));
  }

  /**
   * Says each siege warning once in the assertive region, when it starts
   * (R2052). Every client derives the warning from the same Sieges frame
   * and the same world clock, so every player of the faction hears it at
   * the same moment (PRODUCT.md "Fairness is the promise").
   */
  private saySieges(sieges: readonly SiegeWarning[]) {
    const now = new Set(sieges.map((w) => `${w.siteId}:${w.closeSec}`));
    for (const k of this.siegeSaid) if (!now.has(k)) this.siegeSaid.delete(k);
    for (const w of sieges) {
      const k = `${w.siteId}:${w.closeSec}`;
      if (this.siegeSaid.has(k)) continue;
      this.siegeSaid.add(k);
      this.live.alarm(SL.siege({ site: w.site, at: hhmm(w.closeSec) }));
    }
  }

  /**
   * Hides the fold caret while the line shows the whole sentence (R1423),
   * because a caret that opens nothing is a dead control. The line wraps
   * to two rows (R2131), so the sentence is cut when it needs a third. The
   * strip calls it after a new markup and on a resize, never per frame. A
   * line with no height (a dock not laid out yet) reads as cut, so the
   * caret stays. A closed sheet keeps the line (R1555), so it measures too.
   * The caret yields its room to the sentence first (R1775): the line is
   * measured without it.
   */
  private fitAdvice() {
    const row = this.peekEl.querySelector<HTMLElement>('.advrow:not(.trials)');
    const al = row?.querySelector<HTMLElement>('.al');
    if (!row || !al || this.adviceOpen) return;
    row.classList.add('fits');
    row.classList.toggle('fits', al.clientHeight > 0 && al.scrollHeight <= al.clientHeight);
  }

  /**
   * Opens the panel on the row an advice names (R1305): the menu, the pill,
   * then the row scrolled into view and marked for a moment. The advisor
   * gives no order, so the press ends here.
   */
  private followAdvice() {
    const a = this.state.empire?.advice;
    if (!a) return;
    // The fleet line selects the fleet: the map centres on it and its
    // window opens, and the order stays the player's (R1567). The sheet
    // goes down, so the map shows, as a captain's fleet name does.
    const fleet = adviceFleet(a);
    if (fleet !== null) {
      this.actions.selectFleet(fleet);
      this.collapseSheet();
      return;
    }
    const t = adviceTarget(a);
    if (!t) return;
    if (a.kind === AdviceKind.ADVICE_KIND_DESIGN) this.draft.hull = a.id as ShipClass;
    this.showPanel(t.tab, t.sub);
    const row = this.body.querySelector(t.selector)?.closest<HTMLElement>('.row');
    if (!row) return;
    row.scrollIntoView({ block: 'nearest' });
    row.classList.add('advised');
  }

  /**
   * The sea trials of this frame (player-reach.md §8.1, R1572, card 06).
   * The Empire frame states the mask and the gate; the card keeps only the
   * moment the last step closed, so the closing line can drain its bar on
   * the 4 Hz pass. The rows stay folded while the welcome card is open
   * (R2127).
   */
  private trialsView(): TrialsView {
    const e = this.state.empire;
    const now = performance.now();
    const mask = e?.seaTrials ?? 0;
    const stage = this.trialsCard.stage(e?.seaTrialsOn ?? false, mask, now);
    const open = this.trialsOpen && this.welcomeState !== 'open';
    return { stage, mask, open, left: this.trialsCard.closingLeft(now) };
  }

  /**
   * Opens one panel on its pill, for a press on the sea trials (R1572). It
   * is the navigation of the advisor line and gives no order (R1305).
   */
  private showPanel(tab: Tab, sub: string) {
    this.sub[tab] = sub;
    if (!this.ensureTab(tab)) this.render(true);
  }

  /** Opens the panel of sea trial `i` (R1572). */
  private showTrial(i: number) {
    const t = trialTarget(i);
    this.showPanel(t.tab, t.sub);
  }

  /** Folds the rows of the sea trials open or shut. A collapsed sheet
   *  keeps the line alone (R1555), so the rows raise it. */
  private toggleTrials() {
    this.trialsOpen = !this.trialsOpen;
    if (this.trialsOpen && isSheet() && this.collapsed) this.openTab(this.tab);
  }

  /**
   * Raises the welcome card on the first frame that states it (R1572,
   * card 10). The card reads its lists off that frame once and keeps them:
   * they say what the autopilot built before the handover. A phone puts
   * the sheet down, so the card has the screen above the advisor line.
   */
  private renderWelcome() {
    if (this.welcomeState === 'none' && this.state.empire?.welcome && this.state.catalog) {
      this.welcomeState = 'open';
      this.welcomeEl.innerHTML = welcomeHtml(this.welcomeFacts());
      this.collapseSheet();
      // The card is the first frame: the rest waits for its close (R2127).
      this.firstHold.hold();
    }
    // The card steps behind a panel the player opened, and comes back
    // when the panel closes while the card is still open (R1637).
    const show = this.welcomeState === 'open' && !this.welcomeCovered();
    // The truce sentence runs on the live clock while the card stands
    // (R2120), at the 4 Hz of the text panels.
    if (show) this.tickWelcomeTruce();
    if (show === this.welcomeShown) return;
    this.welcomeShown = show;
    this.welcomeEl.classList.toggle('on', show);
    this.welcomeDim.classList.toggle('on', show);
    // The advisor line can move while the card stands behind a panel, so
    // the phone places the card again when it comes back.
    if (show) { this.welcomePlaced = false; this.welcomeB = -1; }
  }

  /**
   * True while a panel the player opened stands where the welcome card
   * stands (R1637): a card of the OPTIONS geometry (OPTIONS, AUDIO, KEYS,
   * HEAT), the REPORT sheet, and on a telephone the sheet over its
   * collapsed stop, which a tab press, the return briefing's INBOX or a
   * drag opens. The desktop dock is a column beside the card and covers
   * none of it. Photo mode needs no line here: its veil hides the card.
   */
  private welcomeCovered(): boolean {
    if (this.settingsOpen || this.audioOpen || this.keysOpen) return true;
    if (this.reportOpen()) return true;
    return isSheet() && !this.collapsed;
  }

  /** Writes the truce sentence of the welcome card again (R2120). */
  private tickWelcomeTruce() {
    const line = this.welcomeEl.querySelector('.truce');
    if (!line) return;
    const left = this.state.truceLeftSec();
    const text = truceLine(left, dur(left));
    if (line.textContent !== text) line.textContent = text;
  }

  /** True while the welcome card scrolls: a desktop scrolls the card, a
   *  phone its body. A press then acts on the release (§16.8, R2120). */
  private welcomeScrolls(): boolean {
    const scrolls = (e: Element | null) => !!e && e.scrollHeight > e.clientHeight + 1;
    return scrolls(this.welcomeEl) || scrolls(this.welcomeEl.querySelector('.body'));
  }

  /** What the welcome card states, off the frames the client holds. */
  private welcomeFacts(): WelcomeFacts {
    const s = this.state;
    const e = s.empire!;
    const elapsed = s.seasonElapsedSec();
    let hold = '';
    for (const site of s.sites.values()) {
      if (site.kind === SiteKind.SITE_KIND_PLAYER_BASE && site.ownerPlayer === s.playerId) hold = site.name;
    }
    const named = (items: WelcomeItem[]) => items.filter((i) => i.name !== '');
    const sea = this.fleets();
    const pens = s.penned();
    const fleetName = (id: number) => sea.find((f) => f.id === id)?.name ?? pens.find((p) => p.fleetId === id)?.name;
    const truceSec = s.truceLeftSec();
    const speed = (cls: ShipClass) => s.ship(cls)?.speedUh ?? 0;
    return {
      season: s.board?.season ?? s.hello?.season ?? 1,
      day: Math.floor(elapsed / 86400) + 1,
      faction: factionName(s.faction),
      hold,
      squad: s.clan && s.clan.squad > 0 ? { squad: s.clan.squad, clan: s.clan.name } : null,
      clanless: s.clan === null || s.clan.npc,
      truceSec,
      truce: dur(truceSec),
      ...situationOf({
        sites: s.sites.values(), playerId: s.playerId, faction: s.faction,
        fleets: [...sea.map((f) => ({ name: f.name, speedUh: speed(f.cls) })),
          ...pens.map((p) => ({ name: p.name, speedUh: speed(p.cls) }))],
        toWorldSec: (sec) => s.mapToWorldSec(sec), dur, factionName,
      }),
      buildings: named(e.buildings.filter((b) => b.level > 0)
        .map((b) => ({ name: s.building(b.building)?.name ?? '', note: levelNote(b.level) }))),
      research: named([
        ...e.tech.map((id) => ({ name: s.tech(id)?.name ?? '', note: researchNote(true) })),
        ...e.queue.filter((j) => j.kind === JobKind.JOB_KIND_RESEARCH)
          .map((j) => ({ name: s.tech(j.target)?.name ?? j.label, note: researchNote(false) })),
      ]),
      fleets: named([
        ...sea.map((f) => ({ name: f.name, note: fleetNote(s.ship(f.cls)?.name ?? '', false) })),
        ...pens.map((p) => ({ name: p.name, note: fleetNote(s.ship(p.cls)?.name ?? '', true) })),
      ]),
      captains: named(s.roster.map((c) => ({
        name: c.name, note: (c.fleetId ? fleetName(c.fleetId) : undefined) ?? ASHORE,
      }))),
      trials: e.seaTrialsOn,
      sheet: isSheet(),
    };
  }

  /**
   * Places the welcome card on a phone: its foot stands 8 px over the
   * advisor line, so tag 1 stays in sight (R1572). A desktop places it by
   * the stylesheet alone. It runs once, after the strip that holds the
   * line is drawn, and reads one rect.
   */
  private placeWelcome() {
    // Beside the rail the card stands in the place of the context card,
    // and the stylesheet places it (round 090 card 10 C). It skips the
    // measure there and keeps asking, so a turn back to portrait measures.
    if (landscapeRail()) return;
    if (!isSheet()) { this.welcomePlaced = true; return; }
    // The dock arrives from outside the screen (ui/dockin.ts): while it
    // travels, the line stands where it will not stay, so the card keeps
    // asking (R2138). It keeps asking until two renders read one place,
    // so a sheet that still moves cannot leave the card over the line.
    if (this.root.classList.contains('docking') || this.root.classList.contains('snapping')) return;
    const row = this.peekEl.querySelector('.advrow');
    if (!row) return;
    const top = chromeRect(row).top;
    const h = chromeViewport().h;
    if (!(top > 0 && top < h)) return;
    const b = Math.round(h - top + 8);
    if (b === this.welcomeB) { this.welcomePlaced = true; return; }
    this.welcomeB = b;
    this.welcomeEl.style.setProperty('--welcome-b', `${b}px`);
  }

  /**
   * Closes the welcome card for good (R1572): the server keeps the mark,
   * so no device shows it again. While the founding truce runs, TAKE
   * COMMAND opens the world clock's card once, so a new player reads what
   * the TRUCE tab means (R430), and the release of R2127 waits until that
   * card goes, so no toast stands over it (R2138). SEA TRIALS opens the
   * rows of the sea trials and no card over them (R2133). The card itself
   * stated the truce seconds before, so no toast repeats it (R2138).
   */
  private closeWelcome(trials: boolean) {
    if (this.welcomeState !== 'open') return;
    this.welcomeState = 'closed';
    this.welcomeShown = false;
    this.welcomeEl.classList.remove('on');
    this.welcomeDim.classList.remove('on');
    this.welcomeEl.style.removeProperty('--welcome-b');
    this.welcomeB = -1;
    this.actions.closeWelcome();
    const clock = !trials && this.state.truceLeftSec() > 0;
    if (clock) {
      this.releaseAfterClock = true;
      this.setBarTip('clock');
      // A bar with no clock control draws no card: nothing to wait for.
      if (this.tipOn !== 'clock') this.releaseHeld();
    } else {
      // What waited plays now, one item at a time (R2127).
      this.firstHold.release();
    }
    if (trials && !this.trialsOpen) this.toggleTrials();
  }

  /** Plays what the welcome card held, once the world clock's card of
   *  TAKE COMMAND went (R2138). */
  private releaseHeld() {
    if (!this.releaseAfterClock) return;
    this.releaseAfterClock = false;
    this.firstHold.release();
  }

  /**
   * The peek line (R1749): the advisor line, which carries the sea trials
   * (R2131), and the rows of the sea trials under it while they stand open.
   * A telephone and a desktop draw the same line, while the welcome card
   * stands and after it, so its tag 1 points at a line that is there.
   */
  private renderPeek() {
    const tv = this.trialsView();
    const said = this.adviceNow();
    const advice = this.adviceHtml(tv, said);
    this.sayAdvice(said);
    const rows = trialsRowsHtml(tv);
    let first = advice + rows;
    const sheet = isSheet();
    // The chat line (R1799, card 16 of round 085). A DOCKED desktop stands
    // it at the foot of the dock over the queue line while the pane rests
    // at its line stop; SMALL and LARGE stand it inside the closed box as
    // its peek, at the bottom-left of the map (R2057). A telephone stands
    // it in the slot of the peek line while the band is closed, by the
    // rule of `phoneChatLine`: the closed band rests as its line. A chat
    // that the CHAT tile turned off stands no line in any form (R2369).
    // The band shows the lines themselves.
    const line = this.chatLineNow();
    let chatLine: ChatLineView | null = null;
    let boxLine: ChatLineView | null = null;
    if (!sheet) {
      chatLine = this.chatFormOf() === 'pane' && this.settings.chatStop === 'line' && this.settings.chatOn ? line : null;
      boxLine = this.boxStateNow() === 'peek' ? line : null;
    } else {
      // The rows of the sea trials stand open because the player asked
      // for them (SEA TRIALS, or the count), so no chat line takes their
      // slot; the count on VIEW still counts the line (R2138).
      chatLine = phoneChatLine(line, {
        band: this.bandOpen, held: this.welcomeShown || (this.trialsOpen && rows !== ''), advisor: first !== '',
        off: !this.settings.chatOnPhone,
      });
      if (chatLine) first = '';
    }
    this.writeChatLine(chatLine);
    this.writeChatPeek(boxLine);
    if (!this.peekHost.write(first)) return;
    this.fitAdvice();
  }

  /**
   * The two caps of the telephone tab bar (R1749). INBOX carries the
   * unread count as a solid light count, and it is lit while the INBOX
   * stands open in the sheet. MENU carries a light dot while an admin
   * seat's dev lane holds a new build, and it is lit while the OPTIONS
   * menu stands. A desktop holds no cap, so nothing is written there.
   */
  private renderCaps() {
    if (this.capInbox.parentNode !== this.tabs) return;
    const s = this.state;
    const n = this.inboxCount();
    // The caps morph while the OPTIONS menu stands over the tab bar (R1754,
    // card 02 of round 082): the cap that opened the menu closes it, and on
    // a page of the menu the INBOX cap goes back to the page above. The
    // light dot of a new build leaves MENU then: the DEV row of the root
    // reads the build.
    const menuOn = this.settingsOpen;
    // Beside the rail the menu is a rail and a pane, with no page above a
    // page (round 090 card 11 B).
    const back = menuOn && !landscapeRail() ? parentOf(this.optsPage) : null;
    const build = !menuOn && !!s.hello?.admin && (s.dev?.behind ?? 0) > 0;
    const inboxOn = !menuOn && this.tab === 'INBOX' && !this.collapsed;
    const called = this.welcomeShown;
    const inbox = back ? capHtml('back', S.cap_back) : capHtml('inbox', S.cap_inbox, { count: n });
    const menu = (called ? calloutTag(2) : '')
      + (menuOn ? capHtml('close', S.cap_close) : capHtml('menu', S.cap_menu, { dot: build }));
    const key = `${inbox}|${menu}|${inboxOn}|${menuOn}|${called}|${back}`;
    if (key === this.lastCaps) return;
    this.lastCaps = key;
    this.capInbox.innerHTML = inbox;
    this.capMenu.innerHTML = menu;
    // The cap is one fixed button, so the morph rewrites its press: BACK
    // opens the page above, and INBOX its tab.
    if (back) {
      this.capInbox.dataset.act = MENU_ACT.page;
      this.capInbox.dataset.id = back;
      delete this.capInbox.dataset.tab;
    } else {
      this.capInbox.dataset.act = 'meta';
      this.capInbox.dataset.tab = 'INBOX';
      delete this.capInbox.dataset.id;
    }
    this.capInbox.title = back ? S.cap_back_title : S.cap_inbox_title;
    // A screen reader hears the count with the cap (audit P13).
    if (!back && n > 0) this.capInbox.setAttribute('aria-label', IB.cap_label({ n }));
    else this.capInbox.removeAttribute('aria-label');
    this.capMenu.title = menuOn ? S.cap_close_title : S.cap_menu_title;
    this.capInbox.classList.toggle('on', inboxOn);
    this.capMenu.classList.toggle('on', menuOn);
    // While the welcome card stands, MENU wears its tag 2 (R1572): the
    // OPTIONS menu is where the card points.
    this.capMenu.classList.toggle('callout', called);
  }

  /**
   * The dock head of a desktop (R1749): one markup, written only when it
   * changed (§10.3). The squad chip, the cup mark, the rank and the
   * standing-orders tag keep the acts they had in the top bar, so a press
   * on the chip opens the roster card under it and the rank opens
   * STANDINGS. The chips read the marks of the commander row of the
   * OPTIONS menu, so both show one squad and one rank (R1767).
   */
  private renderDockHead() {
    const s = this.state;
    const m = this.commanderMarks();
    const open = !this.collapsed;
    const html = dockHeadHtml({
      name: s.hello?.playerName ?? '',
      squad: m.squad ? {
        ...m.squad, flashMs: this.squadFlashMs(),
      } : null,
      title: m.title,
      rank: m.rank,
      standing: m.orders,
      inbox: this.inboxCount(),
      open: { inbox: this.tab === 'INBOX' && open, opts: this.settingsOpen, dev: this.adminOpen },
      dev: s.hello?.admin ? this.devLine() : null,
      called: this.welcomeShown,
    });
    this.dockHeadHost.write(html);
  }

  // --- the alarm screen (R1753, card 16 of mockup round 082) ---

  /**
   * The presses of the alarm screen. The siege row is fixed chrome and
   * acts on the press (§16.8). The alarm list acts on the press while its
   * rows fit, and on the release when it scrolls inside the room it has. A
   * press anywhere outside the list closes it; the time cell and the siege
   * row toggle it, so their press is no press outside.
   */
  private installAlarms() {
    this.siegeEl.addEventListener('pointerdown', (ev) => this.onAction(ev));
    this.keyActivates(this.siegeEl);
    this.pressOrRelease(this.alarmEl, () => this.alarmEl.scrollHeight > this.alarmEl.clientHeight + 1);
    this.alarmDismiss = (ev: Event) => {
      if (!this.alarmsOpen) return;
      const t = ev.target as HTMLElement | null;
      if (t?.closest?.('#alarmlist, button[data-act="alarms"]')) return;
      this.alarmsOpen = false;
      this.render(true);
    };
    document.addEventListener('pointerdown', this.alarmDismiss, true);
  }

  /**
   * Every running signal of this pass, in the signal rank (R1753): the
   * timers, the siege warnings of the reader's faction, the full stores,
   * the unread INBOX, the unread chat lines of a shut chat (the count the
   * VIEW button carries, R1747, R1799), and the new build of an admin seat.
   */
  private collectSignals(): Signal[] {
    const s = this.state;
    const d = s.dev;
    return rankSignals({
      timers: this.runningTimers(),
      sieges: siegeWarnings(s.sieges, {
        faction: s.faction,
        span: s.rules.objWindowSec,
        windowIn: (hour) => s.windowInSec(hour),
        site: sitePlace,
        attacker: factionName,
      }),
      full: this.fullStores(),
      inbox: this.inboxCount(),
      chat: this.chatOpen() ? 0 : this.chatCount(),
      dev: s.hello?.admin && d && d.behind > 0 ? { behind: d.behind, build: d.build } : null,
    });
  }

  /** The stores at their cap, in the order of the readout line. Credits
   *  are uncapped (the wire states a cap of 0), so they are never full. */
  private fullStores(): FullStore[] {
    const caps = this.state.empire?.caps;
    if (!caps) return [];
    const out: FullStore[] = [];
    for (const k of RES_ORDER) {
      const have = this.state.res[k];
      if (!(caps[k] > 0 && have >= caps[k])) continue;
      out.push({ name: resName(k), have });
    }
    return out;
  }

  /**
   * The siege row and the alarm list (R1753), each written only when its
   * markup changed (§10.3). The siege row names the window battle that
   * comes first among the warnings GO TO has not put away; it yields while
   * the list stands, because the list holds it. The list closes by itself
   * when its last alarm ends. While an alarm runs, `#ui` carries
   * `alarm-on`, and RESTART takes the dark ink, so GO TO stays the one
   * solid light press of the screen.
   */
  private renderAlarms() {
    const sig = this.signalsNow;
    const rows = alarmRows(sig);
    const sieges = sig.flatMap((x) => (x.kind === 'siege' ? [x.siege] : []));
    for (const id of this.siegeSeen) {
      if (!sieges.some((w) => w.siteId === id)) this.siegeSeen.delete(id);
    }
    this.saySieges(sieges);
    if (rows.length === 0) this.alarmsOpen = false;
    const warn = this.alarmsOpen ? undefined : sieges.find((w) => !this.siegeSeen.has(w.siteId));
    const row = warn ? siegeRowHtml(warn) : '';
    // The peek line yields its slot to the siege row on a telephone.
    if (this.siegeHost.write(row)) this.dock.classList.toggle('has-siege', row !== '');
    // The season reset stands under the alarms (R2941).
    const s = this.state;
    const list = this.alarmsOpen
      ? alarmListHtml(rows, {
        season: s.board?.season ?? s.hello?.season ?? 1, leftSec: s.seasonLeftSec(), scale: s.timescale,
      })
      : '';
    if (list !== this.lastAlarmList) {
      this.lastAlarmList = list;
      keepScroll(this.alarmEl, () => { this.alarmEl.innerHTML = list; });
      alignHatches();
      this.dock.classList.toggle('has-alarms', list !== '');
    }
    this.root.classList.toggle('alarm-on', alarmRuns(sig));
  }

  /**
   * One press of the alarm screen (R1753). The time cell and the siege row
   * open the list, and a second press closes it. Every press of the list
   * closes it on its way: GO TO moves the map to the object and puts its
   * siege row away, CHECK opens the network check (R1707), READ the card
   * of the world clock, BUILD and OPEN their panels, and RESTART sends the
   * `saverestart` op the dock head sends.
   */
  private alarmAct(act: string, btn: HTMLButtonElement) {
    const was = this.alarmsOpen;
    this.alarmsOpen = false;
    switch (act) {
      case 'alarms':
        // On a telephone the list stands over the closed sheet.
        if (!was) {
          this.alarmsOpen = true;
          this.collapseSheet();
        }
        break;
      case 'siegego':
        this.siegeSeen.add(Number(btn.dataset.id ?? 0));
        this.actions.focusCell(Number(btn.dataset.n ?? 0));
        this.collapseSheet();
        break;
      case 'alarmcheck': assignWithGesture(NETTEST_HREF); break;
      case 'alarmread': this.setBarTip('clock'); break;
      case 'alarmbuild': this.ensureTab('BUILD'); break;
      case 'alarminbox': this.ensureTab('INBOX'); break;
      case 'alarmrestart': this.actions.admin('saverestart', 0); break;
    }
  }

  /**
   * The squad chip the roster card stands under (R1571). While the OPTIONS
   * menu stands, it is the squad cell of the commander row of its session
   * block (R1757), and the card closes with the menu. Otherwise the dock
   * head holds the chip on a desktop. A telephone draws no head, and its
   * menu covers the screen, so there no chip anchors the card (R2448).
   */
  private squadChip(): HTMLElement | null {
    if (isSheet()) return null;
    const host = this.settingsOpen ? this.settingsEl : this.dockHead;
    return host?.querySelector<HTMLElement>('button[data-act="squad"]') ?? null;
  }

  /**
   * The state of the desktop queue strip (R2740): minimized to its line,
   * or open at SMALL or LARGE. A press writes it to the settings, so the
   * next session opens the strip as the viewer left it. The fold of a
   * cramped room (`foldCrampedQueue`) writes no setting.
   */
  private setQueueDesk(open: boolean, large: boolean) {
    this.queueOpen = open;
    if (open !== this.settings.queueOpen || large !== this.settings.queueLarge) {
      this.writeSettings({ ...this.settings, queueOpen: open, queueLarge: large });
    }
    this.render(true);
  }

  /** The queue strip: one always-visible line, or the whole queue opened.
   *  A desktop strip at SMALL wears the class `small` (R2740). */
  private renderQueueStrip() {
    if (!this.state.empire) return;
    const desk = isSheet() ? undefined : { large: this.settings.queueLarge };
    this.queueStrip.classList.toggle('small', desk !== undefined && this.queueOpen && !desk.large);
    const html = queueStripHtml(this.state, this.queueOpen, this.armed, desk);
    // The open strip scrolls, and its clock rewrites it once a second while
    // a job runs, so it keeps its offset as the tab body does (R1761).
    if (html !== this.lastQueue) {
      this.lastQueue = html;
      keepScroll(this.queueStrip, () => { this.queueStrip.innerHTML = html; });
    }
  }

  /**
   * ECONOMY · CASINO (casino.md). The tab body is a CONSTANT shell.
   *
   * The dock's body is markup-diffed as one string, so a table frame that
   * changed one chip count would rebuild the whole dock four times a
   * second and destroy the button under the player's finger. The shell
   * therefore carries nothing that moves: `CasinoPanel` owns the
   * `.casino-host` element below and writes it under its own guard, and
   * `render()` hands it the host after the body swap.
   */
  private casinoTab(): string {
    return casinoTabHtml(this.casinoFormOf());
  }

  /** The form of the casino (R2198): the sheet of a telephone, the dock
   *  column, or the casino pane over the map. */
  private casinoFormOf(): CasinoForm {
    return casinoForm(isSheet(), this.settings.casinoDesk);
  }

  /** What FLEET · MISSIONS reads beside the state (R1816, R1888). */
  private missionsCtx(): MissionsCtx {
    const p = this.pick;
    return {
      card: this.cardCtx, env: this.cardEnv(), look: this.sceneLook, rowMore: this.rowMore,
      deploying: this.deploying, dialog: p && p.kind !== 'dive' ? p : null, armed: this.armed,
    };
  }

  /** What a captain row reads beside the captain. */
  private capRowCtx(): CapRowCtx {
    return { state: this.state, fleets: this.fleets(), env: this.cardEnv(), codesOpen: this.capCodes };
  }

  /**
   * Asks for one commit (R788).
   *
   * The map calls this on a right-click or a tap that lands on a
   * strategic object with an own combat hull selected. The card names the
   * fleet and the object in one sentence and offers COMMIT and CANCEL. A
   * second ask replaces the first, so one card stands at a time.
   */
  askCommit(fleetId: number, siteId: number): void {
    this.commitAsk = { fleetId, siteId };
    this.renderCommitCard();
  }

  /**
   * Asks whether one penned fleet sails (R1883): the digit of its control
   * group and a short press on its slot. The press selects the hull, and
   * the card of a selected hull in the pens is the deploy ask
   * (`ui/deployask.ts`, `fleetSpec`). It lives as every fleet card lives
   * (R1988): another selection and a deselect take it away.
   */
  askDeploy(fleetId: number): void {
    this.actions.selectFleet(fleetId);
  }

  /**
   * The one client check of a deploy (R1883): a hull under repair does
   * not sail. It answers true and posts the error notice when the hull
   * must stay, so every deploy press and the deploy pick stop before they
   * send the intent. The server refuses the hull as well; this check
   * answers before the round trip.
   */
  refuseDeploy(fleetId: number): boolean {
    if (!this.state.underRepair(fleetId)) return false;
    this.notice(S.deploy_ask_repairing({ fleet: this.state.pennedHull(fleetId)?.name ?? '?' }), true);
    return true;
  }

  /** Closes the modals over the scrim: KEYS, the media player and the
   *  report card. */
  private closeModals(): void {
    this.keysOpen = false;
    this.audioOpen = false;
    if (this.reports.shown) this.reports.close();
  }

  /** The ⚑ of a seamount name for the ridge peek (R2633), or ''. */
  seamountMark(n: NamedSeamount): string {
    return this.reports.seamountMark(n, this.state.hello?.playerName ?? '');
  }

  /** NAME on the peek of an own find (R2633): the naming sheet of the find. */
  openSeamountFind(body: number): void {
    this.names.openSeamountFind(body);
  }

  /** SEARCH on the peek of water (R2699, R2860): the SEARCH picker of the
   *  cell, or of the point `at` of a wreck. */
  openSearchPicker(cell: number, at?: readonly [number, number]): void {
    this.names.openSearchPicker(cell, at);
  }

  /** Esc on the map: takes the card of the rename slot away, as its
   *  CANCEL does, and answers whether one stood. The SEARCH picker holds
   *  no text box that takes the key first. */
  closeNameCard(): boolean {
    if (!this.names.shown) return false;
    this.names.close();
    return true;
  }

  /** Takes the confirm card away. */
  private closeCommitCard(): void {
    this.commitAsk = null;
    this.renderCommitCard();
  }

  /**
   * Draws the confirm card, or hides it when nothing is asked.
   *
   * The card is rebuilt from the store on every call, so a fleet that
   * sinks or a selection that moves cannot leave a stale sentence on the
   * screen: the ask is dropped when either half of it is gone.
   */
  private renderCommitCard(): void {
    const spec = this.commitSpec();
    if (!spec) {
      this.commitAsk = null;
      this.slot.claim('commit', false);
      this.commitCardEl.hidden = true;
      this.commitHost.clear();
      return;
    }
    const html = cardHtml(spec, this.cardEnv());
    this.commitCardEl.hidden = false;
    this.slot.claim('commit', true);
    this.commitHost.write(html);
  }

  /** The commit card, or null when either half of the ask is gone. */
  private commitSpec(): CardSpec | null {
    const ask = this.commitAsk;
    const s = this.state;
    const fleet = ask ? this.seaFleet(ask.fleetId) : undefined;
    const g = ask ? s.siegeOn(ask.siteId) : undefined;
    if (!ask || !fleet || !g) return null;
    // The commit card is a card of the card family in the context slot
    // (round 083 card 16): the fleet in its owner ink, the object as its
    // kind, the sentence as its note, and the verb in the dark face.
    const verb: string = g.owner === s.faction ? S.btn_defend : S.btn_commit;
    return {
      size: 'card', mark: markSvg('fleet', cssColor(s.faction)), title: fleet.name,
      ink: { faction: s.faction }, kind: sitePlace(g),
      note: { lines: [esc(S.commit_card_ask({ fleet: fleet.name, place: sitePlace(g) }))] },
      actions: [
        { act: 'commitgo', label: verb, face: 'dark' },
        { act: 'commitno', label: S.btn_commit_no, face: 'quiet' },
      ],
    };
  }

  /**
   * The card of a selected fleet: the card of the hull at sea, else the
   * deploy ask of a hull in the pens (R1883, R1988), which the map does not
   * hold. A docked hull that leaves the pens takes its card with it, and
   * the selection waits for the hull to come onto the map: a deploy from
   * FLEET · MISSIONS then shows the card of the hull at sea. Any other
   * hull in neither place is a lost contact.
   */
  private fleetSpec(id: number): CardSpec | null {
    const c = this.contact(id);
    const docked = c ? null : this.deploySpec(id);
    if (c || docked) this.dockedSel = docked ? id : 0;
    if (docked) return docked;
    return !c && this.dockedSel === id ? null : fleetCard(this.cardCtx, c, 'card');
  }

  /** The deploy ask (R1883), or null when the hull is not in the pens. */
  private deploySpec(id: number): CardSpec | null {
    const s = this.state;
    const st = this.state.pennedHull(id);
    if (!st) return null;
    const spec = s.ship(st.cls);
    return deployCardSpec({
      st, faction: s.faction, mark: shipMarkSvg(st.cls, this.sceneLook), kind: spec?.name ?? '?',
      state: deployAskState(st, penStep(s, st)),
      // The pens rule: a repair needs a Wharf.
      canRepair: s.level(Building.BUILDING_WHARF) > 0,
      group: s.groups.slotOf(st.fleetId),
      captain: captainFact(s, st.captainId, spec?.support ?? false),
      systems: fitStrip(s.catalog, st),
    });
  }

  /**
   * DEPLOY and REPAIR of the deploy ask (R1883). The press and its key of
   * `[keys.card]` (R1989) both end here. DEPLOY sends the deploy of the
   * DEPLOY press, REPAIR the repair of the pens, and both drop the
   * selection, so the card goes. The hull that sails takes the selection
   * back when it comes onto the map (`Commands.adoptNewFleets`).
   */
  private deployPress(act: 'deploygo' | 'deployfix', id: number): void {
    const a = this.actions;
    if (act === 'deployfix') {
      a.repair(id);
      a.deselect();
      return;
    }
    a.deselect();
    if (this.refuseDeploy(id)) return;
    a.deployDesign(id);
    this.collapseSheet();
  }

  /**
   * One key of `[keys.card]` (R1989): `d` presses DEPLOY and `r` presses
   * REPAIR of the deploy ask. The key acts only while the card stands in
   * the context slot and the press is on. It answers false when no press
   * acted, and the map then takes the key with its own meaning.
   */
  private pressCardKey(id: CardKeyId): boolean {
    // The helm keys of the group `helm` are the map input's (R2707).
    if (id !== 'deploy' && id !== 'repair') return false;
    const sel = this.state.selection;
    if (sel?.kind !== 'fleet' || this.scanReport || this.contact(sel.id)) return false;
    if (this.slot.owner() !== 'selection' || !this.selection.classList.contains('on')) return false;
    const act = id === 'deploy' ? 'deploygo' : 'deployfix';
    const press = this.deploySpec(sel.id)?.actions?.find((x) => x.act === act);
    if (!press || press.off) return false;
    this.deployPress(act, sel.id);
    return true;
  }

  /** The digit of a control group (R539, R1883): a fleet at sea is
   *  recalled, and a fleet in the pens opens the deploy ask. */
  private recallSlot(n: number): void {
    const id = this.state.groups.fleetAt(n);
    if (!this.seaFleet(id) && this.state.pennedHull(id)) {
      this.askDeploy(id);
      return;
    }
    this.actions.recallGroup(n);
  }

  /**
   * FLEET · EXPEDITIONS (round 091 card 03, R1893): UNDERWAY, the dives
   * that run and outfit, then START A DIVE, the depths as spec rows whose
   * START opens the start sheet. USE CORE is the top-bar core card alone
   * (R1458). `ui/overlay/expeditions.ts` writes both parts.
   */
  private expeditionsTab(): string {
    if (!this.state.empire) return '';
    const open = this.pick?.kind === 'dive' ? this.pick.depth : 0;
    return underwayHtml(this.state) + startRowsHtml(this.state, open);
  }

  // --- the selection anchor: a selected object, or a probe scan report ---

  /**
   * The six group slots (R1817; round 085 cards 10, 12 A and 14). One set
   * of views feeds both hosts. The group row of a telephone stands only
   * while a slot holds a fleet (085 open point 4), so a player with no
   * group keeps the dock at its height; the row's height then reaches the
   * console, VIEW and the context sheet through `sheetBarH`, because the
   * row stands inside the dock's bar. A docked fleet keeps its slot with an
   * anchor (R839). The bar of a desktop names the whole fleet from
   * `bar_wide_min_px` of screen width. Each host shows the held slots and
   * the empty slot with the lowest digit (`shownSlots`, R1865). The bar
   * stands always, so with no group it shows the empty slot 1 alone (the
   * owner, 2026-10-13, R2057); the row stands only with a group (R1956).
   */
  private renderGroups() {
    const s = this.state;
    const sel = s.selection;
    const views = slotViews({
      groups: s.groups, sea: this.fleets(), docked: s.penned(),
      selected: sel?.kind === 'fleet' ? sel.id : 0, look: this.sceneLook,
    });
    // The bar keeps to its room, the map left of the dock column (R1865):
    // the narrow names where the wide bar does not fit, and the left start
    // where the narrow bar does not fit either. The room reads the dock's
    // published width, so the 4 Hz render reads no layout.
    const vw = chromeViewport().w;
    const dockW = parseFloat(this.root.style.getPropertyValue('--dock-w')) || 0;
    const fit = barFit(shownSlots(views).length, vw, readoutRoom(vw - dockW), this.barGap());
    this.groupRow.render(views);
    this.groupRow.el.classList.toggle('on', rowShown(views));
    this.groupBar.render(views, fit.wide);
    this.groupBar.el.classList.toggle('wide', fit.wide);
    this.groupBar.el.classList.toggle('tight', fit.tight);
    // The owner ink of the hull marks and the hull bars.
    const ink = cssColor(s.faction);
    for (const host of [this.groupRow.el, this.groupBar.el]) {
      if (host.style.getPropertyValue('--gs-ink') !== ink) host.style.setProperty('--gs-ink', ink);
    }
  }

  /** The gap between two slots of the group bar in chrome px, read once
   *  from the stylesheet (the token `--s-2`); 0 before the sheet holds. */
  private barGapPx = NaN;
  private barGap(): number {
    if (Number.isFinite(this.barGapPx)) return this.barGapPx;
    const g = parseFloat(getComputedStyle(this.groupBar.el).columnGap);
    if (Number.isFinite(g)) this.barGapPx = g;
    return Number.isFinite(g) ? g : 0;
  }

  /** The own fleet at sea that the sheet names, or null: SEND of FLEET ·
   *  ROUTES sends it (R2589). */
  private namedOwnFleet(): { id: number; name: string } | null {
    const sel = this.state.selection;
    const f = sel?.kind === 'fleet' ? this.seaFleet(sel.id) : undefined;
    return f ? { id: f.id, name: f.name } : null;
  }

  /** Sends a stored course to an own fleet at sea (R2589, R2646, R2658):
   *  SEND of FLEET · ROUTES, of a mate's lane and of the lane card. A
   *  course or a fleet that is gone, and a course with no leg, send
   *  nothing. */
  private sendPlot(f: { id: number } | null | undefined, p: CoursePlot | undefined): void {
    if (p && f && p.legs.length > 0) this.actions.sendCourse(f.id, plotLegs(p));
  }

  /** The own fleet on the map with this id, if any. */
  private seaFleet(id: number): UIFleet | undefined {
    return id ? this.fleets().find((f) => f.id === id) : undefined;
  }

  /** An own hull, at sea or in the pens, if any. */
  private ownHull(id: number): { name: string } | undefined {
    return this.seaFleet(id) ?? this.state.pennedHull(id);
  }

  /**
   * A short press on a slot: the recall of the digit key. A docked fleet
   * has no place on the map, so its slot asks to deploy it, as its digit
   * does (R1883); an empty slot says how to set it.
   */
  private tapSlot(n: number) {
    const id = this.state.groups.fleetAt(n);
    if (this.ownHull(id)) {
      this.recallSlot(n);
      return;
    }
    this.slotToast.show(esc(S.slot_tip));
  }

  /**
   * A long press on a slot, or a card head dropped on it (085 card 13):
   * the selected own fleet takes the slot. A slot that held another fleet
   * takes the new one, and the toast names the fleet that left, with UNDO
   * for `undo_sec` (085 open point 7). UNDO stands while the fleet that
   * left is an own hull at sea or in the pens, because the server puts a
   * hull in the pens on a slot too (R1842). With no own fleet selected,
   * the press shows a tip and nothing else.
   */
  holdSlot(n: number) {
    const sel = this.state.selection;
    const f = sel?.kind === 'fleet' ? this.seaFleet(sel.id) : undefined;
    if (!f) { this.slotToast.show(esc(S.slot_tip)); return; }
    const plan = planAssign(this.state.groups, n, f.id);
    this.actions.assignGroup(n);
    if (!plan.left) {
      this.slotToast.show(esc(S.slot_set({ name: f.name, n })));
      return;
    }
    const left = this.ownHull(plan.left);
    const undo = left
      ? () => { for (const st of undoSteps(plan)) this.actions.setGroup(st.slot, st.fleetId); }
      : undefined;
    this.slotToast.show(esc(S.slot_replaced({ name: f.name, n, left: left?.name ?? '?' })), undo);
  }

  /**
   * A long press on the slot of the selected own fleet on the group row of
   * a telephone (R2415): the fleet spends its signature order, as the scan
   * key spends it (`fast/input.ts`), and the slot keeps its fleet. When the
   * order cannot fire, the toast states why and nothing is sent; the
   * server re-validates the spend.
   */
  signSlot(n: number) {
    const id = this.state.groups.fleetAt(n);
    const f = this.seaFleet(id);
    const hull = f ?? this.state.pennedHull(id);
    if (!hull) { this.slotToast.show(esc(S.slot_tip)); return; }
    const a = f?.approach;
    const left = a?.active && a.leftSec > 0 ? dur(this.state.mapToWorldSec(a.leftSec)) : '';
    const why = signatureRefusal(hull.name, this.state.ship(hull.cls)?.signature, a, left, !f);
    if (why) { this.slotToast.show(esc(why)); return; }
    this.slotToast.hide();
    this.actions.fleetOp(id, FleetOp.FLEET_OP_SIGNATURE);
  }

  /**
   * A mouse over a slot of the group bar opens the peek of its fleet
   * (085 card 14), under the slot. A slot with no fleet at sea opens none.
   */
  private hoverSlot(n: number, b: HTMLElement | null) {
    const id = n ? this.state.groups.fleetAt(n) : 0;
    if (!b || !this.seaFleet(id)) { this.hideTooltip(); return; }
    const r = b.getBoundingClientRect();
    const at = { x: r.left + r.width / 2, y: r.bottom };
    this.showObjectPeek(at, { fleet: id }, at);
  }

  /**
   * What the map cards read of this overlay (R1810). The getters read the
   * live fields, so the one object serves every render.
   */
  private readonly cardCtx: MapCardCtx = (() => {
    const o = this;
    return {
      get s() { return o.state; },
      fleets: () => o.fleets(),
      squadmate: (id: number) => o.squadmate(id),
      commanderMark: (id: number, name: string) => o.reports.commanderMark(id, name),
      // The hull name of a friendly hull carries the ⚑ of REPORT (R1323).
      // A bot hull of the faction has no author, and a hull with a faction
      // pool name has no words of a player to report (R1886).
      hullMark: (f: CardFleet) => (f.customName && f.ownerPlayer !== 0 && f.ownerPlayer !== o.state.selfAccountId
        ? o.reports.mark(`fleet:${f.id}`, {
          head: S.report_head_fleet({ name: esc(f.name) }),
          game: { kind: 'fleet', fleetId: f.id },
        }) : ''),
      picking: (act: string, id: number) => {
        const mode = PICK_ACTS[act];
        return mode !== undefined && o[mode] === id;
      },
      get aiming() { return o.aiming; },
      armed: (id: number) => {
        const a = o.armedNow();
        return a && a.id === id ? a : null;
      },
      get baseTorp() { return o.baseTorp; },
      moreOpen: (id: number) => o.moreFor === id,
      editOpen: (id: number) => o.editFor === id,
      groupOf: (id: number) => o.state.groups.slotOf(id),
      get canRename() { return o.state.hasFeature(KEY_FLEET_DESIGNER); },
      scanLeftSec: (id: number) => o.state.scanSeen.leftSec(id, Date.now()),
      chevronsShown: (id: number) => o.chevronsShown(id),
      monster: (id: number) => o.monsterOf(id),
      helm: (id: number) => o.helmOf(id),
      laneOffer: (id: number) => o.laneOfferOf(id),
      get historical() { return o.settings.chart === 'historical'; },
      get canMark() { return o.onMarkContact !== null; },
      lastLay: (id: number) => o.lastLay.get(id) ?? 0,
      guard: (id: number) => {
        const g = o.state.guards.fleet(id);
        if (!g) return null;
        const now = Date.now();
        return { frac: o.state.guards.frac(g, now), leftSec: o.state.guards.leftSec(g, now), phase: g.phase, near: g.near };
      },
    };
  })();

  /** A desktop with a mouse shows the key caps of a card; a finger does not. */
  private cardEnv(): CardEnv { return { keycaps: !this.touchUI }; }

  /**
   * The context slot (§12.0) shows one card (R1810): a probe scan report,
   * or the selected object. The report is checked first, because the two
   * are mutually exclusive by construction: opening a report drops the
   * selection (`report` in `onAction`), and every change of selection
   * drops the report (main.ts `select`, through `closeScanReport`).
   *
   * A desktop stands the card in the slot over the console. A telephone
   * stands it as the context sheet over the tab bar, with the grab handle
   * on its top edge (R2008): closed (the peek line and the action row with
   * ⋯) or raised (the whole card, EDIT and ✕ in its head).
   * A menu that opens on a telephone takes the sheet, and the selection
   * yields to the selection chip or the fold in the head of the menu
   * (`selPlace`, 087 card 01).
   *
   * One change guard covers every form, so the slot writes the DOM only
   * when the markup of its card changed.
   */
  private renderSelection() {
    const report = this.scanReport;
    const sel = this.state.selection;
    if (sel?.kind === 'fleet' && sel.id !== this.lastOwnFleet && this.seaFleet(sel.id)) this.lastOwnFleet = sel.id;
    // The lane card goes with its lane (R2658).
    const laneSpec = this.laneOpen ? this.laneSpec(this.laneOpen.ref, this.laneOpen.fleet, 'card') : null;
    if (this.laneOpen && !laneSpec) this.laneOpen = null;
    const lane = this.laneOpen;
    // The rename card names one hull, so it goes when the card stops
    // showing that hull.
    const shown = !report && sel?.kind === 'fleet' ? sel.id : 0;
    if (this.names.hull !== 0 && shown !== this.names.hull) this.names.close();
    // The ⋯ and EDIT of a card belong to one fleet, and a new selection
    // closes them.
    if (shown !== this.moreFor) this.moreFor = 0;
    if (shown !== this.editFor) this.editFor = 0;
    const phone = isSheet();
    // A new selection opens the sheet closed. On a telephone it also puts
    // the chat band away, because both stand over the tab bar (R1799); a
    // band that opens later makes the sheet yield instead, so the newer
    // of the two holds the foot of the screen.
    const key = report ? `report:${report.unix}:${report.fromId}`
      : lane ? `lane:${lane.ref.owner}:${lane.ref.id}` : sel ? `${sel.kind}:${sel.id}` : '';
    if (key !== this.ctxKey) {
      this.ctxKey = key;
      this.ctxMode = ctxStep(this.ctxMode, 'select', this.routesOn());
      if (phone && key !== '') this.bandOpen = false;
      // On a desktop the newer of the two cards holds the slot: a new
      // selection closes the build card (R1826).
      if (!phone && key !== '') this.closeInfo(false);
    } else if (this.ctxMode.plot && !this.routesOn()) {
      this.ctxMode = CTX_CLOSED;
    }
    // The plot line names one fleet: it ends when the sheet stops naming it.
    if (this.plot && (!this.ctxMode.plot || sel?.kind !== 'fleet' || sel.id !== this.plot.fleetId)) {
      this.plot = null;
      if (this.ctxMode.plot) this.ctxMode = CTX_CLOSED;
    }
    const plot = this.ctxMode.plot ? this.plot : null;
    const spec: CardSpec | null = report
      ? scanReportCard(this.cardCtx, report)
      : lane ? laneSpec
      : !sel ? null
        : sel.kind === 'fleet'
          ? this.fleetSpec(sel.id)
          // A point of interest holds the slot as a site does (R3138).
          : sel.kind === 'point' ? pointCard(this.cardCtx, sel.id)
          : siteCard(this.cardCtx, this.state.sites.get(sel.id), 'card');
    // Beside the rail a card at the right edge stands beside an open menu:
    // the menu lowers it to its peek in place of a leave (round 090 card
    // 05 C).
    const menuOpen = phone && !this.collapsed;
    const beside = phone && railCtxBeside();
    this.ctxMode = menuYield(this.ctxMode, menuOpen && !this.ctxMenuWas, beside);
    this.ctxMenuWas = menuOpen;
    const place = selPlace({
      // The chat band and the alarm list take the foot of a telephone too,
      // and the sheet yields to either while it stands (R1822).
      selection: spec !== null && !(phone && (this.bandOpen || this.alarmsOpen)),
      phone,
      menuOpen,
      three: this.settings.sheetHeights === 'three',
      expanded: this.expanded,
      beside,
    });
    this.slot.claim('selection', place === 'sheet');
    this.root.classList.toggle('ctx-on', phone && place === 'sheet' && this.slot.owner() === 'selection');
    const head = spec && place === 'chip' ? selChipHtml(spec) : spec && place === 'fold' ? foldHtml(spec) : '';
    this.selHeadHost.write(head);
    // The card carries the armed order while it stands (R2164); the hint
    // host stands for it otherwise.
    this.armOnCard = spec?.armed !== undefined && place === 'sheet' && plot === null;
    if (!spec || place !== 'sheet') {
      this.selection.classList.remove('on', 'report', 'closed', 'raised', 'plot');
      this.lastSel = '';
      return;
    }
    const raised = phone && this.ctxMode.raised;
    const env = this.cardEnv();
    const html = plot
      ? !phone ? cardHtml(plotCardSpec(spec, plot), env)
        : grabHtml(raised) + (raised ? plotRaisedHtml(plot, env) : plotClosedHtml(plot, env))
      : !phone ? cardHtml(spec, env)
        : grabHtml(raised) + (raised ? cardHtml(raisedSpec(spec), env) : closedHtml(spec, env));
    this.selection.classList.add('on');
    this.selection.classList.toggle('closed', phone && !raised);
    this.selection.classList.toggle('raised', raised);
    this.selection.classList.toggle('plot', plot !== null);
    if (html !== this.lastSel) {
      this.lastSel = html;
      // The live figures of a fleet change each second, and the card keeps
      // the place a finger scrolled it to.
      keepInnerScroll(() => this.selection.querySelector<HTMLElement>(':scope > .ck'), () => {
        this.selection.innerHTML = html;
      });
      this.selection.classList.toggle('report', !!report);
    }
  }

  /** The flag `routes` (R1795): the plot line of the context sheet. */
  private routesOn(): boolean {
    return this.state.hasFeatureFlag('routes');
  }

  /**
   * Opens the plot line for one own fleet that the sheet names (v2 slot 3).
   * It answers false while the flag `routes` is off. PLOT of ⋯ and the
   * PATROL key call it under `routes` (R2589). LOOP starts on.
   */
  enterPlot(fleetId: number): boolean {
    const sel = this.state.selection;
    const f = this.seaFleet(fleetId);
    if (!this.routesOn() || !f || sel?.kind !== 'fleet' || sel.id !== fleetId) return false;
    this.plot = plotStart(f.id, f.name, { x: f.x, y: f.y }, plotCaps(this.state.rules));
    this.ctxMode = ctxStep(this.ctxMode, 'plot', true);
    this.render(true);
    return true;
  }

  /** Ends the plot line and sends nothing: ✕ of the line and esc (R2589). */
  endPlot(render = true): void {
    this.plot = null;
    this.ctxMode = ctxStep(this.ctxMode, 'endplot', this.routesOn());
    if (render) this.render(true);
  }

  /**
   * DONE of the plot line: it hands the legs to `onPlotDone` and ends the
   * line. The DONE press and the keys enter and `d` call it (R2589,
   * R3185). A draft with no leg is the DONE press that is off: it does
   * nothing and answers false, as it does while no plot line stands.
   */
  confirmPlot(render = true): boolean {
    const p = this.plotting() ? this.plot! : null;
    if (!p || p.legs.length === 0) return false;
    this.onPlotDone?.(p.fleetId, plotWaypoints(p.legs));
    this.endPlot(render);
    return true;
  }

  /** The draft course while the plot line stands, else null. The draft is
   *  a new object on each edit only, so the plot layer packs on a change
   *  alone (R2589). */
  plotDraft(): PlotDraft | null {
    return this.plotting() ? this.plot : null;
  }

  /** The plot line stands: a tap on the map adds a leg and selects nothing. */
  plotting(): boolean {
    return this.ctxMode.plot && this.plot !== null;
  }

  /** A tap on the map while the plot line stands adds a leg at that
   *  point, in world px. It answers whether the tap went to the plot. */
  addPlotLeg(x: number, y: number): boolean {
    if (!this.plotting()) return false;
    this.plot = plotAdd(this.plot!, { x, y });
    this.render(true);
    return true;
  }

  /**
   * Puts the context sheet in a state (R2008): the drag, a tap on the grab
   * handle or the line, ⋯ of a closed sheet and the fold all come here. A
   * raise while a menu stands closes the menu first, so the raised card
   * stands; a lower closes ⋯ and EDIT, which the closed sheet does not
   * show.
   */
  private ctxSet(raised: boolean) {
    if (raised && isSheet() && !this.collapsed) this.collapseSheet();
    this.ctxMode = ctxStep(this.ctxMode, raised ? 'raise' : 'lower', this.routesOn());
    if (!raised) { this.moreFor = 0; this.editFor = 0; }
    this.render(true);
  }

  /** ⋯ or EDIT of a fleet's card by the rule of `gridPress` (R2008,
   *  R2302): one grid at a time, and a closed telephone sheet rises with
   *  the grid. */
  private pressGrid(which: 'more' | 'edit') {
    const sel = this.state.selection;
    const go = gridPress({
      fleet: sel?.kind === 'fleet' ? sel.id : 0,
      open: which === 'more' ? this.moreFor : this.editFor,
      closedPhone: isSheet() && !this.ctxMode.raised,
    });
    if (go.raise) this.ctxSet(true);
    if (which === 'more') this.moreFor = go.open; else this.editFor = go.open;
    if (go.open === 0) return;
    if (which === 'more') this.editFor = 0; else this.moreFor = 0;
  }

  /**
   * The context sheet follows a drag on its grab handle, its line or its
   * card head (§12.0, R2008, `ui/ctxdrag.ts`). A press on a button is
   * the button's, and the body of a raised card scrolls under the finger,
   * so only the handle, the line and the head take the drag. A tap on the
   * line or the handle of the fold in the head of a menu sheet raises the
   * card.
   */
  private installCtxSwipe() {
    // The drag lives as long as the overlay, so nothing keeps it.
    void new SheetDrag({
      el: this.selection,
      live: () => isSheet() && this.selection.classList.contains('on') && this.plotGrip === null,
      raised: () => this.ctxMode.raised,
      set: (raised) => this.ctxSet(raised),
    });
    this.selHead.addEventListener('click', (ev) => {
      const t = ev.target as HTMLElement;
      if (!t.closest('.cs-fold') || (!t.closest('.cs-grab') && (t.closest('button') || !t.closest('.cs-line')))) return;
      this.ctxSet(true);
    });
    this.selection.addEventListener('pointercancel', () => { this.plotGrip = null; });
    // A drag on the grip of a leg moves the leg to the row it is let go
    // over (round 088 card 08).
    this.selection.addEventListener('pointerdown', (ev) => {
      const g = (ev.target as HTMLElement).closest<HTMLElement>('.cs-grip[data-grip]');
      if (!g || !this.plot) return;
      this.plotGrip = { id: ev.pointerId, leg: Number(g.dataset.grip) };
    });
    this.selection.addEventListener('pointerup', (ev) => {
      const g = this.plotGrip;
      this.plotGrip = null;
      if (!g || g.id !== ev.pointerId || !this.plot || typeof document.elementFromPoint !== 'function') return;
      const row = document.elementFromPoint(ev.clientX, ev.clientY)?.closest<HTMLElement>('.cs-leg[data-leg]');
      if (!row) return;
      this.plot = plotMove(this.plot, g.leg, Number(row.dataset.leg));
      this.render(true);
    });
  }

  /**
   * Drops an open probe scan report and the lane card, if one stands.
   *
   * Both share the bottom-left anchor with the selection, so they die with
   * everything that kills a selection: esc, a click on empty water, the ✕
   * of the card, and a press on another object. main.ts calls this from
   * `select`, which is the one door all four reach.
   *
   * Opening a report calls `deselect` first and sets its own field after,
   * so this cannot undo the open.
   */
  closeScanReport() { this.scanReport = null; this.laneOpen = null; }

  /**
   * Opens the lane card of a press on a shipping lane (R2658). The own
   * fleet selected at the press, else the one selected last, is the fleet
   * SEND sends; the selection goes, because the card takes its anchor.
   */
  openLaneCard(ref: LaneRef): void {
    const sel = this.state.selection;
    const now = sel?.kind === 'fleet' && this.seaFleet(sel.id) ? sel.id : 0;
    const fleet = now || this.lastOwnFleet;
    this.actions.deselect();
    this.laneOpen = { ref, fleet };
    this.render(true);
  }

  /** The lane card of a lane, or null when the lane is gone. */
  private laneSpec(ref: LaneRef, fleetId: number, size: CardSize): CardSpec | null {
    const p = findLane(this.state, ref);
    if (!p) return null;
    const f = this.seaFleet(fleetId);
    return laneCard({ s: this.state, fleets: this.fleets(), fleet: f ? { id: f.id, name: f.name } : null }, ref, p, size);
  }

  /** The peek of a lane under a long press (R2658), or null. */
  lanePeek(ref: LaneRef): CardSpec | null {
    const sel = this.state.selection;
    const now = sel?.kind === 'fleet' && this.seaFleet(sel.id) ? sel.id : this.lastOwnFleet;
    return this.laneSpec(ref, now, 'peek');
  }

  /**
   * Puts the windows away when the player selects a fleet (R1205, the
   * owner's phone report of 2026-09-23). A selection is a look at the map,
   * so the map must be free to look at: the four centred cards (OPTIONS,
   * the media player, KEYS, the heat modes), the VIEW panel, the info
   * panel, the bar card and the peek close, and on a telephone the sheet
   * goes down to its bar. The desktop dock is a column beside the map and
   * stays.
   *
   * `Commands.select` calls it for a fleet and never for a deselect. It
   * touches no armed pick (`hunting`, the aim), and no card opens in the
   * same gesture as a fleet selection, so it closes nothing the player
   * just asked for. It does not render: the caller does.
   */
  closeTransient() {
    this.settingsOpen = false;
    this.audioOpen = false;
    this.keysOpen = false;
    this.heatOpen = false;
    this.viewOpen = false;
    this.closeInfo(false);
    this.pick = null;
    this.setBarTip('');
    this.hideTooltip();
    this.collapseSheet();
  }

  /**
   * Arms one pick mode for a fleet, or disarms it when it is the one armed
   * (R469). The modes are mutually exclusive, and a second press of the
   * same button disarms. One helper and not six one-liners: every new mode
   * used to need an edit in every other mode's line, and a miss left two
   * modes armed at once. `PICK_MODES` is the whole set, so a mode added
   * there cannot be left armed.
   */
  armPick(mode: PickMode, id: number): void {
    const on = this[mode] === id;
    for (const m of PICK_MODES) this[m] = 0;
    this[mode] = on ? 0 : id;
    this.render(true);
  }

  // --- delegated actions ---

  /**
   * One text field of a generated panel, by the id the markup gave it.
   *
   * The lookup is scoped to the overlay's own subtree on purpose. The
   * panels are built as HTML strings with fixed ids, and a global
   * `getElementById` would answer with whatever else on the page happened
   * to carry that id — the canvas, a landing screen, a second overlay.
   */
  private field(id: string): HTMLInputElement | null {
    return this.root.querySelector<HTMLInputElement>(`#${id}`);
  }

  /**
   * Gives one scrolling host the release rule (R577, R578).
   *
   * The chrome acts on `pointerdown` everywhere else, because a button the
   * 4 Hz re-render destroys between the press and the release swallows the
   * click. That rule is wrong in a host that pans under the same finger:
   * there, the first pixel of a scroll pressed whatever the finger started
   * on. The two `pan-y` hosts of the dock — the queue strip and the tab
   * body — therefore go through one `ListTap` each (R573).
   *
   * The recogniser is one per host, not one for the whole chrome: a press
   * in the strip and a press in the body are separate gestures, and one
   * must never void the other. The recogniser records the element under
   * the press, so `onTap` gets the button that the press came down on even
   * after a re-render took it out of the document; `actButton` reads the
   * data attributes off that element and not off the document.
   *
   * `onTap` gets `null` when the press came down on no button of the host;
   * by default a tap acts on its button. Nothing here calls
   * `preventDefault`, so an `<input>` or a `<select>` in the host still
   * takes the caret. `scrolls` is the test of `pressOrRelease`.
   *
   * The keyboard reaches the same buttons the way it reaches every other
   * generated one: Enter or Space on the focused button (R106).
   */
  private releaseHost(host: HTMLElement, o: {
    onTap?: (btn: HTMLButtonElement | null) => void; scrolls?: () => boolean;
  } = {}) {
    const onTap = o.onTap ?? ((btn: HTMLButtonElement | null) => { if (btn) this.actButton(btn); });
    const scrolls = o.scrolls;
    const tap = new ListTap<HTMLElement>();
    tap.onTap = (target) => {
      // The lift of a title hold is no tap (R1555): the finger asked for
      // the title and not for the control.
      if (this.titleHold?.held()) return;
      const btn = target.closest('button') as HTMLButtonElement | null;
      onTap(btn?.dataset.act ? btn : null);
    };
    host.addEventListener('pointerdown', (ev) => {
      if (ev.button > 0) return;
      if (scrolls && !scrolls()) { this.onAction(ev); return; }
      tap.down(ev.pointerId, ev.clientX, ev.clientY, ev.target as HTMLElement);
    });
    host.addEventListener('pointermove', (ev) => tap.move(ev.pointerId, ev.clientX, ev.clientY));
    host.addEventListener('pointerup', (ev) => tap.up(ev.pointerId, ev.clientX, ev.clientY));
    host.addEventListener('pointercancel', () => tap.cancel());
    this.keyActivates(host);
  }

  /**
   * A host that is fixed chrome at one moment and a scroller at another
   * (R1206): `scrolls` decides at every press. A scroller's press goes to
   * the `ListTap` of `releaseHost` and acts on the release; a fixed host's
   * press acts at once.
   */
  private pressOrRelease(host: HTMLElement, scrolls: () => boolean) {
    this.releaseHost(host, { scrolls });
  }

  /** Enter or Space on a focused generated button acts like a press (R106). */
  private keyActivates(host: HTMLElement) {
    host.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      if (!(ev.target as HTMLElement).closest('button')?.dataset.act) return;
      ev.preventDefault(); // Space would scroll the sheet out from under it
      this.onAction(ev);
    });
  }

  private onAction(ev: Event) {
    // Left button or touch only; a KeyboardEvent has no `button` (R106).
    const btn = pressedControl<HTMLButtonElement>(ev, 'button');
    if (!btn?.dataset.act) return;
    this.actButton(btn);
  }

  /**
   * Acts on one generated button. `onAction` reads the button off the
   * event; the recogniser of a scrolling host hands one in after the
   * release (R577, R578), which may be a button the re-render already
   * destroyed — the data attributes are read off the element and not off
   * the document.
   */
  private actButton(btn: HTMLButtonElement) {
    // A press of the choice kit carries `data-act` and `data-id` alone
    // (R2011), so a choice that names an object and a value writes both
    // into its id as "id:n": the hull and the load, the slot of a fleet.
    const [idPart, nPart] = (btn.dataset.id ?? '0').split(':');
    const id = Number(idPart || 0);
    const n = Number(btn.dataset.n ?? nPart ?? 0);
    const a = this.actions;
    // A press on any other control disarms (R696), so one armed row can
    // never wait behind a screen the player went on from. A gated case
    // reads `data-arm` and goes through `this.gate.press(key)`.
    const key = this.armKey(btn);
    if (!this.gate.is(key)) this.gate.clear(false);
    const act = btn.dataset.act ?? '';
    // The first press of an armed control only arms it, and the gate
    // redraws the label on its own.
    if (ARMED_ACTS.has(act) && btn.dataset.arm === '1' && !this.gate.press(key)) return;
    const pick = PICK_ACTS[act];
    const fleetOp = FLEET_OP_ACTS[act];
    const audioOp = AUDIO_ACTS[act];
    // A hull under repair does not sail (R1883): the deploy pick is not
    // armed, and the notice says why.
    if (pick === 'deploying' && this.refuseDeploy(id)) return;
    if (pick !== undefined) {
      this.armPick(pick, id);
      // A pick armed from the ⋯ grid of a fleet row needs the map (R1816).
      if (btn.closest('.fl-grid')) this.collapseSheet();
    }
    // A fleet op and an audio op do nothing else, so they never reach the
    // switch and its default, the hub cards of OPTIONS (R3022).
    else if (fleetOp !== undefined || audioOp !== undefined) {
      if (fleetOp !== undefined) a.fleetOp(id, fleetOp);
      else a.audioOp(audioOp!);
      this.render(true);
      return;
    }
    // An act that sends one intent and nothing else (`INTENT_ACTS`).
    const intent = INTENT_ACTS[act];
    if (intent) {
      intent(a, id, n);
      this.render(true);
      return;
    }
    // A control of the INBOX page (`ui/inbox/press.ts`) goes to the module
    // that drew it.
    if (act.startsWith('ib-') && inboxPress(act, btn, this.inboxCtx())) {
      this.render(true);
      return;
    }
    // A card of the rename slot, or a row that opens one (`overlay/namedesk.ts`).
    if (this.names.act(act, id, n)) {
      this.render(true);
      return;
    }
    // The switch ends in one `render(true)`, so no case draws on its own.
    switch (act) {
      // No local toggle state: the server echoes the new value in the next
      // Empire frame, and the 4 Hz regen flips the button then.
      case 'autorebuild': a.setAutoRebuild(!this.state.empire?.autoRebuild); break;
      // The group presses of a fleet card's ⋯ (R539, R1809). The slot is
      // read off the button and checked here, so the map is never handed a
      // slot the model would refuse.
      case 'assign': {
        if (!Number.isInteger(n) || n < 1 || n > GROUP_SLOTS) break;
        // A press on the slot the selected fleet already holds empties it.
        const sel = this.state.selection;
        if (sel?.kind === 'fleet' && this.state.groups.fleetAt(n) === sel.id) a.releaseGroup(n);
        else a.assignGroup(n);
        break;
      }
      // The name in the dock head, the rose press and the GO TO THE HOLD
      // row of the VIEW panel (R1758). The rose answers its own press on
      // its rim, and the row puts the panel away.
      case 'home':
        if (btn.dataset.from === 'rose') noteRosePress();
        if (btn.dataset.from === 'view') this.viewOpen = false;
        this.goHome();
        break;
      // SEA VIEW HERE of the VIEW panel (R2493): the page leaves the map.
      case 'seaview':
        this.viewOpen = false;
        a.seaView?.();
        break;
      case 'info': {
        const kind = btn.dataset.kind as InfoKind;
        // A second press on the same row closes it again.
        if (this.infoOn && this.infoOn.kind === kind && this.infoOn.id === id) this.closeInfo();
        else this.openInfo({ kind, id }, btn);
        break;
      }
      // The ✕ of the card and the back chip of the page (R1826).
      case 'infoclose':
      case 'infoback': this.closeInfo(); break;
      // The stepper ‹ › of the page: the item before or after in the list.
      case 'infostep': {
        const to = this.infoOn ? stepTarget(this.infoList, this.infoOn, n) : null;
        if (to) {
          this.infoOn = to;
          this.foldCard();
          this.comparePair = null;
          this.bodyScrollTo = 0;
        }
        break;
      }
      // MORE and LESS of the backstory on the captain card (084 card 16).
      case 'story': this.storyOpen = !this.storyOpen; break;
      // TRAIN ▸ of the captain card opens the pick, and its head closes it
      // (R2087).
      case 'trainpick': this.trainPick = !this.trainPick; break;
      // A segment of the level picker on a building card (084 card 17,
      // R1839): the level the third column of the grid shows.
      case 'lvlpick': this.levelPick = n; break;
      // COMPARE on the hull card (R1829, 084 card 17) marks the hull; a
      // press on the name of a second hull opens the two in one card. A
      // telephone returns to the list, where the second name stands.
      case 'compare':
        this.compareFrom = this.compareFrom === id ? 0 : id;
        if (this.compareFrom !== 0 && isSheet()) this.closeInfo();
        break;
      // BUILD HERE on the own hold card (R1829, 085 card 03) opens the
      // full list of BUILD · BASE, the one complete list.
      case 'buildhere': this.showPanel('BUILD', 'BASE'); break;
      // MARK on a contact card (R2002) opens the marker menu on the contact.
      case 'markcontact': this.onMarkContact?.(id); break;
      // VIA LANE of the on-ramp offer (R3075) sends the lane course.
      case LANE_OFFER_ACT: this.onLaneOffer?.(id); break;
      // The HOW IT WORKS row of a menu opens or closes its fold, and the
      // viewer's store remembers it (R1895).
      case 'howfold': {
        const t = btn.dataset.key as Tab;
        if (hasFold(t)) setFoldOpen(t, !foldOpen(t));
        break;
      }
      // GO TO of an empty line opens the pill where the thing is made
      // (R1895), as the pill row would.
      case 'goto': {
        const to = goTarget(btn.dataset.key);
        if (!to) break;
        this.dropCards();
        this.showPanel(to.tab, to.sub);
        break;
      }
      // A term button (R1557): the definition in the explainer card.
      case 'term': this.toggleTerm(btn); return;
      // A commodity cell or the world clock (§12.0). A second press on the
      // same control closes the card again, which is the whole of the
      // gesture on a telephone: a press anywhere else is caught by the
      // document listener the constructor put on.
      case 'bartip': {
        const tip = btn.dataset.tip ?? '';
        this.setBarTip(this.tipOn === tip ? '' : tip);
        break;
      }
      // A probe scan line's ▤ REPORT (R203). The report takes the selection
      // anchor, so the selection goes first: `deselect` runs main.ts's own
      // `select`, which clears this field on the way past. Setting it after
      // is therefore the whole mutual exclusion.
      case 'report': {
        // `n` is a chat key, not an index: the list drops lines at the
        // cap, and a button drawn before the drop names its own line (R1338).
        const line = this.state.chatLine(n);
        if (!line?.report) break;
        a.deselect();
        this.scanReport = line;
        break;
      }
      // No `reportclose`: the report has no ✕ of its own. It leaves the
      // way a selection leaves, through `deselect` below and through
      // main.ts's select() (R203).
      // PLOT of ⋯ (v2 §7, R2589): the plot line of the fleet. A press in
      // the ⋯ grid of a fleet row selects the fleet first, because the
      // plot line names the fleet the sheet names, and puts the menu away,
      // because a leg is a tap on the map.
      case 'plot': {
        const sel = this.state.selection;
        if (sel?.kind !== 'fleet' || sel.id !== id) a.selectFleet(id);
        this.moreFor = 0;
        if (this.enterPlot(id) && btn.closest('.fl-grid')) this.collapseSheet();
        break;
      }
      // The plot line (v2 slot 3): LOOP, UNDO, DONE, ✕ of a leg and ✕ of
      // the line, which ends the plot and sends nothing.
      case 'plotloop': if (this.plot) this.plot = plotLoop(this.plot); break;
      case 'plotundo': if (this.plot) this.plot = plotUndo(this.plot); break;
      case 'plotleg': if (this.plot) this.plot = plotRemove(this.plot, n); break;
      // The pass count of a closed loop part steps from 1 to the cap and
      // back to 1 (v2 §7 rule 3, R2589).
      case 'plotpass': if (this.plot) this.plot = plotPasses(this.plot, n); break;
      // SAVE AS PLOT (R2589): the name card, and its SAVE sends the legs.
      // The plot line stays, so DONE still sends the course.
      case 'plotsave': {
        const d = this.plot;
        if (!d || d.legs.length === 0) break;
        const legs = plotWaypoints(d.legs);
        this.names.openPlotName(CS.plot_save_head,
          plotNameOf(d.name, FLEET_NAME_MIN_RUNES, FLEET_NAME_MAX_RUNES, CS.plot_name_default),
          (name) => a.plotAction(PlotOp.PLOT_OP_SAVE, { name, legs }));
        break;
      }
      // FLEET · ROUTES (R2589): SEND to the own fleet the sheet names,
      // RENAME through the name card, and ✕ after its second press.
      case 'plotsend': this.sendPlot(this.namedOwnFleet(), this.state.plots.find((x) => x.id === id)); break;
      case 'plotname': {
        const p = this.state.plots.find((x) => x.id === id);
        if (p) this.names.openPlotName(CS.plot_rename_head({ name: p.name }), p.name,
          (name) => a.plotAction(PlotOp.PLOT_OP_RENAME, { plotId: p.id, name }));
        break;
      }
      case 'plotdel': a.plotAction(PlotOp.PLOT_OP_DELETE, { plotId: id }); break;
      // The LANE switch of an own plot (R2646): the server answers in
      // the next Empire frame.
      case 'plotlane': {
        const p = this.state.plots.find((x) => x.id === id);
        if (p) a.plotAction(PlotOp.PLOT_OP_LANE, { plotId: id, lane: !p.lane });
        break;
      }
      // SEND of a lane of a faction mate (R2646): the plot `id` of the
      // commander `n`, to the own fleet the sheet names.
      case 'lanesend':
        this.sendPlot(this.namedOwnFleet(), this.state.factionLanes.find((x) => x.id === id && x.owner === n));
        break;
      // VIEW of a lane row (R2709): the map fits the lane plot `id` of the
      // commander `n` (0 for an own plot), the chart makes it stand out,
      // and its lane card opens, as a press on the lane does.
      case 'laneview': {
        const ref = { owner: n, id };
        const p = findLane(this.state, ref);
        if (!p || p.legs.length === 0) break;
        a.viewLane(laneMarkId(n, id), plotLegs(p));
        this.collapseSheet();
        this.openLaneCard(ref);
        break;
      }
      // VIEW of a lane gap of the lane card (R3074): the map goes to the
      // chain point `data-key` of the lane plot `id` of the commander `n`.
      case 'lanegap': {
        const p = findLane(this.state, { owner: n, id });
        const at = p ? laneGapAt(p, Number(btn.dataset.key)) : null;
        if (at) a.focus(at.x, at.y);
        break;
      }
      // SEND of the lane card (R2658): the lane plot `id` of the commander
      // `n` (0 for an own plot), to the fleet the card names.
      case 'lanego': this.sendPlot(this.seaFleet(this.laneOpen?.fleet ?? 0), findLane(this.state, { owner: n, id })); break;
      // ASSIGN of the commission card (R2629, R2645): the offer `id` to
      // the own fleet `n`, one fleet for each press. It gives no order.
      case 'comassign': a.assignCommission(id, n); break;
      case 'plotdone': this.confirmPlot(false); break;
      case 'plotend': this.endPlot(false); break;
      // The selection chip in the head of a menu sheet (087 card 01): the
      // menu closes, and the peek comes back.
      case 'selchip': this.collapseSheet(); break;
      // The ⋯ of a fleet's card: the rest of the orders (R1809). EDIT of an
      // own fleet's card: the group slots and RENAME in the place of the ⋯
      // grid (R2008). Opening one grid closes the other, and each press of
      // a closed telephone sheet raises it and opens its grid in one step
      // (R2008, R2302).
      case 'more':
      case 'edit':
        this.pressGrid(act);
        break;
      // REPORT and BLOCK (deployment.md §7.6, R1323).
      case 'linemore': this.reports.toggleLineMenu(btn.dataset.key ?? ''); break;
      case 'reportopen': this.reports.open(btn.dataset.key ?? ''); break;
      case 'reportreason': this.reports.pickReason(btn.dataset.id ?? ''); break;
      case 'reportsend': this.reports.send(); break;
      case 'reportcancel': this.reports.close(); break;
      case 'lineblock': this.reports.blockSender(btn.dataset.key ?? ''); break;
      case 'lineblockseat': this.reports.blockSeat(Number(btn.dataset.n ?? 0)); break;
      case 'admintoggle': this.toggleAdmin(); break;
      case 'keys': this.toggleKeys(); break;
      case 'keysclose': this.keysOpen = false; break;
      // Both cards are centred over the map, so the second one to open
      // shuts the first: two stacked panels is not a layout, it is a bug.
      case 'opts':
        if (this.settingsOpen) this.settingsOpen = false;
        else this.openModal('options');
        break;
      case 'optsclose': this.settingsOpen = false; break;
      // The STANDING ORDERS tag of the dock head opens the menu on PLAY,
      // the section that holds the switch (R1767).
      case 'standingopts':
        this.openModal('options');
        this.openOptsPage('play');
        break;
      // The DEV row of the telephone root (R1754) opens the DEV page of the
      // menu (R1766): ‹ BACK returns to the root, ✕ CLOSE closes the menu.
      case MENU_ACT.dev: this.openOptsPage('dev'); break;
      // The pages of the menu (R1731): the root, a section, ACCOUNT and
      // the music player under AUDIO.
      case MENU_ACT.page: this.openOptsPage(btn.dataset.id ?? ''); break;
      // A hub row of the ACCOUNT page leaves the world the way MENU does,
      // onto the part of the hub that holds the setting.
      case MENU_ACT.hub: {
        const href = HUB_LINKS[btn.dataset.id ?? ''];
        if (href) a.leaveFor(href);
        break;
      }
      case MENU_ACT.standing: this.optionsHost?.toggleStanding(); break;
      // The shared rows of the menu (`ui/options/rows.ts`): one rule turns
      // a press into the next settings, here and in the hub.
      case OPT_ACT.sw:
      case OPT_ACT.chart:
      case OPT_ACT.scale:
      case OPT_ACT.render:
      case OPT_ACT.chatDesk:
      case OPT_ACT.casinoDesk:
      case OPT_ACT.sheets:
      case OPT_ACT.tabLabels: {
        const next = applyPress(this.settings, act, btn.dataset);
        if (next) this.writeSettings(next);
        // The sheet heights install or remove the grip at once (R1748).
        if (act === OPT_ACT.sheets) this.syncSheetHeights();
        // The tab labels draw the bar again at once (R2215).
        if (act === OPT_ACT.tabLabels) this.fitTabs(true);
        break;
      }
      // The UI theme (R1776): the pick goes through the theme state, so
      // the theme link follows it, and the chrome wears it at once.
      case OPT_ACT.theme: {
        const pick = stepUiTheme(this.settings, cachedEntitlements(), Number(btn.dataset.id));
        if (pick === null) break;
        Object.assign(this.settings, setThemePick('ui', pick));
        wearTheme(this.state.faction);
        break;
      }
      // A language pick opens the confirm strip, because the change
      // reloads the page (R855); a press on the language in force closes it.
      case OPT_ACT.lang: {
        const id = btn.dataset.id ?? '';
        this.optsLang = id === this.settings.lang ? null : id;
        break;
      }
      case OPT_ACT.langNo: this.optsLang = null; break;
      // RELOAD NOW: store the pick, wait for the account push, reload.
      case OPT_ACT.langGo: {
        const next = applyPress(this.settings, act, btn.dataset);
        if (!next) break;
        this.writeSettings(next);
        void flushSettings().then(() => { location.reload(); });
        return;
      }
      case 'keysopen': this.openModal('keys'); break;
      case 'photomode': this.settingsOpen = false; this.onPhoto?.(); break;
      case 'reportproblem': this.settingsOpen = false; this.onReportProblem?.(); break;
      // The six menus and the chat channels (R237). Both were `onclick` —
      // a release — while every other control in the shell acted on the
      // press.
      case 'tab': {
        const t = btn.dataset.tab as Tab | undefined;
        if (t) this.pressTab(t);
        break;
      }
      case 'channel': {
        const ch = Number.parseInt(btn.dataset.id ?? '', 10);
        if (Number.isFinite(ch)) this.state.readChannel(ch);
        break;
      }
      // The chat line (R1799): the press opens the chat on the channel of
      // the line. ▾ of the band or of the pane closes the chat.
      case 'chatline': {
        const ch = Number.parseInt(btn.dataset.ch ?? '', 10);
        if (Number.isFinite(ch)) this.state.readChannel(ch);
        this.openChat();
        return;
      }
      case 'chatclose': this.closeChat(); return;
      // The presses of the desktop chat setting on the chat head (R1838):
      // the size of the box, DOCK and UNDOCK.
      case 'chatsize': this.writeChatDesk(sizeDesk(this.settings)); return;
      // The size press and the minimize press of the open desktop queue
      // strip (R2740).
      case 'queuesize': this.setQueueDesk(true, !this.settings.queueLarge); return;
      case 'queuemin': this.setQueueDesk(false, this.settings.queueLarge); return;
      case 'chatdock': this.writeChatDesk(dockDesk(this.settings)); return;
      case 'chatundock': this.writeChatDesk(undockDesk(this.settings)); return;
      // DOCK and UNDOCK of the casino head, and the close of the casino
      // pane, which leaves the casino for the first page of ECONOMY
      // (R2198).
      case 'casinodock':
      case 'casinoundock':
        this.writeSettings({ ...this.settings, casinoDesk: act === 'casinodock' ? 'docked' : 'undocked' });
        break;
      case 'casinoclose':
        this.sub.ECONOMY = SUBS.ECONOMY[0];
        this.queryProduction();
        this.render(true);
        return;
      // The HEAT tile of the VIEW panel (R1806). With a mode painting the
      // map the tile is lit, and a press turns the mode off (the second
      // press of `setHeatmap`); with no mode it opens the heat page — a
      // tile that did nothing would be a dead control.
      // The heat page of the VIEW panel and its way back (round 086 card 10).
      case 'heat':
      case 'heatpage':
        if (act === 'heat' && this.heatLive()) a.setHeatmap(this.state.heatmap);
        else { this.heatOpen = true; this.viewOpen = true; }
        break;
      case 'heatback': this.heatOpen = false; break;
      // The FACTION KEY tile (R1568, R1805): the FACTIONS section of the
      // legend card on a desktop, the key section of the VIEW panel on a
      // telephone. Each form factor keeps its own flag.
      case 'factionkey':
        if (isSheet()) this.setOpt('factionKeyPhone', !this.settings.factionKeyPhone);
        else this.setOpt('factionKey', !this.settings.factionKey);
        break;
      // The head row of the key section of a telephone folds the section
      // or opens it (R1859).
      case 'viewkey': this.viewKeyOpen = !this.viewKeyOpen; break;
      // The DEPTH and CURRENT fold of a telephone (R2616).
      case 'viewdeep': this.viewDeepOpen = !this.viewDeepOpen; break;
      // A group of chart pieces of the VIEW panel (R1812, round 086 card
      // 15): one press hides every piece of NAMES, TRACKS or CARDS, a
      // second shows them again.
      case 'chartgroup': {
        const g = oneOf(CHART_GROUPS, btn.dataset.id);
        if (!g) break;
        const hide = this.settings.chartHide;
        this.setOpt('chartHide', hide.includes(g) ? hide.filter((x) => x !== g) : [...hide, g]);
        break;
      }
      // The minimize control of the legend card and its chip (R1805).
      case 'legend':
        if (legendHeld()) { releaseLegend(); factionKey.refresh(); } else this.setOpt('legend', !this.settings.legend);
        break;
      // The minimize control of the cartouche plate folds it to its head
      // row (round 086 card 04).
      case 'cartouche': cartouche.toggleFold(); break;
      // The advisor line and its switch (R1305). The press sends the word
      // opposite to what the row shows: the world takes it on the empire,
      // and the account keeps it for the next season (R1566). The row
      // turns with the Empire frame that answers.
      case 'advice': this.followAdvice(); return;
      // The sea trials (R1572): the line opens the panel of the next act,
      // and a SHOW button the panel of its own. Both give no order (R1305).
      case 'trialnext': {
        const next = nextTrial(this.state.empire?.seaTrials ?? 0);
        if (next >= 0) this.showTrial(next);
        return;
      }
      case 'trialshow': {
        const i = Number(btn.dataset.n ?? -1);
        if (Number.isInteger(i) && i >= 0 && i < TRIAL_COUNT) this.showTrial(i);
        return;
      }
      case 'trialsfold': this.toggleTrials(); break;
      // The drill row leaves for the arcade (R1564).
      case 'trialdrill': assignWithGesture(DRILL_URL); return;
      // The two buttons of the welcome card (R1572).
      case 'welcomego': this.closeWelcome(false); break;
      case 'welcometrials': this.closeWelcome(true); break;
      case 'welcomefold': toggleBuilt(this.welcomeEl); break;
      // THE SEASON block of the welcome card (R2447): its fold on a
      // telephone, HOW IT PLAYS on the conquest page (R2445), and FIND A
      // CLAN in a new tab, so the seat stays open behind it.
      case 'welcomeseason': toggleSeason(this.welcomeEl); break;
      case 'welcomehow': openHowPlays('conquest'); return;
      case 'welcomeclan': openLink(hubSectionHref('clan', 'find')); return;
      case 'advicefold': this.adviceOpen = !this.adviceOpen; break;
      case 'advisor': a.setAdvisor(!this.advisorOn()); break;
      // --- sound (plan §5) ---
      case 'audio': this.toggleAudio(); break;
      case 'audioclose': this.audioOpen = false; break;
      // The card knows which way the switch is standing, so the op carries
      // the state to set rather than a "flip it" the director has to guess.
      case 'mshuffle': a.audioOp('shuffle', this.audio.status?.shuffle ? 0 : 1); break;
      case 'sub':
        if (this.tab === 'WAR' && btn.dataset.sub === 'REPORTS') {
          this.openBattleReports();
          return;
        }
        this.sub[this.tab] = btn.dataset.sub ?? '';
        this.dropCards();
        this.queryPill();
        break;
      case 'warlog': this.warLogOpen = !this.warLogOpen; break;
      case TREASURE_FOUND: this.treasureFoundOpen = !this.treasureFoundOpen; break;
      case COMMISSION_ARCHIVE: this.commissionArchiveOpen = !this.commissionArchiveOpen; break;
      case 'techkeys': this.techKeysOpen = !this.techKeysOpen; break;
      case 'capcodes': this.capCodes = !this.capCodes; break;
      case 'meta': this.pressTab(btn.dataset.tab as Tab); break;
      // The CHAT tile of the VIEW panel opens the chat as a state of the
      // dock, or closes it (R1799), and puts the panel away (R1747). The
      // chart's toggle writes the settings store, which is where a tile's
      // answer lives, so the choice comes back after a reload.
      case 'social':
        this.viewOpen = false;
        // The tile turns the chat of this form factor off, and the account
        // keeps the off state (R2369): no chat and no chat line stand.
        if (this.chatTileOn()) {
          this.writeSettings({ ...this.settings, [this.chatOnKey()]: false });
          this.closeChat();
          this.render(true);
        } else {
          this.openChat();
        }
        return;
      case 'minimap': this.setOpt('minimap', !this.settings.minimap); break;
      // VIEW opens and closes its panel (R1747). A mouse press reaches it
      // here; a touch press reaches `toggleView` at its release (R1610).
      case 'view': this.viewOpen = !this.viewOpen; break;
      case 'centerf': {
        const fl = this.seaFleet(id);
        if (fl) a.focus(fl.x, fl.y);
        break;
      }
      // RESEED, RESTART and ROLL carry `data-arm`; the rest do not.
      case 'adm': {
        const op = btn.dataset.op ?? '';
        if (op === 'reveal') this.revealOn = !this.revealOn;
        if (op === 'veil') this.veilOff = !this.veilOff;
        if (op === 'caches') this.adminCachesOn = !this.adminCachesOn;
        if (op === 'autopilot') this.autopilotOn = !this.autopilotOn;
        a.admin(op, n);
        break;
      }
      // Both deploy orders put the sheet down on a phone (R840): the hull
      // arrives on the water the menu was covering.
      case 'deployd':
        if (this.refuseDeploy(id)) break;
        a.deployDesign(id); this.collapseSheet(); break;
      // The captain picker card of a hull in the pens (R1893): the CAPTAIN
      // press of the row and of its ⋯. A second press on the same hull
      // puts the card away.
      case 'capdlg': this.togglePick({ kind: 'cap', fleetId: id, sel: 0 }, btn); break;
      // The start sheet of one depth (R1893), the same way.
      case 'divestart': this.togglePick({ kind: 'dive', depth: id, sel: 0 }, btn); break;
      // A press on a captain row of a picker selects the captain.
      case 'picksel': if (this.pick) this.pick.sel = id; break;
      // The ✕ of a picker card and the back chip of its page.
      case 'pickclose':
      case 'pickback': this.closePick(); break;
      // LAY of a Sapper (R2635, R2651): the kind rides `n`, and the overlay
      // keeps it, so LAY of the order row lays it again. An empty stow
      // sends no kind; the press stands off then.
      case 'lay':
        if (n > 0) { this.lastLay.set(id, n); a.fleetOp(id, FleetOp.FLEET_OP_LAY, n); }
        break;
      // A pill of the lay pick (R2710): the id is "fleet:kind". It sets the
      // kind LAY lays and sends nothing, as the server lays the kind a LAY
      // names.
      case 'laypick':
        if (n > 0) this.lastLay.set(id, n);
        break;
      // The LOADOUT sheet of a hull in the pens (R2031), with the saved mix
      // as its draft; a second press on the same hull puts it away.
      case 'loaddlg':
        this.togglePick({ kind: 'load', fleetId: id, sel: 0, mix: draftOf(this.state, id) }, btn);
        break;
      // One step of a stepper of the LOADOUT sheet: the id is "kind:step".
      // The draft clamps at 0 and at the magazine; nothing is sent.
      case 'loadstep':
        if (this.pick?.kind === 'load') this.pick.mix = stepDraft(this.state, this.pick.fleetId, this.pick.mix, id, n);
        break;
      // The MAX press » of a row (R2040): the id is the kind. It fills the
      // free room of the magazine with the kind; nothing is sent.
      case 'loadmax':
        if (this.pick?.kind === 'load') this.pick.mix = maxDraft(this.state, this.pick.fleetId, this.pick.mix, id);
        break;
      // AUTO and SAVE of the sheet: the empty mix, or the draft.
      case 'loadauto':
      case 'loadset':
        a.setLoad(id, act === 'loadset' && this.pick?.kind === 'load' ? this.pick.mix : []);
        this.closePick();
        break;
      // The STOW sheet of a Sapper in the pens (R2654, R2658), the way the
      // LOADOUT sheet works: the steppers and MAX change the draft, and
      // AUTO and SAVE send `FLEET_OP_SET_STOW`.
      case 'stowdlg':
        this.togglePick({ kind: 'stow', fleetId: id, sel: 0, mix: stowDraftOf(this.state, id) }, btn);
        break;
      case 'stowstep':
        if (this.pick?.kind === 'stow') this.pick.mix = stepStow(this.state, this.pick.fleetId, this.pick.mix, id, n);
        break;
      case 'stowmax':
        if (this.pick?.kind === 'stow') this.pick.mix = maxStow(this.state, this.pick.fleetId, this.pick.mix, id);
        break;
      case 'stowauto':
      case 'stowset':
        a.setStow(id, act === 'stowset' && this.pick?.kind === 'stow' ? this.pick.mix : []);
        this.closePick();
        break;
      // The way to the picker from wherever the block was stated (R385):
      // FLEET · MISSIONS and the selection panel. It sets the menu, the
      // pill and the open dialog together, so the picker for that hull is
      // on screen after one press.
      //
      // It must not go through `openTab`, which collapses the sheet when
      // the menu asked for is the menu already open — the press would then
      // put the dock away instead of showing the list.
      case 'assigncap':
        // The deploy ask of a warship with nobody aboard offers this press
        // too (R1883). The picker outranks the card in the slot, and the
        // card comes back when the picker closes.
        this.tab = 'FLEET';
        this.sub.FLEET = 'MISSIONS';
        this.openPick({ kind: 'cap', fleetId: id, sel: 0 }, btn);
        this.openSheet();
        // applySheet, and not render(): it is the one place that keeps the
        // two sheet flags from contradicting each other, and it re-renders.
        this.applySheet();
        break;
      case 'setcap':
        a.fleetOp(id, FleetOp.FLEET_OP_SET_CAPTAIN, n);
        this.closePick();
        break;
      case 'invhull': this.draft.setHull(this.state, id); break;
      case 'invwt': this.draft.weapType = id; break;
      case 'invst': this.draft.shieldType = id; break;
      case 'print': a.print(id, this.state.rules.standardPrintRuns); break;
      case 'sell': {
        const price = Number(this.field(`price-${id}`)?.value ?? 0);
        if (price > 0) a.market({ listDesign: id, price });
        else a.refuse(S.refuse_no_price);
        break;
      }
      // Every control of the CASINO panel (casino.md). The panel owns the
      // acts, so the switch has one arm and the button's own `data-op`
      // names which one. The press arrives from the tab body's release
      // recogniser, like every other button in this host (R578).
      case 'casino': this.casino.act(btn, key); break;
      case 'aucpage':
        this.auctionPage = (btn.dataset.sub as typeof AUCTION_PAGES[number]) ?? 'BROWSE';
        break;
      case 'misolist': {
        const qty = Number(this.field('iso-qty')?.value ?? 0);
        const price = Number(this.field('iso-price')?.value ?? 0);
        if (qty > 0 && price > 0) a.market({ listIsotopes: qty, price });
        else a.refuse(S.refuse_no_qty_price);
        break;
      }
      // The season summary card opens again (R1518). The card is the
      // page's own and reads the store, so the press is a flag there.
      case 'summary': this.state.showSummary(true); break;
      case 'invmod': {
        const why = this.draft.pick(this.state, id, n);
        if (why) a.refuse(why);
        break;
      }
      case 'inventsubmit': a.invent(id, this.draft.submit(this.state)); break;
      // The core card asks, and this is the answer (R776, R1458). The card
      // closes on the press: the next core needs a new reading of the queue.
      case 'core':
        this.coreAsk = false;
        a.useDataCore();
        break;
      // The Data Core cell of the top bar (R1458): a press opens the core
      // card and a second press puts it away; CANCEL puts it away too.
      case 'coreask': this.coreAsk = !this.coreAsk; break;
      // The squad chip (R1571): a press opens the roster card, and a press
      // with either card up puts both away.
      // On a telephone the menu covers the screen, so a press on its
      // squad cell closes the menu and the card stands alone (R2448).
      case 'squad': {
        const open = this.squadOpen || this.squadKeyOpen;
        this.squadOpen = !open;
        this.squadKeyOpen = false;
        if (!open && isSheet()) this.settingsOpen = false;
        break;
      }
      // MARKS KEY of the roster card: the key takes the roster's place.
      case 'squadkey': this.squadOpen = false; this.squadKeyOpen = true; break;
      // OPEN CLAN of the roster card: the CLAN tab, and the card goes.
      case 'squadclan': this.closeSquad(); this.pressTab('CLAN'); break;
      // RALLY of the window head and the squad card: a squad marker on the
      // object and the H-hour line to SQUAD, then a rest.
      case 'rally': {
        if (this.rallyResting()) break;
        const g = this.state.sieges.find((x) => x.siteId === id);
        if (!g) break;
        this.rally(id, n, g.owner !== 0 && g.owner === this.state.faction);
        this.rallyRestUntil = Date.now() + SIEGE_WINDOW.rallyRestMs;
        this.notice(SW.rally_notice({ target: targetName(g) }));
        break;
      }
      case 'coreno': this.coreAsk = false; break;
      case 'dive': a.expedition(n, id); this.closePick(); break;
      // A captain's fleet name (R1483): the fleet is selected and the map
      // centres on it. The telephone sheet goes down, so the map shows.
      case 'capfleet': a.selectFleet(id); this.collapseSheet(); break;
      // A press on a fleet row of FLEET · MISSIONS (R1816): one gesture,
      // one outcome. The fleet is selected, the map centres on it, and on
      // a telephone the sheet goes down so the map shows.
      case 'flsel': a.selectFleet(id); this.rowMore = 0; this.collapseSheet(); break;
      // The ⋯ of a fleet row opens the grid of its orders under the row.
      case 'flmore': this.rowMore = this.rowMore === id ? 0 : id; break;
      // The ⋯ of a library row of DRAFT opens its grid under the row (R1888).
      case 'libmore': this.libMore = this.libMore === id ? 0 : id; break;
      // ⋯ of a captain row opens its grid under the row (R1889).
      case 'capmore': this.capMore = this.capMore === id ? 0 : id; break;
      // `PICK_ACTS` armed the pick; `id` is the penned fleet's id: a deploy
      // names the hull, because the pens hold objects rather than pools.
      // The pick is on the map, so the sheet goes down with it (R840).
      case 'deploystart': this.collapseSheet(); break;
      case 'aim': this.deploying = 0; a.aim(id); break;
      case 'basetorp': this.baseTorp = id; break;
      // A chip of the TRAIN pick sends one point and closes the pick (R2087).
      case 'train': this.trainPick = false; a.train(id, n); break;
      // No clan case here (phase 4b). The hub page owns every change to a
      // clan, and the change lands at the next season roll, so the in-game
      // tab draws no control that could send a `ClanAction`.
      // Every COMMIT opens the confirm card, wherever it was pressed from
      // (R788): the card is the confirmation, so the panel sends no order.
      case 'commit': this.askCommit(n, id); break;
      // The confirm card of R788: COMMIT sends the order, CANCEL drops it.
      case 'commitgo':
        if (this.commitAsk) a.siege(SiegeOp.SIEGE_OP_COMMIT, this.commitAsk.siteId, this.commitAsk.fleetId);
        this.closeCommitCard();
        break;
      case 'commitno': this.closeCommitCard(); break;
      // The deploy ask of R1883: DEPLOY and REPAIR (`deployPress`).
      case 'deploygo':
      case 'deployfix': this.deployPress(act, id); break;
      // SHOW jumps the map to the cell and puts a telephone sheet away,
      // so the target stands in view.
      case 'focuscell': a.focusCell(n); this.collapseSheet(); break;
      // SHOW on a structure (R2517): the map jumps to the object, and the
      // context slot opens the card of the object, as a tap on its mark does.
      case 'focussite': a.focusCell(n); this.collapseSheet(); a.selectSite(id); break;
      // A DEPOT family head (R3244): each press shows the next own item of
      // the family on the map, and puts a telephone sheet away, so the item
      // and its peek stand in view. The next press of the head walks on.
      case 'depotgo': {
        const r = nextDepotObject(this.state, n === 1, this.depotShown[n === 1 ? 1 : 0]);
        if (!r) break;
        this.depotShown[n === 1 ? 1 : 0] = r.id;
        this.collapseSheet();
        a.showSeaObject(r.x, r.y, r.id);
        break;
      }
      // The alarm screen (R1753): the time cell and the siege row open the
      // alarm list, and each press of the list closes it on its way.
      case 'alarms':
      case 'alarmsclose':
      case 'siegego':
      case 'alarmcheck':
      case 'alarmread':
      case 'alarmbuild':
      case 'alarminbox':
      case 'alarmrestart':
        this.alarmAct(act, btn);
        break;
      // A press on a hub card aboard the menu (ALERTS, BLOCKED; R1731):
      // the card answers for the acts it owns and ignores every other.
      default: this.optionsHost?.act(act, btn); break;
    }
    this.render(true);
  }
}
